/** Tax planning inputs — calculations wired in a follow-up. */

import { getApiConfig } from "../config";
import { proxyFetch } from "../api/proxyFetch";
import { loadTradingIncomeForTaxYear } from "./analytics";
import { accountScopeGeneration, subscribeAccountScope } from "./accountScope";
import {
  loadCollapsedSectionIds,
  setCollapsibleOpen,
} from "./collapsibleSections";
import { CUSTOM_PANEL_MARKER_ATTR } from "./tabs";
import {
  estimatedTaxPaidForMonthKey,
  forecastAssumedPriorYearSettlementAmount,
  notifyTaxSettingsChanged,
  OWN_CAPITAL_TAX_PAYMENTS_FROM_YEAR,
  TAX_SETTINGS_CHANGED_EVENT,
  taxSettingsStorageKey,
  notifyTaxPaymentMonthFocus,
  type ForecastOwnCapitalTaxPlan,
} from "./taxEstimatedPayments";
export type { ForecastOwnCapitalTaxPlan } from "./taxEstimatedPayments";
import {
  commitTaxSettingsToStore,
  ensureTaxStoreReady,
  getTaxSettingsFromStore,
  registerTaxStoreDeps,
} from "./taxStore";

export { bindTaxStoreUser } from "./taxStore";
import { showToast } from "./toast";
export {
  estimatedTaxPaidForMonthKey,
  TAX_SETTINGS_CHANGED_EVENT,
  TAX_PAYMENT_MONTH_FOCUS_EVENT,
} from "./taxEstimatedPayments";
export type { TaxPaymentMonthFocusDetail } from "./taxEstimatedPayments";

const TAX_MODAL_HOST_ID = "sfe-tax-modal-host";

const TAX_ANALYTICS_LOAD_TIMEOUT_MS = 12_000;

function withTaxAnalyticsTimeout<T>(promise: Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timeoutId = window.setTimeout(
      () => reject(new Error("Own ledger tax data timed out")),
      TAX_ANALYTICS_LOAD_TIMEOUT_MS,
    );
    promise.then(
      (value) => {
        window.clearTimeout(timeoutId);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(timeoutId);
        reject(error);
      },
    );
  });
}

const TAX_YEAR_START = 2025;
/** How many calendar years ahead of today appear in the Tax year selector. */
const TAX_FORECAST_YEARS_AHEAD = 5;

/** Tax years shown in selectors and settings — grows as the calendar advances. */
export function getTaxYears(): number[] {
  const endYear = Math.max(new Date().getFullYear() + TAX_FORECAST_YEARS_AHEAD, TAX_YEAR_START);
  return Array.from({ length: endYear - TAX_YEAR_START + 1 }, (_, index) => TAX_YEAR_START + index);
}

/** Years shown in the Tax year chip row (fact-only unless Forecast is on). */
export function getTaxYearChipOptions(forecastEnabled: boolean): TaxYear[] {
  const nowYear = currentTaxYear();
  const endYear = forecastEnabled
    ? Math.max(nowYear + TAX_FORECAST_YEARS_AHEAD, TAX_YEAR_START)
    : nowYear;
  return Array.from(
    { length: endYear - TAX_YEAR_START + 1 },
    (_, index) => TAX_YEAR_START + index,
  );
}

function normalizeTaxYearChipSelection(year: TaxYear, forecastEnabled: boolean): TaxYear {
  const options = getTaxYearChipOptions(forecastEnabled);
  if (options.includes(year)) return year;
  const now = currentTaxYear();
  if (options.includes(now)) return now;
  return options.at(-1) ?? now;
}

export type TaxYear = number;
export type YearMode = "fact" | "forecast";

export function currentTaxYear(): TaxYear {
  return new Date().getFullYear();
}

export function isTaxYear(value: number): value is TaxYear {
  return Number.isInteger(value) && value >= TAX_YEAR_START && value <= getTaxYears().at(-1)!;
}

export function normalizeTaxYear(value: number): TaxYear {
  const years = getTaxYears();
  if (years.includes(value)) return value;
  const now = currentTaxYear();
  if (years.includes(now)) return now;
  return years.at(-1) ?? now;
}

function defaultYearMode(year: number): YearMode {
  return year <= new Date().getFullYear() ? "fact" : "forecast";
}

export interface TaxBracket {
  ratePct: number;
  from: number;
  to: number | null;
}

export interface TaxPaycheck {
  grossIncome: number | null;
  totalTaxPaid: number | null;
  noIncome?: boolean;
}

export type TaxPaycheckMonth = [TaxPaycheck, TaxPaycheck];
export type TaxPaycheckYear = TaxPaycheckMonth[];

export type TaxPaymentKind = "estimated" | "balance-due" | "extension" | "other";
export type TaxPersonId = "anton" | "olha";

/** Non-trading income / tax already paid, dated and attributed to a W-2 person. */
export interface TaxOtherIncomeItem {
  id: string;
  date: string;
  amount: number;
  taxContributed: number;
  person: TaxPersonId;
  note: string;
}

export interface TaxEstimatedPayment {
  id: string;
  /** Tax year the payment applies to, independent of its cash payment date. */
  taxYear: number;
  kind: TaxPaymentKind;
  /** ISO cash payment date YYYY-MM-DD. */
  date: string;
  amount: number;
  note: string;
  /**
   * When true, payment reduces Own Capital EOM (left the trading account).
   * When false, Tax-tab only. Default: tax year 2026+ → true; earlier → false.
   */
  fromOwnCapital?: boolean;
}

export interface TaxYearInputs {
  mode: YearMode;
  w2PaychecksAnton: TaxPaycheckYear;
  w2PaychecksOlha: TaxPaycheckYear;
  w2NoIncomeAnton: boolean[];
  w2NoIncomeOlha: boolean[];
  /** Legacy annual inputs retained for saved-settings compatibility. */
  w2IncomeAnton: number | null;
  w2IncomeOlha: number | null;
  /** Combined trading income (wheel / short-term gains). Legacy annual total. */
  tradingIncome: number | null;
  /** Monthly trading income entries. */
  tradingIncomeMonths: (number | null)[];
  standardDeduction: number | null;
  investmentPortfolioEoy: number | null;
  /** Prior-year total tax, used for safe-harbor planning. */
  priorYearTotalTax: number | null;
  /** When true, priorYearTotalTax was filled from the previous tax year. */
  pickPriorYearTotalTax: boolean;
  w2WithholdingMonthlyAnton: number | null;
  w2WithholdingMonthlyOlha: number | null;
  /** Actual estimated tax paid each quarter (after W-2 withholding). */
  quarterlyPaidAfterW2: [number | null, number | null, number | null, number | null];
  /** Monthly estimated tax payments (derived from estimatedTaxPayments). */
  estimatedTaxPaidMonths: (number | null)[];
  /** Estimated tax payments with payment dates. */
  estimatedTaxPayments: TaxEstimatedPayment[];
  /** Non-trading income and tax already contributed (dated list). */
  otherIncomeItems: TaxOtherIncomeItem[];
  factTotalEstimatedTaxPaid: number | null;
  irsInterestAprPct: number | null;
  profitPerMonthPct: number | null;
}

export interface TaxSettings {
  filingStatus: "mfj";
  w2PersonNames: { anton: string; olha: string };
  /** Legacy shared brackets retained for saved-settings compatibility. */
  brackets: TaxBracket[];
  bracketsByYear: Partial<Record<string, TaxBracket[]>>;
  copyBracketsFromPrevious: Partial<Record<string, boolean>>;
  defaultStandardDeduction: number;
  /** IRS underpayment rate (federal short-term + 3%, annual). */
  irsInterestAprPct: number;
  /** Expected monthly return on unpaid estimated tax kept invested. */
  irsOpportunityMonthlyPct: number;
  years: Partial<Record<string, TaxYearInputs>>;
}

const DEFAULT_BRACKETS: TaxBracket[] = [
  { ratePct: 10, from: 1, to: 23850 },
  { ratePct: 12, from: 23851, to: 96950 },
  { ratePct: 22, from: 96951, to: 206700 },
  { ratePct: 24, from: 206701, to: 394600 },
  { ratePct: 32, from: 394601, to: 501050 },
  { ratePct: 35, from: 501051, to: 751600 },
  { ratePct: 37, from: 751601, to: 10_000_000 },
];

const TAX_BRACKETS_2026: TaxBracket[] = [
  { ratePct: 10, from: 1, to: 24800 },
  { ratePct: 12, from: 24801, to: 100800 },
  { ratePct: 22, from: 100801, to: 211400 },
  { ratePct: 24, from: 211401, to: 403550 },
  { ratePct: 32, from: 403551, to: 512450 },
  { ratePct: 35, from: 512451, to: 768700 },
  { ratePct: 37, from: 768701, to: 10_000_000 },
];

function bracketsMatch(left: TaxBracket[] | undefined, right: TaxBracket[]): boolean {
  return (
    left?.length === right.length &&
    left.every(
      (bracket, index) =>
        bracket.ratePct === right[index].ratePct &&
        bracket.from === right[index].from &&
        bracket.to === right[index].to,
    )
  );
}

const QUARTER_LABELS = [
  "Q1 (Tax Due By Apr 15)",
  "Q2 (Tax Due By Jun 15)",
  "Q3 (Tax Due By Sep 15)",
  "Q4 (Tax Due By Jan 15)",
] as const;

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
] as const;

const TIP_TRADING_INCOME =
  "Own-ledger cash income for the month: STO premiums, buy-to-close costs, assignment P/L, and ordinary stock-sale P/L.";

const TIP_TRADING_FIFO =
  "FIFO option P/L replayed from your own ledger by close month (short STO→BTC/expire, long BTO→STC). Scoped like Analytics(L) closed-trade history (closed + expired only). Excludes assignments and stock legs—those flow through Income instead. Forecast months use target ROC projection.";

const TIP_TRADING_INCOME_TOTAL =
  "Sum of monthly own-ledger cash income (fact months plus forecast from target ROC for future months in the current year).";

const TIP_TRADING_FIFO_TOTAL =
  "Sum of replayed FIFO P/L: closed and expired option round-trips for fact months; target ROC projection for forecast months.";

const TIP_OTHER_INCOME =
  "Non-trading income you add here (side work, gifts treated as income, etc.). It is not trading P/L and is dated separately.";

const TIP_OTHER_TAX =
  "Tax already paid or withheld on that other income. Counted with W-2 withholding toward tax paid.";

const TIP_OTHER_SECTION_TOTAL =
  "Trading cash income plus other non-trading income for the year. FIFO is shown separately and is not added here.";

const TIP_EST_OTHER_INCOME =
  "Non-trading other income entered in the Other Income list for this period.";

const TIP_EST_OTHER_TAX =
  "Tax contributed on other (non-trading) income for this period.";

const TIP_EST_W2 =
  "Combined W-2 gross income for Anton and Olha in this month.";

const TIP_EST_W2_QUARTER =
  "Combined W-2 gross income for Anton and Olha in this quarter.";

const TIP_EST_W2_ANNUAL =
  "Combined W-2 gross income for Anton and Olha for the full year.";

const TIP_EST_TRADING =
  "Own-ledger cash trading income for the month.";

const TIP_EST_TRADING_QUARTER =
  "Own-ledger cash trading income for this quarter.";

const TIP_EST_TRADING_ANNUAL =
  "Own-ledger cash trading income for the full year.";

const TIP_EST_TOTAL =
  "W-2 gross plus cash trading income plus other income. Shown for reference; tax due uses FIFO total.";

const TIP_EST_FIFO_TOTAL =
  "W-2 gross plus FIFO trading income plus other income for the month. YTD sum of this column drives tax-due calculations.";

const TIP_EST_FIFO_TOTAL_QUARTER =
  "W-2 gross plus FIFO trading income plus other income for this quarter.";

const TIP_EST_FIFO_TOTAL_ANNUAL =
  "W-2 gross plus FIFO trading income plus other income for the full year. This annual total drives the tax-due calculation.";

const TIP_EST_SAFE_HARBOR =
  "Cumulative safe-harbor target: prior-year total tax × months elapsed ÷ 12.";

const TIP_EST_TAX_DUE =
  "Cumulative marginal tax on YTD W-2 + FIFO + other income through Settings brackets. Standard deduction applies only in annual totals.";

const TIP_EST_TAX_DUE_ANNUAL =
  "Full-year federal tax on W-2 + FIFO + other income minus the standard deduction, using bracket rates.";

const TIP_EST_W2_TAX =
  "W-2 withholding for the month (Anton + Olha paycheck tax paid).";

const TIP_EST_EST_PAID =
  "Estimated tax payments in this month. Click to add, edit, or delete payment records with dates.";

const TIP_EST_TOTAL_PAID =
  "Cumulative W-2 withholding plus other tax contributed plus estimated tax payments through this month.";

const TIP_IRS_INTEREST_ANNUAL =
  "IRS underpayment rate (federal short-term rate + 3 percentage points). Used for Form 2210 penalty estimates on safe-harbor shortfalls.";

const TIP_IRS_OPPORTUNITY_MONTHLY =
  "Expected monthly return on unpaid estimated tax kept invested instead of paying early.";

const TIP_EST_REMAINING =
  "Safe-harbor target minus cumulative payments. Positive = underpaid safe harbour. Click for month-by-month breakdown.";

const TIP_EST_SAFE_HARBOR_MET =
  "Cumulative payments have reached the safe-harbor target for this month.";

const TIP_EST_IRS_SAFE_HARBOR_BALANCE =
  "Cumulative total tax due minus cumulative W-2 withholding and estimated payments. Red = underpaid; green = overpaid (always shown as a positive amount).";

const TIP_EST_IRS_EST_UNPAID =
  "Cumulative estimated tax still owed (YTD tax due minus YTD W-2 withholding and estimated payments). Base balance for IRS opportunity.";

const TIP_EST_IRS_INTEREST =
  "Form 2210 penalty on safe-harbour underpayment: shortfall × rate × days ÷ 365 from each quarter due date. Click for month-by-month breakdown.";

const TIP_EST_IRS_OPPORTUNITY =
  "Estimated return on unpaid tax: each month-end balance starts accruing the 1st of the following month through today (or quarter-end). Click for month-by-month breakdown.";

const TIP_EST_TAX_REMAIN =
  "Cumulative tax owed minus cumulative W-2 withholding and estimated payments.";

const TIP_EST_TOTAL_TAX_OWED =
  "Cumulative marginal federal tax on W-2 + FIFO income through this quarter (Settings brackets, no standard deduction).";

const TIP_EST_TOTAL_TAX_OWED_ANNUAL =
  "Cumulative full-year federal tax on W-2 + FIFO income minus the standard deduction, using marginal tax brackets from Settings.";

const TIP_EST_W2_TAX_QUARTER =
  "W-2 withholding in this quarter (Anton + Olha).";

const TIP_EST_EST_PAID_QUARTER =
  "Estimated tax payments entered for this quarter.";

const TIP_EST_SAFE_HARBOR_QUARTER =
  "This quarter's share of the annual safe-harbor target (prior-year total tax ÷ 4).";

const TIP_EST_TAX_DUE_QUARTER =
  "Marginal federal tax on this quarter's W-2 + FIFO + other income, using bracket rates after prior quarters' income. No standard deduction.";

const TIP_EST_TOTAL_PAID_QUARTER =
  "W-2 withholding plus other tax plus estimated tax payments in this quarter.";

const TIP_EST_REMAINING_QUARTER =
  "This quarter's safe-harbor target minus quarter payments. Positive = underpaid. Click for month-by-month breakdown.";

const TIP_EST_TAX_REMAIN_ANNUAL =
  "Cumulative full-year tax on W-2 + FIFO + other income (after standard deduction) minus cumulative W-2 withholding, other tax, and estimated payments.";

const TIP_EST_W2_TAX_YTD =
  "Cumulative W-2 withholding through year-end (Anton + Olha).";

const TIP_EST_EST_PAID_YTD =
  "Cumulative estimated tax payments entered through year-end.";

const EST_SECTION_START = " sfe-tax-estimate-section-start";
const EST_SECTION_END = " sfe-tax-estimate-section-end";
const EST_SECTION_INCOME = " sfe-tax-estimate-section-income";
const EST_SECTION_TAX = " sfe-tax-estimate-section-tax";
const EST_SECTION_PAYMENTS = " sfe-tax-estimate-section-payments";

const TAX_COLLAPSIBLE_SCOPE = "tax";

const ALL_TAX_SECTIONS = [
  "settings",
  "w2",
  "other-income",
  "estimate",
  "quarter-0",
  "quarter-1",
  "quarter-2",
  "quarter-3",
  "annual",
] as const;

function loadTaxCollapsedSections(): Set<string> {
  return loadCollapsedSectionIds(TAX_COLLAPSIBLE_SCOPE, ALL_TAX_SECTIONS, (section) => section === "annual");
}

function quarterSectionKey(quarterIndex: number): string {
  return `quarter-${quarterIndex}`;
}

function emptyPaycheck(): TaxPaycheck {
  return { grossIncome: null, totalTaxPaid: null, noIncome: false };
}

function emptyPaycheckYear(): TaxPaycheckYear {
  return MONTH_LABELS.map(() => [emptyPaycheck(), emptyPaycheck()]);
}

function emptyYearInputs(mode: YearMode): TaxYearInputs {
  return {
    mode,
    w2PaychecksAnton: emptyPaycheckYear(),
    w2PaychecksOlha: emptyPaycheckYear(),
    w2NoIncomeAnton: MONTH_LABELS.map(() => false),
    w2NoIncomeOlha: MONTH_LABELS.map(() => false),
    w2IncomeAnton: null,
    w2IncomeOlha: null,
    tradingIncome: null,
    tradingIncomeMonths: MONTH_LABELS.map(() => null),
    standardDeduction: null,
    investmentPortfolioEoy: null,
    priorYearTotalTax: null,
    pickPriorYearTotalTax: false,
    w2WithholdingMonthlyAnton: null,
    w2WithholdingMonthlyOlha: null,
    quarterlyPaidAfterW2: [null, null, null, null],
    estimatedTaxPaidMonths: MONTH_LABELS.map(() => null),
    estimatedTaxPayments: [],
    otherIncomeItems: [],
    factTotalEstimatedTaxPaid: null,
    irsInterestAprPct: 8,
    profitPerMonthPct: 6,
  };
}

function normalizePaycheckYear(value: unknown): TaxPaycheckYear {
  const source = Array.isArray(value) ? value : [];
  return MONTH_LABELS.map((_, monthIndex) => {
    const month = Array.isArray(source[monthIndex]) ? source[monthIndex] : [];
    return [0, 1].map((paycheckIndex) => {
      const paycheck =
        month[paycheckIndex] && typeof month[paycheckIndex] === "object"
          ? month[paycheckIndex] as Partial<TaxPaycheck>
          : {};
      return {
        grossIncome:
          paycheck.grossIncome != null && Number.isFinite(Number(paycheck.grossIncome))
            ? Number(paycheck.grossIncome)
            : null,
        totalTaxPaid:
          paycheck.totalTaxPaid != null && Number.isFinite(Number(paycheck.totalTaxPaid))
            ? Number(paycheck.totalTaxPaid)
            : null,
        noIncome: paycheck.noIncome === true,
      };
    }) as TaxPaycheckMonth;
  });
}

function normalizeTradingIncomeMonths(value: unknown): (number | null)[] {
  const source = Array.isArray(value) ? value : [];
  return MONTH_LABELS.map((_, monthIndex) => {
    const amount = source[monthIndex];
    return amount != null && Number.isFinite(Number(amount)) ? Number(amount) : null;
  });
}

function normalizeEstimatedTaxPaidMonths(value: unknown): (number | null)[] {
  const source = Array.isArray(value) ? value : [];
  return MONTH_LABELS.map((_, monthIndex) => {
    const amount = source[monthIndex];
    return amount != null && Number.isFinite(Number(amount)) ? Number(amount) : null;
  });
}

function newEstimatedTaxPaymentId(): string {
  return `est-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function newOtherIncomeItemId(): string {
  return `oth-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function normalizeOtherIncomeItems(value: unknown): TaxOtherIncomeItem[] {
  if (!Array.isArray(value)) return [];
  const items: TaxOtherIncomeItem[] = [];
  for (const entry of value) {
    if (!entry || typeof entry !== "object") continue;
    const row = entry as Partial<TaxOtherIncomeItem>;
    const amount = row.amount != null && Number.isFinite(Number(row.amount)) ? Number(row.amount) : null;
    const taxContributed =
      row.taxContributed != null && Number.isFinite(Number(row.taxContributed))
        ? Number(row.taxContributed)
        : 0;
    const date = typeof row.date === "string" ? row.date.trim() : "";
    const id = typeof row.id === "string" && row.id ? row.id : newOtherIncomeItemId();
    if (amount == null || !/^\d{4}-\d{2}-\d{2}$/.test(date)) continue;
    const person: TaxPersonId = row.person === "olha" ? "olha" : "anton";
    const note = typeof row.note === "string" ? row.note.trim() : "";
    items.push({
      id,
      date,
      amount,
      taxContributed: Math.max(0, taxContributed),
      person,
      note,
    });
  }
  return items.sort((a, b) => a.date.localeCompare(b.date) || a.id.localeCompare(b.id));
}

/** Attribute an other-income date to a month inside the selected tax year. */
function otherIncomeMonthIndex(date: string, year: TaxYear): number {
  const parsed = parseIsoDate(date);
  if (!parsed) return 0;
  if (parsed.year === year) return parsed.monthIndex;
  if (parsed.year < year) return 0;
  return 11;
}

function otherIncomeForMonth(y: TaxYearInputs, year: TaxYear, monthIndex: number): number {
  return (y.otherIncomeItems ?? []).reduce((sum, item) => {
    return otherIncomeMonthIndex(item.date, year) === monthIndex ? sum + item.amount : sum;
  }, 0);
}

function otherTaxPaidForMonth(y: TaxYearInputs, year: TaxYear, monthIndex: number): number {
  return (y.otherIncomeItems ?? []).reduce((sum, item) => {
    return otherIncomeMonthIndex(item.date, year) === monthIndex ? sum + item.taxContributed : sum;
  }, 0);
}

function otherIncomeYearTotals(items: TaxOtherIncomeItem[]): { amount: number; taxContributed: number } {
  return items.reduce(
    (sum, item) => ({
      amount: sum.amount + item.amount,
      taxContributed: sum.taxContributed + item.taxContributed,
    }),
    { amount: 0, taxContributed: 0 },
  );
}

function otherIncomeYearTotalsByStatus(
  items: TaxOtherIncomeItem[],
  year: TaxYear,
): { fact: { amount: number; taxContributed: number }; forecast: { amount: number; taxContributed: number } } {
  const fact = { amount: 0, taxContributed: 0 };
  const forecast = { amount: 0, taxContributed: 0 };
  for (const item of items) {
    const monthIndex = otherIncomeMonthIndex(item.date, year);
    const bucket = isTaxMonthForecast(year, monthIndex) ? forecast : fact;
    bucket.amount += item.amount;
    bucket.taxContributed += item.taxContributed;
  }
  return { fact, forecast };
}

function normalizeEstimatedTaxPayments(
  value: unknown,
  fallbackTaxYear: TaxYear,
): TaxEstimatedPayment[] {
  if (!Array.isArray(value)) return [];
  const payments: TaxEstimatedPayment[] = [];
  for (const entry of value) {
    if (!entry || typeof entry !== "object") continue;
    const row = entry as Partial<TaxEstimatedPayment>;
    const amount = row.amount != null && Number.isFinite(Number(row.amount)) ? Number(row.amount) : null;
    const date = typeof row.date === "string" ? row.date.trim() : "";
    const id = typeof row.id === "string" && row.id ? row.id : newEstimatedTaxPaymentId();
    if (amount == null || !/^\d{4}-\d{2}-\d{2}$/.test(date)) continue;
    const note = typeof row.note === "string" ? row.note.trim() : "";
    const taxYear =
      row.taxYear != null && Number.isInteger(Number(row.taxYear))
        ? Number(row.taxYear)
        : fallbackTaxYear;
    const kind: TaxPaymentKind =
      row.kind === "balance-due" ||
      row.kind === "extension" ||
      row.kind === "other"
        ? row.kind
        : "estimated";
    const fromOwnCapital =
      row.fromOwnCapital === true ? true : row.fromOwnCapital === false ? false : undefined;
    payments.push({
      id,
      taxYear,
      kind,
      date,
      amount,
      note,
      ...(fromOwnCapital !== undefined ? { fromOwnCapital } : {}),
    });
  }
  return payments.sort((a, b) => a.date.localeCompare(b.date) || a.id.localeCompare(b.id));
}

function defaultFromOwnCapitalForTaxYear(taxYear: number): boolean {
  return taxYear >= OWN_CAPITAL_TAX_PAYMENTS_FROM_YEAR;
}

function paymentFromOwnCapital(payment: TaxEstimatedPayment): boolean {
  if (payment.fromOwnCapital === true) return true;
  if (payment.fromOwnCapital === false) return false;
  return defaultFromOwnCapitalForTaxYear(payment.taxYear);
}

function formatIsoDateLocal(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function paymentMonthIndex(date: string, year: TaxYear): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return null;
  const paymentYear = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  if (paymentYear !== year || monthIndex < 0 || monthIndex >= MONTH_LABELS.length) return null;
  return monthIndex;
}

function migrateLegacyEstimatedTaxPayments(
  value: Partial<TaxYearInputs> | undefined,
  year: TaxYear,
): TaxEstimatedPayment[] {
  const existing = normalizeEstimatedTaxPayments(value?.estimatedTaxPayments, year);
  if (existing.length > 0) return existing;

  const payments: TaxEstimatedPayment[] = [];
  const months = value?.estimatedTaxPaidMonths;
  if (Array.isArray(months)) {
    months.forEach((amount, monthIndex) => {
      if (amount != null && amount !== 0) {
        payments.push({
          id: newEstimatedTaxPaymentId(),
          taxYear: year,
          kind: "estimated",
          date: `${year}-${String(monthIndex + 1).padStart(2, "0")}-15`,
          amount,
          note: "",
        });
      }
    });
  }
  if (payments.length === 0 && Array.isArray(value?.quarterlyPaidAfterW2)) {
    value.quarterlyPaidAfterW2.forEach((amount, quarterIndex) => {
      if (amount != null && amount !== 0) {
        payments.push({
          id: newEstimatedTaxPaymentId(),
          taxYear: year,
          kind: "estimated",
          date: formatIsoDateLocal(quarterDueDate(year, quarterIndex)),
          amount,
          note: "",
        });
      }
    });
  }
  return payments;
}

function syncEstimatedTaxPaidMonthsFromPayments(
  year: TaxYear,
  payments: TaxEstimatedPayment[],
): (number | null)[] {
  return MONTH_LABELS.map((_, monthIndex) => {
    const total = payments
      .filter(
        (payment) =>
          payment.kind === "estimated" &&
          paymentMonthIndex(payment.date, year) === monthIndex,
      )
      .reduce((sum, payment) => sum + payment.amount, 0);
    return total !== 0 ? total : null;
  });
}

function estimatedTaxPaymentsForYear(
  year: TaxYear,
  payments: TaxEstimatedPayment[],
): TaxEstimatedPayment[] {
  return payments.filter((payment) => payment.taxYear === year);
}

function defaultEstPaymentDate(year: TaxYear, monthIndex: number): string {
  return `${year}-${String(monthIndex + 1).padStart(2, "0")}-15`;
}

function defaultOtherIncomeDate(year: TaxYear): string {
  const now = new Date();
  if (now.getFullYear() === year) {
    return isoFromDateParts(year, now.getMonth(), now.getDate());
  }
  return isoFromDateParts(year, 0, 1);
}

function paymentDateAppliesToTaxYear(date: string, taxYear: TaxYear): boolean {
  const parsed = parseIsoDate(date);
  if (!parsed) return false;
  if (parsed.year === taxYear) return true;
  return (
    parsed.year === taxYear + 1 &&
    (parsed.monthIndex < 3 || (parsed.monthIndex === 3 && parsed.day <= 15))
  );
}

function taxPaymentKindLabel(kind: TaxPaymentKind): string {
  if (kind === "balance-due") return "Balance due";
  if (kind === "extension") return "Extension";
  if (kind === "other") return "Other";
  return "Estimated";
}

const EST_PAYMENT_WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"] as const;

function parseIsoDate(iso: string): { year: number; monthIndex: number; day: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  if (!Number.isFinite(year) || monthIndex < 0 || monthIndex > 11 || day < 1 || day > 31) {
    return null;
  }
  return { year, monthIndex, day };
}

function isoFromDateParts(year: number, monthIndex: number, day: number): string {
  return `${year}-${String(monthIndex + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function formatEstPaymentDisplayDate(iso: string): string {
  const parsed = parseIsoDate(iso);
  if (!parsed) return iso;
  return `${MONTH_LABELS[parsed.monthIndex]} ${parsed.day}, ${parsed.year}`;
}

function renderEstPaymentCalendarInner(
  taxYear: TaxYear,
  viewMonthIndex: number,
  selectedIso: string,
): string {
  const calendarYear = taxYear + Math.floor(viewMonthIndex / 12);
  const calendarMonthIndex = viewMonthIndex % 12;
  const calendarDaysInMonth = new Date(calendarYear, calendarMonthIndex + 1, 0).getDate();
  const daysInMonth =
    viewMonthIndex === 15 ? Math.min(15, calendarDaysInMonth) : calendarDaysInMonth;
  const firstWeekday = new Date(calendarYear, calendarMonthIndex, 1).getDay();
  const dayButtons: string[] = [];

  for (let pad = 0; pad < firstWeekday; pad += 1) {
    dayButtons.push(`<span class="sfe-tax-est-cal-pad" aria-hidden="true"></span>`);
  }
  for (let day = 1; day <= daysInMonth; day += 1) {
    const iso = isoFromDateParts(calendarYear, calendarMonthIndex, day);
    const selected = iso === selectedIso;
    dayButtons.push(`
      <button type="button" class="sfe-tax-est-cal-day${selected ? " is-selected" : ""}" data-date="${esc(iso)}">${day}</button>`);
  }

  return `
    <div class="sfe-tax-est-cal-head">
      <button type="button" class="sfe-tax-est-cal-nav" data-cal-prev${viewMonthIndex === 0 ? " disabled" : ""} aria-label="Previous month">‹</button>
      <span class="sfe-tax-est-cal-title">${esc(MONTH_LABELS[calendarMonthIndex])} ${calendarYear}</span>
      <button type="button" class="sfe-tax-est-cal-nav" data-cal-next${viewMonthIndex === 15 ? " disabled" : ""} aria-label="Next month">›</button>
    </div>
    <div class="sfe-tax-est-cal-weekdays">
      ${EST_PAYMENT_WEEKDAY_LABELS.map((label) => `<span>${label}</span>`).join("")}
    </div>
    <div class="sfe-tax-est-cal-grid">${dayButtons.join("")}</div>`;
}

function bindEstPaymentDateField(
  root: HTMLElement,
  taxYear: TaxYear,
  onDateChange: (iso: string) => void,
): void {
  const field = root.querySelector<HTMLElement>(".sfe-tax-est-payments-date-field");
  if (!field) return;
  const hidden = field.querySelector<HTMLInputElement>(".sfe-tax-est-payments-date");
  const trigger = field.querySelector<HTMLButtonElement>(".sfe-tax-est-payments-date-trigger");
  const popout = field.querySelector<HTMLDivElement>(".sfe-tax-est-payments-calendar");
  if (!hidden || !trigger || !popout) return;

  const initialDate = parseIsoDate(hidden.value);
  let viewMonthIndex = initialDate
    ? initialDate.monthIndex + Math.max(0, initialDate.year - taxYear) * 12
    : 0;
  let outsideListener: ((event: MouseEvent) => void) | null = null;

  const closeCalendar = () => {
    popout.hidden = true;
    field.classList.remove("is-open");
    trigger.setAttribute("aria-expanded", "false");
    if (outsideListener) {
      document.removeEventListener("click", outsideListener);
      outsideListener = null;
    }
  };

  const renderCalendar = () => {
    popout.innerHTML = renderEstPaymentCalendarInner(taxYear, viewMonthIndex, hidden.value);
    popout.querySelector<HTMLButtonElement>("[data-cal-prev]")?.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (viewMonthIndex > 0) {
        viewMonthIndex -= 1;
        renderCalendar();
      }
    });
    popout.querySelector<HTMLButtonElement>("[data-cal-next]")?.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (viewMonthIndex < 15) {
        viewMonthIndex += 1;
        renderCalendar();
      }
    });
    popout.querySelectorAll<HTMLButtonElement>(".sfe-tax-est-cal-day").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        const iso = button.dataset.date;
        if (!iso) return;
        hidden.value = iso;
        const label = field.querySelector(".sfe-tax-est-payments-date-label");
        if (label) label.textContent = formatEstPaymentDisplayDate(iso);
        onDateChange(iso);
        closeCalendar();
        renderCalendar();
      });
    });
  };

  const openCalendar = (event: Event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!popout.hidden) {
      closeCalendar();
      return;
    }
    const parsed = parseIsoDate(hidden.value);
    if (parsed) {
      viewMonthIndex = parsed.monthIndex + Math.max(0, parsed.year - taxYear) * 12;
    }
    renderCalendar();
    popout.hidden = false;
    field.classList.add("is-open");
    trigger.setAttribute("aria-expanded", "true");
    if (outsideListener) {
      document.removeEventListener("click", outsideListener);
    }
    outsideListener = (mouseEvent: MouseEvent) => {
      const target = mouseEvent.target as Node;
      if (field.contains(target)) return;
      closeCalendar();
    };
    window.setTimeout(() => {
      if (outsideListener) document.addEventListener("click", outsideListener);
    }, 0);
  };

  trigger.addEventListener("click", openCalendar);
}

function normalizeYearInputs(
  value: Partial<TaxYearInputs> | undefined,
  mode: YearMode,
  year?: TaxYear,
): TaxYearInputs {
  const base = emptyYearInputs(mode);
  const taxYear = year ?? (mode === "fact" ? 2026 : 2027);
  const estimatedTaxPayments = migrateLegacyEstimatedTaxPayments(value, taxYear);
  const estimatedTaxPaidMonths =
    estimatedTaxPayments.length > 0
      ? syncEstimatedTaxPaidMonthsFromPayments(taxYear, estimatedTaxPayments)
      : normalizeEstimatedTaxPaidMonths(value?.estimatedTaxPaidMonths);
  return {
    ...base,
    ...value,
    w2PaychecksAnton: normalizePaycheckYear(value?.w2PaychecksAnton),
    w2PaychecksOlha: normalizePaycheckYear(value?.w2PaychecksOlha),
    w2NoIncomeAnton: MONTH_LABELS.map((_, index) => value?.w2NoIncomeAnton?.[index] === true),
    w2NoIncomeOlha: MONTH_LABELS.map((_, index) => value?.w2NoIncomeOlha?.[index] === true),
    tradingIncomeMonths: normalizeTradingIncomeMonths(value?.tradingIncomeMonths),
    estimatedTaxPayments,
    estimatedTaxPaidMonths,
    otherIncomeItems: normalizeOtherIncomeItems(value?.otherIncomeItems),
    pickPriorYearTotalTax: value?.pickPriorYearTotalTax === true,
    quarterlyPaidAfterW2: Array.isArray(value?.quarterlyPaidAfterW2)
      ? [
          value.quarterlyPaidAfterW2[0] ?? null,
          value.quarterlyPaidAfterW2[1] ?? null,
          value.quarterlyPaidAfterW2[2] ?? null,
          value.quarterlyPaidAfterW2[3] ?? null,
        ]
      : base.quarterlyPaidAfterW2,
  };
}

export function defaultTaxSettings(): TaxSettings {
  const years: Partial<Record<string, TaxYearInputs>> = {};
  const bracketsByYear: Partial<Record<string, TaxBracket[]>> = {};
  const copyBracketsFromPrevious: Partial<Record<string, boolean>> = {};
  for (const y of getTaxYears()) {
    years[String(y)] = emptyYearInputs(defaultYearMode(y));
    bracketsByYear[String(y)] =
      y === 2025
        ? DEFAULT_BRACKETS.map((b) => ({ ...b }))
        : y === 2026
          ? TAX_BRACKETS_2026.map((b) => ({ ...b }))
          : DEFAULT_BRACKETS.map((b) => ({ ratePct: b.ratePct, from: 0, to: 0 }));
    copyBracketsFromPrevious[String(y)] = y > 2026;
  }
  return {
    filingStatus: "mfj",
    w2PersonNames: { anton: "TaxPayer1", olha: "TaxPayer2" },
    brackets: DEFAULT_BRACKETS.map((b) => ({ ...b })),
    bracketsByYear,
    copyBracketsFromPrevious,
    defaultStandardDeduction: 31_500,
    irsInterestAprPct: 8,
    irsOpportunityMonthlyPct: 6,
    years,
  };
}

function legacyIrsInterestAprPct(parsed: Partial<TaxSettings>): number | null {
  for (const year of getTaxYears()) {
    const value = parsed.years?.[String(year)]?.irsInterestAprPct;
    if (value != null && Number.isFinite(Number(value))) return Number(value);
  }
  return null;
}

function normalizeTaxSettings(parsed: Partial<TaxSettings>): TaxSettings {
  const base = defaultTaxSettings();
  const legacyBrackets = parsed.brackets?.length ? parsed.brackets : base.brackets;
  const saved2026Brackets = parsed.bracketsByYear?.["2026"];
  const migrateLegacy2026Brackets =
    !saved2026Brackets?.length || bracketsMatch(saved2026Brackets, DEFAULT_BRACKETS);
  const bracketsByYear = Object.fromEntries(
    getTaxYears().map((year) => {
      const saved = parsed.bracketsByYear?.[String(year)];
      // Before 2025 became selectable, the 2025 thresholds were also used as
      // the 2026 defaults. Upgrade only that untouched legacy value.
      const migrateLegacy2026 =
        year === 2026 && migrateLegacy2026Brackets;
      const fallback =
        year === 2025
          ? DEFAULT_BRACKETS
          : year === 2026
            ? TAX_BRACKETS_2026
            : legacyBrackets;
      return [
        String(year),
        saved?.length && !migrateLegacy2026
          ? saved.map((b) => ({ ...b }))
          : fallback.map((b) => ({ ...b })),
      ];
    }),
  );
  const normalizedYears = Object.fromEntries(
    getTaxYears().map((year) => [
      String(year),
      normalizeYearInputs(
        parsed.years?.[String(year)],
        defaultYearMode(year),
        year,
      ),
    ]),
  ) as Record<string, TaxYearInputs>;

  // Early-year balance/extension payments settle the prior tax year. Older
  // saved data attributed them to the payment's calendar year because 2025 was
  // not selectable; migrate and redistribute all payments by explicit taxYear.
  const paymentsByTaxYear = new Map<number, TaxEstimatedPayment[]>();
  const seenPaymentIds = new Set<string>();
  for (const sourceYear of getTaxYears()) {
    for (const payment of normalizedYears[String(sourceYear)].estimatedTaxPayments) {
      if (seenPaymentIds.has(payment.id)) continue;
      seenPaymentIds.add(payment.id);
      const parsedDate = parseIsoDate(payment.date);
      const isEarlyYearPriorTaxPayment =
        (payment.kind === "balance-due" || payment.kind === "extension") &&
        parsedDate?.year === payment.taxYear &&
        (parsedDate.monthIndex < 3 || (parsedDate.monthIndex === 3 && parsedDate.day <= 15));
      const inferredTaxYear =
        isEarlyYearPriorTaxPayment &&
        (getTaxYears() as readonly number[]).includes(payment.taxYear - 1)
          ? payment.taxYear - 1
          : payment.taxYear;
      const migrated = inferredTaxYear === payment.taxYear
        ? payment
        : { ...payment, taxYear: inferredTaxYear };
      const target = paymentsByTaxYear.get(inferredTaxYear) ?? [];
      target.push(migrated);
      paymentsByTaxYear.set(inferredTaxYear, target);
    }
  }
  for (const year of getTaxYears()) {
    const payments = (paymentsByTaxYear.get(year) ?? []).sort(
      (a, b) => a.date.localeCompare(b.date) || a.id.localeCompare(b.id),
    );
    normalizedYears[String(year)] = {
      ...normalizedYears[String(year)],
      estimatedTaxPayments: payments,
      estimatedTaxPaidMonths: syncEstimatedTaxPaidMonthsFromPayments(year, payments),
    };
  }

  return {
    ...base,
    ...parsed,
    w2PersonNames: {
      anton:
        typeof parsed.w2PersonNames?.anton === "string" &&
        parsed.w2PersonNames.anton.trim()
          ? parsed.w2PersonNames.anton.trim()
          : base.w2PersonNames.anton,
      olha:
        typeof parsed.w2PersonNames?.olha === "string" &&
        parsed.w2PersonNames.olha.trim()
          ? parsed.w2PersonNames.olha.trim()
          : base.w2PersonNames.olha,
    },
    brackets: legacyBrackets,
    bracketsByYear,
    copyBracketsFromPrevious: {
      ...base.copyBracketsFromPrevious,
      ...parsed.copyBracketsFromPrevious,
      ...(migrateLegacy2026Brackets ? { "2026": false } : {}),
    },
    defaultStandardDeduction:
      parsed.defaultStandardDeduction != null &&
      Number.isFinite(Number(parsed.defaultStandardDeduction))
        ? Number(parsed.defaultStandardDeduction)
        : base.defaultStandardDeduction,
    irsInterestAprPct:
      parsed.irsInterestAprPct != null && Number.isFinite(Number(parsed.irsInterestAprPct))
        ? Number(parsed.irsInterestAprPct)
        : (legacyIrsInterestAprPct(parsed) ?? base.irsInterestAprPct),
    irsOpportunityMonthlyPct:
      parsed.irsOpportunityMonthlyPct != null &&
      Number.isFinite(Number(parsed.irsOpportunityMonthlyPct))
        ? Number(parsed.irsOpportunityMonthlyPct)
        : base.irsOpportunityMonthlyPct,
    years: normalizedYears,
  };
}

function loadTaxSettingsFromLocal(): TaxSettings {
  try {
    const raw = localStorage.getItem(taxSettingsStorageKey());
    return raw
      ? normalizeTaxSettings(JSON.parse(raw) as Partial<TaxSettings>)
      : defaultTaxSettings();
  } catch {
    return defaultTaxSettings();
  }
}

function writeTaxSettingsLocal(settings: TaxSettings): void {
  localStorage.setItem(taxSettingsStorageKey(), JSON.stringify(settings));
}

function countEstimatedTaxPayments(settings: TaxSettings): number {
  let count = 0;
  for (const year of getTaxYears()) {
    count += yearInputs(settings, year).estimatedTaxPayments.length;
  }
  return count;
}

function paymentIds(settings: TaxSettings): Set<string> {
  const ids = new Set<string>();
  for (const year of getTaxYears()) {
    for (const payment of yearInputs(settings, year).estimatedTaxPayments) {
      ids.add(payment.id);
    }
  }
  return ids;
}

function otherIncomeIds(settings: TaxSettings): Set<string> {
  const ids = new Set<string>();
  for (const year of getTaxYears()) {
    for (const item of yearInputs(settings, year).otherIncomeItems) {
      ids.add(item.id);
    }
  }
  return ids;
}

/** Union estimated-tax payments by id (local wins on conflict); keep other server fields. */
function mergeTaxSettings(local: TaxSettings, server: TaxSettings): TaxSettings {
  let merged = normalizeTaxSettings(server);
  for (const year of getTaxYears()) {
    const byId = new Map<string, TaxEstimatedPayment>();
    for (const payment of yearInputs(server, year).estimatedTaxPayments) {
      byId.set(payment.id, payment);
    }
    for (const payment of yearInputs(local, year).estimatedTaxPayments) {
      byId.set(payment.id, payment);
    }
    const payments = [...byId.values()].sort(
      (a, b) => a.date.localeCompare(b.date) || a.id.localeCompare(b.id),
    );
    const otherById = new Map<string, TaxOtherIncomeItem>();
    for (const item of yearInputs(server, year).otherIncomeItems) {
      otherById.set(item.id, item);
    }
    for (const item of yearInputs(local, year).otherIncomeItems) {
      otherById.set(item.id, item);
    }
    const otherIncomeItems = [...otherById.values()].sort(
      (a, b) => a.date.localeCompare(b.date) || a.id.localeCompare(b.id),
    );
    merged = setYear(merged, year, {
      estimatedTaxPayments: payments,
      estimatedTaxPaidMonths: syncEstimatedTaxPaidMonthsFromPayments(year, payments),
      otherIncomeItems,
    });
  }
  return normalizeTaxSettings(merged);
}

function localHasPaymentsMissingFromServer(local: TaxSettings, server: TaxSettings): boolean {
  const serverIds = paymentIds(server);
  for (const id of paymentIds(local)) {
    if (!serverIds.has(id)) return true;
  }
  const serverOtherIds = otherIncomeIds(server);
  for (const id of otherIncomeIds(local)) {
    if (!serverOtherIds.has(id)) return true;
  }
  return false;
}

async function requestServerTaxSettings(
  method: "GET" | "PUT",
  settings?: TaxSettings,
): Promise<TaxSettings | null> {
  const config = getApiConfig();
  const headers: Record<string, string> = { Accept: "application/json" };
  if (settings) headers["Content-Type"] = "application/json";
  const response = await proxyFetch(`${config.baseUrl.replace(/\/$/, "")}/tax-settings`, {
    method,
    headers,
    credentials: "include",
    body: settings ? JSON.stringify({ settings }) : undefined,
  });
  if (!response.ok) {
    throw new Error(`Tax settings ${method} failed (${response.status})`);
  }
  const payload = (await response.json()) as { settings?: Partial<TaxSettings> | null };
  return payload.settings ? normalizeTaxSettings(payload.settings) : null;
}

registerTaxStoreDeps<TaxSettings>({
  createDefault: () => defaultTaxSettings(),
  loadLocal: loadTaxSettingsFromLocal,
  saveLocal: (settings) => writeTaxSettingsLocal(settings),
  merge: mergeTaxSettings,
  localHasPaymentsMissingFromServer,
  fetchServer: () => requestServerTaxSettings("GET"),
  pushServer: async (settings) => {
    await requestServerTaxSettings("PUT", settings);
  },
});

export function loadTaxSettings(): TaxSettings {
  return getTaxSettingsFromStore<TaxSettings>();
}

export function saveTaxSettings(settings: TaxSettings, opts?: { silent?: boolean }): void {
  commitTaxSettingsToStore(settings, { silent: opts?.silent });
}

/** Merge server tax settings into local (single-flight via taxStore). */
async function ensureTaxSettingsHydrated(): Promise<TaxSettings> {
  return ensureTaxStoreReady<TaxSettings>();
}

function esc(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function renderTaxHint(tip: string): string {
  return `<span class="sfe-tax-hint" tabindex="0" role="img" aria-label="${esc(tip)}" data-sfe-tax-tip="${esc(tip)}">i</span>`;
}

function renderTaxColHead(label: string, tip?: string): string {
  if (!tip) return label;
  return `<span class="sfe-tax-paycheck-col-head-label">${label}</span>${renderTaxHint(tip)}`;
}

function fmtMoney(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "";
  return n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function fmtWholeMoney(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "";
  return n.toLocaleString("en-US", {
    maximumFractionDigits: 0,
  });
}

function fmtBracketDifference(
  value: number | null | undefined,
  previousValue: number | null | undefined,
): string {
  if (value == null || previousValue == null) return "";
  const difference = value - previousValue;
  if (Math.abs(difference) < 0.005) return "";
  return `(${difference > 0 ? "+" : ""}${difference.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })})`;
}

function fmtBracketMoney(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "";
  return `$${n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function parseNum(raw: string): number | null {
  const cleaned = raw.replace(/[$,%\s]/g, "");
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function yearInputs(settings: TaxSettings, year: TaxYear): TaxYearInputs {
  return normalizeYearInputs(
    settings.years[String(year)],
    defaultYearMode(year),
    year,
  );
}

function taxYearChipClassName(year: TaxYear, isSelected: boolean): string {
  const classes = ["year-chip", "sfe-income-year-chip"];
  const nowYear = new Date().getFullYear();
  if (year < nowYear) classes.push("sfe-year-chip--past");
  else if (year === nowYear) classes.push("sfe-year-chip--current");
  else classes.push("sfe-year-chip--blue");
  if (isSelected) classes.push("active");
  return classes.join(" ");
}

function taxYearChipTip(year: TaxYear): string | null {
  const nowYear = new Date().getFullYear();
  if (year === nowYear) return "Fact-Forecast";
  if (year < nowYear) return "Fact";
  return "Forecast";
}

function setYear(settings: TaxSettings, year: TaxYear, patch: Partial<TaxYearInputs>): TaxSettings {
  const cur = yearInputs(settings, year);
  return {
    ...settings,
    years: { ...settings.years, [String(year)]: { ...cur, ...patch } },
  };
}

function taxBracketsForYear(settings: TaxSettings, year: TaxYear): TaxBracket[] {
  const yearIdx = getTaxYears().indexOf(year);
  if (yearIdx > 0 && settings.copyBracketsFromPrevious[String(year)] === true) {
    return taxBracketsForYear(settings, getTaxYears()[yearIdx - 1]);
  }
  return settings.bracketsByYear[String(year)] ?? settings.brackets;
}

function moneyInput(
  id: string,
  label: string,
  value: number | null,
  tip?: string,
): string {
  const hint = tip
    ? renderTaxHint(tip)
    : "";
  return `
    <label class="sfe-tax-field" for="${esc(id)}">
      <span class="kpi-k sfe-tax-field-k">${esc(label)}${hint}</span>
      <div class="sfe-tax-input-wrap">
        <span class="sfe-tax-input-prefix">$</span>
        <input type="text" inputmode="decimal" id="${esc(id)}" class="sfe-tax-input" value="${esc(fmtMoney(value))}" autocomplete="off" />
      </div>
    </label>`;
}

function pctInput(id: string, label: string, value: number | null, tip?: string): string {
  const hint = tip
    ? renderTaxHint(tip)
    : "";
  return `
    <label class="sfe-tax-field" for="${esc(id)}">
      <span class="kpi-k sfe-tax-field-k">${esc(label)}${hint}</span>
      <div class="sfe-tax-input-wrap">
        <input type="text" inputmode="decimal" id="${esc(id)}" class="sfe-tax-input sfe-tax-input-pct" value="${value != null && Number.isFinite(value) ? esc(String(value)) : ""}" autocomplete="off" />
        <span class="sfe-tax-input-suffix">%</span>
      </div>
    </label>`;
}

function readonlyRow(label: string, tip?: string, value = "—"): string {
  const hint = tip ? renderTaxHint(tip) : "";
  return `
    <div class="sfe-tax-readonly-row">
      <span class="kpi-k sfe-tax-field-k">${esc(label)}${hint}</span>
      <span class="kpi-v sfe-tax-readonly-val">${esc(value)}</span>
    </div>`;
}

function taxSettingsMoneyField(
  id: string,
  label: string,
  value: number | null,
  tip?: string,
  actionHtml = "",
  readonly = false,
): string {
  const hint = tip ? renderTaxHint(tip) : "";
  const displayValue = value == null ? "—" : `$${fmtMoney(value)}`;
  const readonlyCls = readonly ? " sfe-tax-settings-kpi-readonly" : "";
  const editDisabled = readonly ? " disabled" : "";
  const editTitle = readonly ? "Uncheck Pick from last year to edit" : "Edit";
  return `
    <div class="kpi sfe-tax-settings-kpi${readonlyCls}">
      <span class="kpi-k sfe-tax-settings-kpi-label">${esc(label)}${hint}</span>
      <div class="sfe-tax-kpi-value-row sfe-tax-settings-money-view">
        <span class="kpi-v">${displayValue}</span>
        <button type="button" class="sfe-tax-settings-edit-btn" aria-label="Edit ${esc(label)}" title="${esc(editTitle)}"${editDisabled}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="10" height="10" aria-hidden="true">
            <path fill="currentColor" d="M11.013 1.427a1.75 1.75 0 0 1 2.474 0l1.086 1.086a1.75 1.75 0 0 1 0 2.474l-8.986 8.986a1 1 0 0 1-.446.261l-3.25 1.083a.25.25 0 0 1-.316-.316l1.083-3.25a1 1 0 0 1 .261-.446l8.986-8.986z"/>
          </svg>
        </button>
      </div>
      <div class="sfe-tax-settings-field-footer">${actionHtml}</div>
      <div class="sfe-tax-settings-money-edit" hidden>
        <div class="sfe-tax-input-wrap">
          <span class="sfe-tax-input-prefix">$</span>
          <input type="text" id="${esc(id)}" class="sfe-tax-input" value="${esc(fmtMoney(value))}" autocomplete="off" />
        </div>
      </div>
    </div>`;
}

function taxSettingsPctField(
  id: string,
  label: string,
  value: number | null,
  tip?: string,
): string {
  const hint = tip ? renderTaxHint(tip) : "";
  const displayValue =
    value != null && Number.isFinite(value) ? `${value}%` : "—";
  return `
    <div class="kpi sfe-tax-settings-kpi">
      <span class="kpi-k sfe-tax-settings-kpi-label">${esc(label)}${hint}</span>
      <div class="sfe-tax-kpi-value-row sfe-tax-settings-money-view">
        <span class="kpi-v">${displayValue}</span>
        <button type="button" class="sfe-tax-settings-edit-btn" aria-label="Edit ${esc(label)}" title="Edit">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="10" height="10" aria-hidden="true">
            <path fill="currentColor" d="M11.013 1.427a1.75 1.75 0 0 1 2.474 0l1.086 1.086a1.75 1.75 0 0 1 0 2.474l-8.986 8.986a1 1 0 0 1-.446.261l-3.25 1.083a.25.25 0 0 1-.316-.316l1.083-3.25a1 1 0 0 1 .261-.446l8.986-8.986z"/>
          </svg>
        </button>
      </div>
      <div class="sfe-tax-settings-field-footer"></div>
      <div class="sfe-tax-settings-money-edit" hidden>
        <div class="sfe-tax-input-wrap">
          <input type="text" id="${esc(id)}" class="sfe-tax-input sfe-tax-input-pct" value="${value != null && Number.isFinite(value) ? esc(String(value)) : ""}" autocomplete="off" />
          <span class="sfe-tax-input-suffix">%</span>
        </div>
      </div>
    </div>`;
}

function renderBracketTable(
  brackets: TaxBracket[],
  readonly: boolean,
  previousBrackets: TaxBracket[] | null,
): string {
  const field = (
    bracket: TaxBracket,
    previousBracket: TaxBracket | undefined,
    key: keyof TaxBracket,
  ) => {
    const value = bracket[key];
    const displayValue =
      key === "ratePct"
        ? `${value ?? ""}%`
        : fmtBracketMoney(value);
    const difference =
      key === "ratePct"
        ? ""
        : fmtBracketDifference(value, previousBracket?.[key]);
    return `
      <div class="sfe-tax-bracket-field${key === "ratePct" ? " sfe-tax-bracket-field-rate" : ""}${readonly ? " sfe-tax-bracket-field-readonly" : ""}">
        <div class="sfe-tax-bracket-field-view${difference ? " sfe-tax-bracket-field-view-with-diff" : ""}">
          <span class="sfe-tax-bracket-value${key === "ratePct" ? " sfe-tax-bracket-value-rate" : ""}">${displayValue}</span>
          ${difference ? `<span class="sfe-tax-bracket-year-diff">${esc(difference)}</span>` : ""}
        </div>
      </div>`;
  };

  return `
    <div class="sfe-tax-bracket-card${readonly ? " sfe-tax-bracket-card-readonly" : ""}" role="table" aria-label="Tax brackets"${readonly ? ' aria-disabled="true"' : ""}>
      <div class="sfe-tax-bracket-card-head" role="row">
        <span class="sfe-tax-bracket-col-head sfe-tax-bracket-col-head-rate" role="columnheader">Rate</span>
        <span class="sfe-tax-bracket-income-head sfe-tax-bracket-col-head" role="columnheader">
          <span class="sfe-tax-bracket-col-head-main">Taxable income</span>
          <span class="sfe-tax-bracket-income-subheads">
            <span class="sfe-tax-bracket-col-head-sub">From</span>
            <span class="sfe-tax-bracket-col-head-sub">To</span>
          </span>
        </span>
      </div>
      ${brackets
        .map(
          (bracket, i) => `
            <div class="sfe-tax-bracket-card-row" role="row" data-bracket-idx="${i}">
              <div role="cell">${field(bracket, previousBrackets?.[i], "ratePct")}</div>
              <div class="sfe-tax-bracket-range" role="cell">
                ${field(bracket, previousBrackets?.[i], "from")}
                ${field(bracket, previousBrackets?.[i], "to")}
              </div>
            </div>`,
        )
        .join("")}
    </div>`;
}

function paycheckYearTotals(paychecks: TaxPaycheckYear, noIncomeMonths: boolean[] = []): {
  grossIncome: number;
  totalTaxPaid: number;
} {
  const totals = { grossIncome: 0, totalTaxPaid: 0 };
  paychecks.forEach((month, monthIndex) => {
    if (noIncomeMonths[monthIndex]) return;
    month.forEach((paycheck) => {
      if (paycheck.noIncome) return;
      totals.grossIncome += paycheck.grossIncome ?? 0;
      totals.totalTaxPaid += paycheck.totalTaxPaid ?? 0;
    });
  });
  return totals;
}

type PaycheckTotals = { grossIncome: number; totalTaxPaid: number };

function isPaycheckMonthForecast(year: TaxYear, monthIndex: number): boolean {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  return year > currentYear || (year === currentYear && monthIndex > currentMonth);
}

function paycheckYearTotalsByStatus(
  paychecks: TaxPaycheckYear,
  noIncomeMonths: boolean[],
  year: TaxYear,
): { fact: PaycheckTotals; forecast: PaycheckTotals } {
  const fact = { grossIncome: 0, totalTaxPaid: 0 };
  const forecast = { grossIncome: 0, totalTaxPaid: 0 };
  paychecks.forEach((month, monthIndex) => {
    if (noIncomeMonths[monthIndex]) return;
    const bucket = isPaycheckMonthForecast(year, monthIndex) ? forecast : fact;
    month.forEach((paycheck) => {
      if (paycheck.noIncome) return;
      bucket.grossIncome += paycheck.grossIncome ?? 0;
      bucket.totalTaxPaid += paycheck.totalTaxPaid ?? 0;
    });
  });
  return { fact, forecast };
}

function addPaycheckTotals(a: PaycheckTotals, b: PaycheckTotals): PaycheckTotals {
  return {
    grossIncome: a.grossIncome + b.grossIncome,
    totalTaxPaid: a.totalTaxPaid + b.totalTaxPaid,
  };
}

function renderFactForecastBreakdownRow(
  label: "Fact" | "Forecast",
  totals: PaycheckTotals,
): string {
  const rowClass =
    label === "Fact" ? "sfe-tax-w2-totals-breakdown-row-fact" : "sfe-tax-w2-totals-breakdown-row-forecast";
  const grossCls = totals.grossIncome > 0 ? " roi-strong" : "";
  const taxCls = totals.totalTaxPaid > 0 ? " sfe-neg" : "";
  return `
    <div class="sfe-tax-w2-totals-breakdown-row ${rowClass}">
      <span class="sfe-tax-month-status sfe-tax-month-status-${label === "Fact" ? "fact" : "forecast"}">${label}</span>
      <span class="sfe-tax-w2-totals-breakdown-gross${grossCls}">$${fmtMoney(totals.grossIncome)}</span>
      <span class="sfe-tax-w2-totals-breakdown-tax${taxCls}">$${fmtMoney(totals.totalTaxPaid)}</span>
    </div>`;
}

function renderFactForecastTotalsCard(
  fact: PaycheckTotals,
  forecast: PaycheckTotals,
  columnLabels: { gross?: string; tax?: string } = {},
): string {
  const grossLabel = columnLabels.gross ?? "Gross";
  const taxLabel = columnLabels.tax ?? "Tax";
  return `
    <div class="sfe-tax-w2-totals-breakdown">
      <div class="sfe-tax-w2-totals-breakdown-head" aria-hidden="true">
        <span class="sfe-tax-w2-totals-breakdown-head-spacer"></span>
        <span class="sfe-tax-w2-totals-breakdown-head-label">${esc(grossLabel)}</span>
        <span class="sfe-tax-w2-totals-breakdown-head-label">${esc(taxLabel)}</span>
      </div>
      ${renderFactForecastBreakdownRow("Fact", fact)}
      ${renderFactForecastBreakdownRow("Forecast", forecast)}
    </div>`;
}

function paycheckMonthTotals(
  paychecks: TaxPaycheckMonth,
  noIncome: boolean,
): { grossIncome: number; totalTaxPaid: number } {
  if (noIncome) return { grossIncome: 0, totalTaxPaid: 0 };
  const totals = { grossIncome: 0, totalTaxPaid: 0 };
  paychecks.forEach((paycheck) => {
    if (paycheck.noIncome) return;
    totals.grossIncome += paycheck.grossIncome ?? 0;
    totals.totalTaxPaid += paycheck.totalTaxPaid ?? 0;
  });
  return totals;
}

function paycheckCopySource(
  monthIndex: number,
  paycheckIndex: number,
): { monthIndex: number; paycheckIndex: number } | null {
  if (paycheckIndex > 0) {
    return { monthIndex, paycheckIndex: paycheckIndex - 1 };
  }
  if (monthIndex > 0) {
    return { monthIndex: monthIndex - 1, paycheckIndex: 1 };
  }
  return null;
}

function paycheckCopyTooltip(monthIndex: number, paycheckIndex: number): {
  label: string;
  disabled: boolean;
} {
  const source = paycheckCopySource(monthIndex, paycheckIndex);
  if (!source) {
    return { label: "No previous paycheck", disabled: true };
  }
  if (paycheckIndex > 0) {
    return { label: `Copy from Paycheck ${paycheckIndex}`, disabled: false };
  }
  return {
    label: `Copy from ${MONTH_LABELS[source.monthIndex]} Paycheck 2`,
    disabled: false,
  };
}

function renderCopyPaycheckButton(
  className: string,
  tooltip: string,
  disabled: boolean,
  dataAttrs: Record<string, string | number>,
): string {
  const attrs = Object.entries(dataAttrs)
    .map(([key, value]) => `data-${key}="${value}"`)
    .join(" ");
  return `<button
          type="button"
          class="${className}"
          ${attrs}
          aria-label="${esc(tooltip)}"
          data-tooltip="${esc(tooltip)}"
          ${disabled ? "disabled" : ""}
        >
          <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
            <path fill="currentColor" d="M5 1.5h7.5A1.5 1.5 0 0 1 14 3v7.5a1.5 1.5 0 0 1-1.5 1.5H12V5a1 1 0 0 0-1-1H5v-.5A2 2 0 0 1 5 1.5ZM3.5 5H10a1 1 0 0 1 1 1v7.5a1 1 0 0 1-1 1H3.5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z"/>
          </svg>
        </button>`;
}

function renderEditPaycheckMonthButton(
  person: "anton" | "olha",
  monthIndex: number,
): string {
  const tooltip = `Edit ${MONTH_LABELS[monthIndex]} paychecks`;
  return `<button
          type="button"
          class="sfe-tax-copy-paycheck-btn sfe-tax-edit-paycheck-month"
          data-person="${person}"
          data-month="${monthIndex}"
          aria-label="${esc(tooltip)}"
          data-tooltip="${esc(tooltip)}"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="11" height="11" aria-hidden="true">
            <path fill="currentColor" d="M11.013 1.427a1.75 1.75 0 0 1 2.474 0l1.086 1.086a1.75 1.75 0 0 1 0 2.474l-8.986 8.986a1 1 0 0 1-.446.261l-3.25 1.083a.25.25 0 0 1-.316-.316l1.083-3.25a1 1 0 0 1 .261-.446l8.986-8.986z"/>
          </svg>
        </button>`;
}

function renderNoIncomeCheckbox(
  checked: boolean,
  disabled: boolean,
  inputClass: "sfe-tax-no-income-input" | "sfe-tax-paycheck-no-income-input",
  dataAttrs: Record<string, string | number>,
  extraClass = "",
  label = "",
): string {
  const attrs = Object.entries(dataAttrs)
    .map(([key, value]) => `data-${key}="${esc(String(value))}"`)
    .join(" ");
  const labelHtml = label
    ? `<span class="sfe-tax-no-income-check-label">${esc(label)}</span>`
    : "";
  return `
    <label class="sfe-tax-no-income-check ${extraClass}" title="Mark as having no W-2 income">
      <input
        type="checkbox"
        class="${inputClass} sfe-tax-no-income-check-input"
        ${attrs}
        ${checked ? "checked" : ""}
        ${disabled ? "disabled" : ""}
        aria-label="${label || "No income"}"
      />
      ${labelHtml}
    </label>`;
}

function renderPaycheckCell(
  person: "anton" | "olha",
  monthIndex: number,
  paycheckIndex: number,
  field: keyof TaxPaycheck,
  value: number | null,
  disabled: boolean,
): string {
  const label = field === "grossIncome" ? "Gross income" : "Tax withheld";
  const isEmpty = value == null;
  const displayValue = isEmpty ? "—" : `$${fmtMoney(value)}`;
  return `
    <td class="sfe-tax-paycheck-field sfe-tax-paycheck-editor-cell${disabled ? " sfe-tax-paycheck-field-disabled" : ""}">
      <div
        class="sfe-tax-paycheck-field-view${disabled ? "" : " sfe-tax-paycheck-field-clickable"}"
        ${disabled ? "" : 'role="button" tabindex="0"'}
        aria-label="${disabled ? label : `Edit ${label}`}"
      >
        <span class="kpi-v sfe-tax-paycheck-month-value sfe-tax-paycheck-cell-value${field === "totalTaxPaid" && !isEmpty ? " sfe-neg" : field === "grossIncome" && !isEmpty ? " roi-strong" : ""}${isEmpty ? " sfe-tax-paycheck-value-empty" : ""}">${displayValue}</span>
        <button
          type="button"
          class="sfe-tax-paycheck-edit-btn"
          aria-label="Edit ${MONTH_LABELS[monthIndex]} paycheck ${paycheckIndex + 1} ${label}"
          title="${disabled ? "No income" : "Edit"}"
          ${disabled ? "disabled" : ""}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="9" height="9" aria-hidden="true">
            <path fill="currentColor" d="M11.013 1.427a1.75 1.75 0 0 1 2.474 0l1.086 1.086a1.75 1.75 0 0 1 0 2.474l-8.986 8.986a1 1 0 0 1-.446.261l-3.25 1.083a.25.25 0 0 1-.316-.316l1.083-3.25a1 1 0 0 1 .261-.446l8.986-8.986z"/>
          </svg>
        </button>
      </div>
      ${
        disabled
          ? ""
          : `<div class="sfe-tax-paycheck-field-edit" hidden>
              <label class="sfe-tax-paycheck-input-wrap">
                <span class="sfe-tax-input-prefix">$</span>
                <input
                  type="text"
                  class="sfe-tax-paycheck-input"
                  inputmode="decimal"
                  data-person="${person}"
                  data-month="${monthIndex}"
                  data-paycheck="${paycheckIndex}"
                  data-field="${field}"
                  value="${esc(fmtMoney(value))}"
                  aria-label="${MONTH_LABELS[monthIndex]} paycheck ${paycheckIndex + 1} ${label}"
                  autocomplete="off"
                />
              </label>
            </div>`
      }
    </td>`;
}

function renderPersonW2TotalsCard(
  name: string,
  gross: number,
  tax: number,
  factForecast: { fact: PaycheckTotals; forecast: PaycheckTotals },
  extraClass = "",
): string {
  const grossCls = gross > 0 ? " roi-strong" : "";
  const taxCls = tax > 0 ? " sfe-neg" : "";
  return `
    <div class="kpi sfe-tax-w2-person-totals-card ${extraClass}">
      <div class="sfe-tax-w2-person-totals-head">
        <span class="kpi-k sfe-tax-w2-person-totals-name">${esc(name)}</span>
      </div>
      <div class="sfe-tax-w2-totals-hero">
        <div class="sfe-tax-w2-totals-hero-metric">
          <span class="sfe-tax-w2-totals-hero-label">Gross income</span>
          <span class="kpi-v sfe-tax-w2-totals-hero-value sfe-tax-kpi-gross${grossCls}">$${fmtMoney(gross)}</span>
        </div>
        <div class="sfe-tax-w2-totals-hero-metric sfe-tax-w2-totals-hero-metric-tax">
          <span class="sfe-tax-w2-totals-hero-label">Taxes</span>
          <span class="kpi-v sfe-tax-w2-totals-hero-value sfe-tax-kpi-tax${taxCls}">$${fmtMoney(tax)}</span>
        </div>
      </div>
      ${renderFactForecastTotalsCard(factForecast.fact, factForecast.forecast)}
    </div>`;
}

function renderW2TotalsCards(
  year: TaxYear,
  personNames: TaxSettings["w2PersonNames"],
  anton: PaycheckTotals,
  olha: PaycheckTotals,
  antonByStatus: { fact: PaycheckTotals; forecast: PaycheckTotals },
  olhaByStatus: { fact: PaycheckTotals; forecast: PaycheckTotals },
): string {
  const householdGross = anton.grossIncome + olha.grossIncome;
  const householdTaxPaid = anton.totalTaxPaid + olha.totalTaxPaid;
  const householdByStatus = {
    fact: addPaycheckTotals(antonByStatus.fact, olhaByStatus.fact),
    forecast: addPaycheckTotals(antonByStatus.forecast, olhaByStatus.forecast),
  };
  return `
    <aside class="kpi-row sfe-tax-w2-totals-cards">
      ${renderPersonW2TotalsCard(`Total ${personNames.anton}`, anton.grossIncome, anton.totalTaxPaid, antonByStatus)}
      ${renderPersonW2TotalsCard(`Total ${personNames.olha}`, olha.grossIncome, olha.totalTaxPaid, olhaByStatus)}
      ${renderPersonW2TotalsCard(
        "Total Household",
        householdGross,
        householdTaxPaid,
        householdByStatus,
        "sfe-total-income-kpi",
      )}
    </aside>`;
}

function renderPaycheckTotalCell(value: number, field: "grossIncome" | "totalTaxPaid"): string {
  const displayValue = `$${fmtMoney(value)}`;
  const colorCls =
    field === "totalTaxPaid" && value > 0
      ? " sfe-neg"
      : field === "grossIncome" && value > 0
        ? " roi-strong"
        : "";
  return `
    <td class="sfe-tax-paycheck-field sfe-tax-paycheck-editor-cell sfe-tax-paycheck-field-disabled">
      <div class="sfe-tax-paycheck-field-view">
        <span class="kpi-v sfe-tax-paycheck-month-value sfe-tax-paycheck-cell-value${colorCls}">${displayValue}</span>
      </div>
    </td>`;
}

function renderPaycheckEditorTable(
  person: "anton" | "olha",
  monthIndex: number,
  paychecks: TaxPaycheckMonth,
  noIncome: boolean,
): string {
  const totals = paycheckMonthTotals(paychecks, noIncome);
  const stubRows = paychecks
    .map((paycheck, paycheckIndex) => {
      const copyTip = paycheckCopyTooltip(monthIndex, paycheckIndex);
      const paycheckNoIncome = paycheck.noIncome === true;
      const disabled = noIncome || paycheckNoIncome;
      return `
        <tr class="sfe-tax-paycheck-editor-stub-row${paycheckNoIncome ? " sfe-tax-paycheck-editor-stub-row-none" : ""}">
          <th scope="row" class="sfe-tax-paycheck-editor-stub-label">
            <span class="sfe-tax-paycheck-editor-stub-title">
              <span>Paycheck ${paycheckIndex + 1}</span>
              ${renderCopyPaycheckButton(
                "sfe-tax-copy-paycheck-btn sfe-tax-copy-paycheck-stub",
                copyTip.label,
                disabled || copyTip.disabled,
                { person, month: monthIndex, paycheck: paycheckIndex },
              )}
            </span>
          </th>
          ${renderPaycheckCell(person, monthIndex, paycheckIndex, "grossIncome", paycheck.grossIncome, disabled)}
          ${renderPaycheckCell(person, monthIndex, paycheckIndex, "totalTaxPaid", paycheck.totalTaxPaid, disabled)}
          <td class="sfe-tax-paycheck-editor-none-cell">
            ${renderNoIncomeCheckbox(
              paycheckNoIncome,
              noIncome,
              "sfe-tax-paycheck-no-income-input",
              { person, month: monthIndex, paycheck: paycheckIndex },
              "",
              "None",
            )}
          </td>
        </tr>`;
    })
    .join("");

  return `
    <table class="sfe-tax-paycheck-table sfe-tax-paycheck-editor-table">
      <colgroup>
        <col class="sfe-tax-paycheck-col-month" />
        <col class="sfe-tax-paycheck-col-money" />
        <col class="sfe-tax-paycheck-col-money" />
        <col class="sfe-tax-paycheck-col-actions" />
      </colgroup>
      <tbody>
        ${stubRows}
        <tr class="sfe-tax-paycheck-editor-stub-row sfe-tax-paycheck-editor-total-row">
          <th scope="row" class="sfe-tax-paycheck-editor-stub-label sfe-tax-paycheck-editor-total-label">Total</th>
          ${renderPaycheckTotalCell(totals.grossIncome, "grossIncome")}
          ${renderPaycheckTotalCell(totals.totalTaxPaid, "totalTaxPaid")}
          <td class="sfe-tax-paycheck-editor-none-cell" aria-hidden="true"></td>
        </tr>
      </tbody>
    </table>`;
}

function renderPaycheckEditorPanel(
  person: "anton" | "olha",
  monthIndex: number,
  paychecks: TaxPaycheckMonth,
  noIncome: boolean,
  status: "fact" | "forecast",
): string {
  if (noIncome) {
    return `
      <div class="sfe-tax-paycheck-editor-panel sfe-tax-paycheck-editor-panel-none">
        <p class="muted small sfe-tax-paycheck-no-income-msg">Uncheck <strong>No income?</strong> in the row above to enter pay stubs.</p>
      </div>`;
  }

  return `
    <div class="sfe-tax-paycheck-editor-panel sfe-tax-paycheck-editor-panel-${status}">
      ${renderPaycheckEditorTable(person, monthIndex, paychecks, noIncome)}
    </div>`;
}

function renderTaxSectionSummaryHead(titleHtml: string): string {
  return `<span class="sfe-tax-section-summary-head">
      <span class="sfe-tax-section-summary-chevron" aria-hidden="true">▾</span>
      ${titleHtml}
    </span>`;
}

function renderTaxSectionSummary(
  title: string,
  year: TaxYear,
  subtitle?: string,
  titleExtraHtml?: string,
): string {
  const subtitleBlock = subtitle
    ? `<span class="sfe-tax-section-summary-totals">${subtitle}</span>`
    : "";
  return `<summary class="sfe-tax-section-h">
      ${renderTaxSectionSummaryHead(`<span class="insight-h sfe-tax-section-summary-title">
        <span class="sfe-tax-section-summary-title-row">
          <span class="sfe-tax-section-summary-title-text">${title}</span>
          ${renderCurrentYearMark(year)}
          ${titleExtraHtml ?? ""}
        </span>
      </span>`)}
      ${subtitleBlock}
    </summary>`;
}

function renderTaxSectionHeaderText(title: string, year: TaxYear, subtitle?: string): string {
  const subtitleBlock = subtitle
    ? `<span class="sfe-tax-filing-status">${subtitle}</span>`
    : "";
  return `
          <div class="sfe-tax-section-header-text">
            <span class="sfe-tax-section-header-title-row">
              <span class="insight-h">${title}</span>
              ${renderCurrentYearMark(year)}
            </span>
            ${subtitleBlock}
          </div>`;
}

function isCurrentTaxYear(year: TaxYear): boolean {
  return year === new Date().getFullYear();
}

function renderCurrentYearMark(year: TaxYear): string {
  if (!isCurrentTaxYear(year)) return "";
  return `<span class="sfe-tax-current-year-mark">Current Year</span>`;
}

function isTaxMonthForecast(year: TaxYear, monthIndex: number): boolean {
  return isPaycheckMonthForecast(year, monthIndex);
}

function taxMonthStatus(
  year: TaxYear,
  monthIndex: number,
): "fact" | "fact-forecast" | "forecast" {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  if (year < currentYear || (year === currentYear && monthIndex < currentMonth)) {
    return "fact";
  }
  if (year > currentYear || monthIndex > currentMonth) {
    return "forecast";
  }
  return "fact-forecast";
}

function taxMonthStatusLabel(status: "fact" | "fact-forecast" | "forecast"): string {
  if (status === "fact-forecast") return "Fact-Forecast";
  if (status === "forecast") return "Forecast";
  return "Fact";
}

function taxQuarterStatus(
  year: TaxYear,
  quarterIndex: number,
): "fact" | "fact-forecast" | "forecast" {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentQuarter = Math.floor(now.getMonth() / 3);
  if (year < currentYear || (year === currentYear && quarterIndex < currentQuarter)) {
    return "fact";
  }
  if (year > currentYear || quarterIndex > currentQuarter) {
    return "forecast";
  }
  return "fact-forecast";
}

function tradingYearTotal(months: number[]): number {
  return months.reduce<number>((sum, amount) => sum + amount, 0);
}

function tradingYearTotalsByStatus(
  months: number[],
  year: TaxYear,
): { fact: number; forecast: number } {
  const totals = { fact: 0, forecast: 0 };
  months.forEach((amount, monthIndex) => {
    if (isTaxMonthForecast(year, monthIndex)) totals.forecast += amount;
    else totals.fact += amount;
  });
  return totals;
}

function tradingFifoYearTotal(fifoMonths: number[]): number {
  return fifoMonths.reduce<number>((sum, amount) => sum + amount, 0);
}

function tradingFifoTotalsByStatus(
  fifoMonths: number[],
  year: TaxYear,
): { fact: number; forecast: number } {
  const totals = { fact: 0, forecast: 0 };
  fifoMonths.forEach((amount, monthIndex) => {
    if (isTaxMonthForecast(year, monthIndex)) totals.forecast += amount;
    else totals.fact += amount;
  });
  return totals;
}

function tradingTotalsValueClass(
  value: number,
  yearStatus: "Fact" | "Forecast" | "Fact+Forecast",
): string {
  if (value < 0) return " sfe-neg";
  if (value > 0) {
    return yearStatus === "Forecast" ? " sfe-tax-forecast-income" : " roi-strong";
  }
  return "";
}

function taxYearTotalStatus(year: TaxYear): "Fact" | "Forecast" | "Fact+Forecast" {
  const nowYear = new Date().getFullYear();
  if (year < nowYear) return "Fact";
  if (year > nowYear) return "Forecast";
  return "Fact+Forecast";
}

function taxQuarterTotalStatus(
  year: TaxYear,
  quarterIndex: number,
): "Fact" | "Forecast" | "Fact+Forecast" {
  const nowYear = new Date().getFullYear();
  if (year < nowYear) return "Fact";
  if (year > nowYear) return "Forecast";
  const startMonth = quarterIndex * 3;
  let hasFact = false;
  let hasForecast = false;
  for (let monthIndex = startMonth; monthIndex < startMonth + 3; monthIndex += 1) {
    if (isTaxMonthForecast(year, monthIndex)) hasForecast = true;
    else hasFact = true;
  }
  if (hasFact && hasForecast) return "Fact+Forecast";
  if (hasForecast) return "Forecast";
  return "Fact";
}

function renderTaxPeriodStatusBadge(
  status: "Fact" | "Forecast" | "Fact+Forecast",
): string {
  if (status === "Fact") {
    return `<span class="sfe-tax-month-status sfe-tax-estimate-quarter-group-status sfe-tax-month-status-fact">Fact</span>`;
  }
  if (status === "Forecast") {
    return `<span class="sfe-tax-month-status sfe-tax-estimate-quarter-group-status sfe-tax-month-status-forecast">Forecast</span>`;
  }
  return `
    <span class="sfe-tax-year-total-status-chips sfe-tax-estimate-quarter-group-status-chips">
      <span class="sfe-tax-month-status sfe-tax-month-status-fact">Fact</span>
      <span class="sfe-tax-year-total-status-plus" aria-hidden="true">+</span>
      <span class="sfe-tax-month-status sfe-tax-month-status-forecast">Forecast</span>
    </span>`;
}

function renderTaxYearTotalStatusMarks(year: TaxYear): string {
  const status = taxYearTotalStatus(year);
  if (status === "Fact") {
    return `<span class="sfe-tax-month-status sfe-tax-month-status-fact">Fact</span>`;
  }
  if (status === "Forecast") {
    return `<span class="sfe-tax-month-status sfe-tax-month-status-forecast">Forecast</span>`;
  }
  return `
    <span class="sfe-tax-year-total-status-chips">
      <span class="sfe-tax-month-status sfe-tax-month-status-fact">Fact</span>
      <span class="sfe-tax-year-total-status-plus" aria-hidden="true">+</span>
      <span class="sfe-tax-month-status sfe-tax-month-status-forecast">Forecast</span>
    </span>`;
}

function renderTradingSummaryColgroup(): string {
  return `
          <colgroup>
            <col class="sfe-tax-paycheck-col-lead" />
            <col class="sfe-tax-paycheck-col-month-name" />
            <col class="sfe-tax-paycheck-col-money" />
            <col class="sfe-tax-paycheck-col-money" />
          </colgroup>`;
}

function renderTradingSummaryHead(): string {
  return `
            <tr>
              <th scope="col" class="sfe-tax-paycheck-col-head sfe-tax-paycheck-col-head-lead" aria-hidden="true"></th>
              <th scope="col" class="sfe-tax-paycheck-col-head sfe-tax-paycheck-col-head-month">Month</th>
              <th scope="col" class="sfe-tax-paycheck-col-head sfe-tax-paycheck-col-head-money sfe-tax-paycheck-col-head-with-hint">${renderTaxColHead("Income", TIP_TRADING_INCOME)}</th>
              <th scope="col" class="sfe-tax-paycheck-col-head sfe-tax-paycheck-col-head-money sfe-tax-paycheck-col-head-with-hint">${renderTaxColHead("FIFO", TIP_TRADING_FIFO)}</th>
            </tr>`;
}

function renderPaycheckSummaryColgroup(): string {
  return `
          <colgroup>
            <col class="sfe-tax-paycheck-col-lead" />
            <col class="sfe-tax-paycheck-col-month-name" />
            <col class="sfe-tax-paycheck-col-copy" />
            <col class="sfe-tax-paycheck-col-money" />
            <col class="sfe-tax-paycheck-col-money" />
            <col class="sfe-tax-paycheck-col-actions" />
          </colgroup>`;
}

function renderPaycheckSummaryHead(
  moneyLabel: string,
  useMonthEditor = false,
): string {
  return `
            <tr>
              <th scope="col" class="sfe-tax-paycheck-col-head sfe-tax-paycheck-col-head-lead" aria-hidden="true"></th>
              <th scope="col" class="sfe-tax-paycheck-col-head sfe-tax-paycheck-col-head-month">Month</th>
              <th scope="col" class="sfe-tax-paycheck-col-head sfe-tax-paycheck-col-head-copy">${useMonthEditor ? "Edit" : "Copy"}</th>
              <th scope="col" class="sfe-tax-paycheck-col-head sfe-tax-paycheck-col-head-money">${moneyLabel}</th>
              <th scope="col" class="sfe-tax-paycheck-col-head sfe-tax-paycheck-col-head-money sfe-tax-paycheck-col-head-tax">Tax paid</th>
              <th scope="col" class="sfe-tax-paycheck-col-head sfe-tax-paycheck-col-head-none">No income?</th>
            </tr>`;
}

function renderTradingMonthCells(
  month: string,
  status: "fact" | "fact-forecast" | "forecast",
  statusLabel: string,
): string {
  return `
                  <td class="sfe-tax-paycheck-summary-cell sfe-tax-paycheck-summary-cell-lead">
                    <div class="sfe-tax-trading-row-label">
                      <span class="sfe-tax-paycheck-row-lead">
                        <span class="sfe-tax-month-status sfe-tax-month-status-${status}">${statusLabel}</span>
                      </span>
                    </div>
                  </td>
                  <td class="sfe-tax-paycheck-summary-cell sfe-tax-paycheck-summary-cell-month-name">
                    <div class="sfe-tax-trading-row-label">
                      <span class="sfe-tax-paycheck-month-name">${month}</span>
                    </div>
                  </td>`;
}

function renderTradingIncomeCell(year: TaxYear, monthIndex: number, value: number): string {
  const displayValue = `$${fmtMoney(value)}`;
  const forecast = isTaxMonthForecast(year, monthIndex);
  const colorCls = value > 0 ? (forecast ? " sfe-tax-forecast-income" : " roi-strong") : "";
  return `
    <td class="kpi-v sfe-tax-paycheck-month-value sfe-tax-paycheck-summary-cell sfe-tax-paycheck-summary-cell-money sfe-tax-trading-income-cell${colorCls}${value === 0 ? " sfe-tax-paycheck-value-empty" : ""}">${displayValue}</td>`;
}

function renderTradingFifoCell(year: TaxYear, monthIndex: number, value: number): string {
  const forecast = isTaxMonthForecast(year, monthIndex);
  const displayValue = `$${fmtMoney(value)}`;
  const colorCls =
    value < 0
      ? " sfe-neg"
      : value > 0
        ? forecast
          ? " sfe-tax-forecast-income"
          : " roi-strong"
        : "";
  return `
    <td class="kpi-v sfe-tax-paycheck-month-value sfe-tax-paycheck-summary-cell sfe-tax-paycheck-summary-cell-money sfe-tax-trading-fifo-cell${colorCls}${value === 0 ? " sfe-tax-paycheck-value-empty" : ""}">${displayValue}</td>`;
}

function renderTradingIncomeTable(
  year: TaxYear,
  months: number[],
  fifoMonths: number[],
  loading: boolean,
): string {
  const bodyRows = loading
    ? `<tr class="sfe-tax-trading-loading-row"><td colspan="4" class="muted small sfe-tax-trading-loading-cell">Loading from own ledger…</td></tr>`
    : MONTH_LABELS.map((month, monthIndex) => {
        const monthStatus = taxMonthStatus(year, monthIndex);
        const statusLabel = taxMonthStatusLabel(monthStatus);
        const amount = months[monthIndex] ?? 0;
        const fifoAmount = fifoMonths[monthIndex] ?? 0;
        return `
                <tr class="sfe-tax-paycheck-summary-row sfe-tax-trading-summary-row sfe-tax-paycheck-summary-row-${monthStatus}">
                  ${renderTradingMonthCells(month, monthStatus, statusLabel)}
                  ${renderTradingIncomeCell(year, monthIndex, amount)}
                  ${renderTradingFifoCell(year, monthIndex, fifoAmount)}
                </tr>`;
      }).join("");
  return `
      <section class="sfe-tax-paycheck-table-card sfe-tax-trading-table-card">
        <header class="sfe-tax-paycheck-table-head">
          ${renderTaxSectionHeaderText(
            "Trading Income",
            year,
            "Income from own ledger · FIFO replayed from SF+ ledger",
          )}
        </header>
        <div class="sfe-tax-paycheck-table-wrap">
        <table class="sfe-tax-paycheck-table sfe-tax-trading-table">
          ${renderTradingSummaryColgroup()}
          <thead>
            ${renderTradingSummaryHead()}
          </thead>
          <tbody>
            ${bodyRows}
          </tbody>
        </table>
      </div>
      </section>`;
}

function renderFactForecastAmountRow(
  label: "Fact" | "Forecast",
  amount: number,
  allowNegative = false,
): string {
  const isForecast = label === "Forecast";
  const amountCls =
    amount < 0 && allowNegative
      ? " sfe-neg"
      : amount > 0
        ? isForecast
          ? " sfe-tax-forecast-income"
          : " roi-strong"
        : "";
  return `
    <div class="sfe-tax-w2-totals-breakdown-row sfe-tax-w2-totals-breakdown-row-${isForecast ? "forecast" : "fact"}">
      <span class="sfe-tax-month-status sfe-tax-month-status-${isForecast ? "forecast" : "fact"}">${label}</span>
      <span class="sfe-tax-w2-totals-breakdown-gross${amountCls}">$${fmtMoney(amount)}</span>
    </div>`;
}

function renderFactForecastAmountCard(fact: number, forecast: number, allowNegative = false): string {
  return `
    <div class="sfe-tax-w2-totals-breakdown sfe-tax-other-income-ff-breakdown">
      ${renderFactForecastAmountRow("Fact", fact, allowNegative)}
      ${renderFactForecastAmountRow("Forecast", forecast, allowNegative)}
    </div>`;
}

function shouldShowFactForecastSplit(
  year: TaxYear,
  byStatus: { fact: number; forecast: number },
): boolean {
  return byStatus.forecast !== 0 || taxYearTotalStatus(year) !== "Fact";
}

function renderTradingIncomeTotalsCard(
  year: TaxYear,
  total: number,
  byStatus: { fact: number; forecast: number },
): string {
  const yearStatus = taxYearTotalStatus(year);
  const grossCls = tradingTotalsValueClass(total, yearStatus);
  return `
      <div class="kpi sfe-tax-w2-person-totals-card sfe-tax-trading-totals-card">
        <span class="kpi-k sfe-tax-w2-person-totals-name">Total Trading Income${renderTaxHint(TIP_TRADING_INCOME_TOTAL)}</span>
        <div class="kpi sfe-tax-w2-totals-subcard sfe-tax-trading-totals-total-subcard">
          <div class="sfe-tax-trading-totals-total-head">
            <span class="kpi-k">Total</span>
            ${renderTaxYearTotalStatusMarks(year)}
          </div>
          <span class="kpi-v sfe-tax-trading-totals-total-value sfe-tax-kpi-gross${grossCls}">$${fmtMoney(total)}</span>
        </div>
        ${renderFactForecastAmountCard(byStatus.fact, byStatus.forecast)}
      </div>`;
}

function renderTradingFifoTotalsCard(
  year: TaxYear,
  total: number,
  byStatus: { fact: number; forecast: number },
): string {
  const yearStatus = taxYearTotalStatus(year);
  const grossCls = tradingTotalsValueClass(total, yearStatus);
  return `
      <div class="kpi sfe-tax-w2-person-totals-card sfe-tax-trading-totals-card sfe-tax-trading-fifo-totals-card">
        <span class="kpi-k sfe-tax-w2-person-totals-name">Total FIFO Income${renderTaxHint(TIP_TRADING_FIFO_TOTAL)}</span>
        <div class="kpi sfe-tax-w2-totals-subcard sfe-tax-trading-totals-total-subcard">
          <div class="sfe-tax-trading-totals-total-head">
            <span class="kpi-k">Total</span>
            ${renderTaxYearTotalStatusMarks(year)}
          </div>
          <span class="kpi-v sfe-tax-trading-totals-total-value sfe-tax-kpi-gross${grossCls}">$${fmtMoney(total)}</span>
        </div>
        ${renderFactForecastAmountCard(byStatus.fact, byStatus.forecast, true)}
      </div>`;
}

function renderOtherIncomeTaxTotalsCard(
  totals: { amount: number; taxContributed: number },
  byStatus: {
    fact: { amount: number; taxContributed: number };
    forecast: { amount: number; taxContributed: number };
  },
): string {
  const grossCls = totals.amount > 0 ? " roi-strong" : "";
  const taxCls = totals.taxContributed > 0 ? " sfe-neg" : "";
  return `
      <div class="kpi sfe-tax-w2-person-totals-card sfe-tax-other-income-w2-card">
        <div class="sfe-tax-w2-person-totals-head">
          <span class="kpi-k sfe-tax-w2-person-totals-name">Other income</span>
        </div>
        <div class="sfe-tax-w2-totals-hero">
          <div class="sfe-tax-w2-totals-hero-metric">
            <span class="sfe-tax-w2-totals-hero-label">Other income${renderTaxHint(TIP_OTHER_INCOME)}</span>
            <span class="kpi-v sfe-tax-w2-totals-hero-value sfe-tax-kpi-gross${grossCls}">$${fmtMoney(totals.amount)}</span>
          </div>
          <div class="sfe-tax-w2-totals-hero-metric sfe-tax-w2-totals-hero-metric-tax">
            <span class="sfe-tax-w2-totals-hero-label">Taxes${renderTaxHint(TIP_OTHER_TAX)}</span>
            <span class="kpi-v sfe-tax-w2-totals-hero-value sfe-tax-kpi-tax${taxCls}">$${fmtMoney(totals.taxContributed)}</span>
          </div>
        </div>
        ${renderFactForecastTotalsCard(
          { grossIncome: byStatus.fact.amount, totalTaxPaid: byStatus.fact.taxContributed },
          { grossIncome: byStatus.forecast.amount, totalTaxPaid: byStatus.forecast.taxContributed },
          { gross: "Income", tax: "Tax" },
        )}
      </div>`;
}

function renderOtherIncomeSectionTotals(
  year: TaxYear,
  incomeTotal: number,
  incomeByStatus: { fact: number; forecast: number },
  fifoMonths: number[],
  otherItems: TaxOtherIncomeItem[],
): string {
  const fifoTotal = tradingFifoYearTotal(fifoMonths);
  const fifoByStatus = tradingFifoTotalsByStatus(fifoMonths, year);
  const otherTotals = otherIncomeYearTotals(otherItems);
  const otherByStatus = otherIncomeYearTotalsByStatus(otherItems, year);
  const combined = incomeTotal + otherTotals.amount;
  const combinedByStatus = {
    fact: incomeByStatus.fact + otherByStatus.fact.amount,
    forecast: incomeByStatus.forecast + otherByStatus.forecast.amount,
  };
  const yearStatus = taxYearTotalStatus(year);
  const combinedCls = combined > 0 ? (yearStatus === "Forecast" ? " sfe-tax-forecast-income" : " roi-strong") : "";
  const showCombinedSplit = shouldShowFactForecastSplit(year, combinedByStatus);
  const combinedSplit = showCombinedSplit
    ? renderFactForecastAmountCard(combinedByStatus.fact, combinedByStatus.forecast)
    : "";
  return `
    <aside class="sfe-tax-other-income-totals">
      ${renderTradingIncomeTotalsCard(year, incomeTotal, incomeByStatus)}
      ${renderOtherIncomeTaxTotalsCard(otherTotals, otherByStatus)}
      ${renderTradingFifoTotalsCard(year, fifoTotal, fifoByStatus)}
      <div class="kpi sfe-tax-w2-person-totals-card sfe-tax-other-income-hero sfe-total-income-kpi">
        <div class="sfe-tax-w2-person-totals-head">
          <span class="kpi-k sfe-tax-w2-person-totals-name">Combined Total income${renderTaxHint(TIP_OTHER_SECTION_TOTAL)}</span>
        </div>
        <div class="sfe-tax-w2-totals-hero sfe-tax-other-income-combined-hero">
          <div class="sfe-tax-w2-totals-hero-metric">
            <span class="sfe-tax-w2-totals-hero-label">Income</span>
            <span class="kpi-v sfe-tax-w2-totals-hero-value sfe-tax-kpi-gross${combinedCls}">$${fmtMoney(combined)}</span>
            <span class="sfe-tax-other-income-hero-breakdown">Trading $${fmtMoney(incomeTotal)} · Other $${fmtMoney(otherTotals.amount)}</span>
          </div>
        </div>
        ${combinedSplit}
      </div>
    </aside>`;
}

function renderOtherIncomeList(
  names: TaxSettings["w2PersonNames"],
  year: TaxYear,
  items: TaxOtherIncomeItem[],
  editingId: string | null,
): string {
  const rows =
    items.length === 0
      ? `<tr class="sfe-tax-paycheck-summary-row"><td colspan="6" class="muted small sfe-tax-other-list-empty">No other income yet. Add a non-trading income record.</td></tr>`
      : items
          .map((item) => {
            const highlight = item.id === editingId ? " sfe-tax-other-list-row-editing" : "";
            const noteText = item.note.trim();
            const noteCell = noteText
              ? `<span class="sfe-tax-other-list-note" title="${esc(noteText)}">${esc(noteText)}</span>`
              : `<span class="sfe-tax-other-list-note sfe-tax-other-list-note-empty muted">—</span>`;
            const personName = item.person === "olha" ? names.olha : names.anton;
            const incomeCls = item.amount > 0 ? " roi-strong" : "";
            return `
              <tr class="sfe-tax-paycheck-summary-row sfe-tax-other-list-row${highlight}" data-other-income-id="${esc(item.id)}">
                <td class="sfe-tax-paycheck-summary-cell sfe-tax-other-list-date-cell">${esc(formatEstPaymentDisplayDate(item.date))}</td>
                <td class="sfe-tax-paycheck-summary-cell sfe-tax-other-list-who-cell">${esc(personName)}</td>
                <td class="kpi-v sfe-tax-paycheck-month-value sfe-tax-paycheck-summary-cell sfe-tax-other-list-amount sfe-tax-other-list-income${incomeCls}">$${fmtMoney(item.amount)}</td>
                <td class="kpi-v sfe-tax-paycheck-month-value sfe-tax-paycheck-summary-cell sfe-tax-other-list-amount">$${fmtMoney(item.taxContributed)}</td>
                <td class="sfe-tax-paycheck-summary-cell sfe-tax-other-list-note-cell">${noteCell}</td>
                <td class="sfe-tax-paycheck-summary-cell sfe-tax-other-list-actions">
                  <button type="button" class="sfe-btn sfe-tax-other-list-edit" data-other-income-edit="${esc(item.id)}">Edit</button>
                  <button type="button" class="sfe-btn sfe-btn-danger sfe-tax-other-list-delete" data-other-income-delete="${esc(item.id)}">Delete</button>
                </td>
              </tr>`;
          })
          .join("");

  return `
      <section class="sfe-tax-paycheck-table-card sfe-tax-other-list-card">
        <header class="sfe-tax-paycheck-table-head">
          ${renderTaxSectionHeaderText(
            "Other income &amp; tax",
            year,
            "Non-trading income dated separately · counted in tax due and tax paid",
          )}
          <button type="button" class="sfe-btn sfe-btn-primary sfe-tax-other-list-add" data-other-income-add>Add</button>
        </header>
        <div class="sfe-tax-paycheck-table-wrap sfe-tax-other-list-table-wrap">
          <table class="sfe-tax-paycheck-table sfe-tax-other-list-table">
            <colgroup>
              <col class="sfe-tax-other-list-col-date" />
              <col class="sfe-tax-other-list-col-who" />
              <col class="sfe-tax-other-list-col-income" />
              <col class="sfe-tax-other-list-col-tax" />
              <col class="sfe-tax-other-list-col-note" />
              <col class="sfe-tax-other-list-col-actions" />
            </colgroup>
            <thead>
              <tr>
                <th scope="col" class="sfe-tax-paycheck-col-head sfe-tax-paycheck-col-head-month sfe-tax-other-list-col-head-date">Date</th>
                <th scope="col" class="sfe-tax-paycheck-col-head sfe-tax-paycheck-col-head-month">Who</th>
                <th scope="col" class="sfe-tax-paycheck-col-head sfe-tax-paycheck-col-head-money">Income</th>
                <th scope="col" class="sfe-tax-paycheck-col-head sfe-tax-paycheck-col-head-money">Tax</th>
                <th scope="col" class="sfe-tax-paycheck-col-head sfe-tax-other-list-col-head-note">Note</th>
                <th scope="col" class="sfe-tax-paycheck-col-head sfe-tax-other-list-col-head-actions">Actions</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
      </section>`;
}

function renderOtherIncomeModal(
  names: TaxSettings["w2PersonNames"],
  year: TaxYear,
  items: TaxOtherIncomeItem[],
  editingId: string | null,
  draft: { date: string; amount: string; tax: string; person: TaxPersonId; note: string },
): string {
  const editing = editingId ? items.find((item) => item.id === editingId) : null;
  const formDate = editing?.date ?? (draft.date || defaultOtherIncomeDate(year));
  const formAmount = editing ? fmtMoney(editing.amount) : draft.amount;
  const formTax = editing ? fmtMoney(editing.taxContributed) : draft.tax;
  const formPerson: TaxPersonId = editing?.person ?? draft.person;
  const formNote = editing?.note ?? draft.note;
  const title = editing ? "Edit other income" : "Add other income";
  const submitLabel = editing ? "Save" : "Add income";
  return `
    <div class="sfe-modal-overlay sfe-modal-open sfe-tax-other-income-overlay" id="sfe-tax-other-income-modal" ${CUSTOM_PANEL_MARKER_ATTR}="true">
      <div class="sfe-modal sfe-tax-other-income-modal" role="dialog" aria-modal="true" aria-labelledby="sfe-tax-other-income-title">
        <div class="sfe-modal-header">
          <h3 id="sfe-tax-other-income-title">${esc(title)} — ${year}</h3>
          <button type="button" class="sfe-modal-close sfe-tax-other-income-close" aria-label="Close">×</button>
        </div>
        <form class="sfe-tax-other-income-form">
          <label class="sfe-modal-field sfe-tax-other-income-field">
            Date
            <input type="date" class="sfe-tax-other-income-input sfe-tax-other-income-date" value="${esc(formDate)}" required />
          </label>
          <label class="sfe-modal-field sfe-tax-other-income-field">
            Who generated
            <select class="sfe-tax-other-income-input sfe-tax-other-income-person">
              <option value="anton"${formPerson === "anton" ? " selected" : ""}>${esc(names.anton)}</option>
              <option value="olha"${formPerson === "olha" ? " selected" : ""}>${esc(names.olha)}</option>
            </select>
          </label>
          <label class="sfe-modal-field sfe-tax-other-income-field">
            Income
            <span class="sfe-tax-other-list-amount-wrap">
              <span class="sfe-tax-other-list-amount-prefix">$</span>
              <input type="text" class="sfe-tax-other-income-input sfe-tax-other-income-amount" inputmode="decimal" value="${esc(formAmount)}" autocomplete="off" required />
            </span>
          </label>
          <label class="sfe-modal-field sfe-tax-other-income-field">
            Tax contributed
            <span class="sfe-tax-other-list-amount-wrap">
              <span class="sfe-tax-other-list-amount-prefix">$</span>
              <input type="text" class="sfe-tax-other-income-input sfe-tax-other-income-tax" inputmode="decimal" value="${esc(formTax)}" autocomplete="off" />
            </span>
          </label>
          <label class="sfe-modal-field sfe-tax-other-income-field sfe-tax-other-income-field-note">
            Note
            <input type="text" class="sfe-tax-other-income-input sfe-tax-other-income-note" value="${esc(formNote)}" placeholder="Optional memo" maxlength="200" autocomplete="off" />
          </label>
          <div class="sfe-modal-actions">
            <button type="button" class="sfe-btn sfe-tax-other-income-close">Cancel</button>
            <button type="submit" class="sfe-btn sfe-btn-primary">${esc(submitLabel)}</button>
          </div>
        </form>
      </div>
    </div>`;
}

function w2MonthGross(y: TaxYearInputs, monthIndex: number): number {
  const anton = paycheckMonthTotals(y.w2PaychecksAnton[monthIndex], y.w2NoIncomeAnton[monthIndex]);
  const olha = paycheckMonthTotals(y.w2PaychecksOlha[monthIndex], y.w2NoIncomeOlha[monthIndex]);
  return anton.grossIncome + olha.grossIncome;
}

function w2MonthTaxPaid(y: TaxYearInputs, monthIndex: number): number {
  const anton = paycheckMonthTotals(y.w2PaychecksAnton[monthIndex], y.w2NoIncomeAnton[monthIndex]);
  const olha = paycheckMonthTotals(y.w2PaychecksOlha[monthIndex], y.w2NoIncomeOlha[monthIndex]);
  return anton.totalTaxPaid + olha.totalTaxPaid;
}

function estimatedTaxPaidForMonth(y: TaxYearInputs, year: TaxYear, monthIndex: number): number {
  if (y.estimatedTaxPayments.length > 0) {
    // Payments are already scoped to this tax-year bucket; match by cash month + kind.
    // Do not require payment.taxYear === year here — a stale taxYear field would
    // otherwise zero-out Analytics Tax Due / Own Capital after a local save.
    const fromPayments = y.estimatedTaxPayments
      .filter(
        (payment) =>
          payment.kind === "estimated" &&
          paymentMonthIndex(payment.date, year) === monthIndex,
      )
      .reduce((sum, payment) => sum + payment.amount, 0);
    if (fromPayments !== 0) return fromPayments;
    const monthlyFromSync = y.estimatedTaxPaidMonths[monthIndex];
    if (monthlyFromSync != null) return monthlyFromSync;
    return 0;
  }
  const monthly = y.estimatedTaxPaidMonths[monthIndex];
  if (monthly != null) return monthly;
  const quarterIndex = Math.floor(monthIndex / 3);
  const quarterPaid = y.quarterlyPaidAfterW2[quarterIndex];
  if (quarterPaid != null) return quarterPaid / 3;
  return 0;
}

function computeTaxFromBrackets(taxableIncome: number, brackets: TaxBracket[]): number {
  if (taxableIncome <= 0) return 0;
  let tax = 0;
  for (const bracket of brackets) {
    const floor = bracket.from;
    const ceiling = bracket.to ?? Number.POSITIVE_INFINITY;
    if (taxableIncome < floor) break;
    const taxableInBracket = Math.min(taxableIncome, ceiling) - floor + 1;
    if (taxableInBracket > 0) {
      tax += taxableInBracket * (bracket.ratePct / 100);
    }
  }
  return tax;
}

function computeTaxOnW2PlusFifoIncome(
  settings: TaxSettings,
  year: TaxYear,
  y: TaxYearInputs,
  w2PlusFifoIncome: number,
  applyDeduction = false,
): number {
  const brackets = taxBracketsForYear(settings, year);
  const deduction = applyDeduction
    ? (y.standardDeduction ?? settings.defaultStandardDeduction)
    : 0;
  const taxableIncome = Math.max(0, w2PlusFifoIncome - deduction);
  return computeTaxFromBrackets(taxableIncome, brackets);
}

/** Marginal tax on new income, stacking brackets after prior YTD W-2 + FIFO. */
function computeIncrementalTaxOnW2PlusFifoIncome(
  settings: TaxSettings,
  year: TaxYear,
  y: TaxYearInputs,
  priorW2PlusFifoIncome: number,
  currentW2PlusFifoIncome: number,
  applyDeduction = false,
): number {
  if (currentW2PlusFifoIncome <= priorW2PlusFifoIncome) return 0;
  const taxBefore = computeTaxOnW2PlusFifoIncome(
    settings,
    year,
    y,
    priorW2PlusFifoIncome,
    applyDeduction,
  );
  const taxAfter = computeTaxOnW2PlusFifoIncome(
    settings,
    year,
    y,
    currentW2PlusFifoIncome,
    applyDeduction,
  );
  return Math.max(0, taxAfter - taxBefore);
}

interface TaxEstimateMonthRow {
  monthIndex: number;
  w2Income: number;
  tradingIncome: number;
  otherIncome: number;
  totalIncome: number;
  fifoTotal: number;
  cumulativeW2Income: number;
  cumulativeFifoTrading: number;
  cumulativeFifoTotal: number;
  monthlySafeHarborTarget: number;
  safeHarborTarget: number;
  monthlyTaxDue: number;
  totalTaxDue: number;
  w2TaxPaid: number;
  otherTaxPaid: number;
  estimatedTaxPaid: number;
  monthlyTotalTaxPaid: number;
  totalTaxPaid: number;
  cumulativeW2TaxPaid: number;
  cumulativeEstTaxPaid: number;
  safeHarborRemaining: number;
  safeHarborMet: boolean;
}

interface TaxEstimateQuarterInterest {
  irsInterestAprPct: number;
  accrualDays: number;
  irsInterest: number;
  irsOpportunity: number;
}

interface IrsOpportunityMonthRow {
  monthIndex: number;
  monthLabel: string;
  cumulativeUnpaid: number;
  priorUnpaidBalance: number;
  newUnpaidBalance: number;
  priorAccrualDays: number;
  newAccrualDays: number;
  priorOpportunity: number;
  newOpportunity: number;
  totalOpportunity: number;
}

interface IrsOpportunityQuarterSummary {
  quarterIndex: number;
  label: string;
  totalOpportunity: number;
}

interface IrsOpportunityBreakdown {
  year: TaxYear;
  scope: "quarter" | "annual";
  quarterIndex: number;
  title: string;
  monthlyRatePct: number;
  dueDate: string;
  priorDueDate: string;
  accrueEnd: string;
  priorUnpaidBalance: number;
  newUnpaidBalance: number;
  cumulativeUnpaid: number;
  totalOpportunity: number;
  months: IrsOpportunityMonthRow[];
  quarterSummaries: IrsOpportunityQuarterSummary[];
}

interface IrsInterestMonthRow {
  monthIndex: number;
  monthLabel: string;
  cumulativeShortfall: number;
  priorShortfallBalance: number;
  newShortfall: number;
  accrualDays: number;
  interest: number;
}

interface IrsInterestQuarterSummary {
  quarterIndex: number;
  label: string;
  totalInterest: number;
}

interface IrsInterestBreakdown {
  scope: "quarter" | "annual";
  quarterIndex: number;
  title: string;
  annualRatePct: number;
  dueDate: string;
  accrueEnd: string;
  priorShortfall: number;
  newShortfall: number;
  cumulativeShortfall: number;
  totalInterest: number;
  months: IrsInterestMonthRow[];
  quarterSummaries: IrsInterestQuarterSummary[];
}

interface SafeHarborMonthRow {
  monthIndex: number;
  monthLabel: string;
  cumulativeTarget: number;
  cumulativePaid: number;
  cumulativeRemaining: number;
  cumulativeUnderpaid: number;
}

interface SafeHarborBreakdown {
  year: TaxYear;
  scope: "quarter" | "annual";
  quarterIndex: number;
  title: string;
  cumulativeTarget: number;
  cumulativePaid: number;
  cumulativeRemaining: number;
  cumulativeUnderpaid: number;
  months: SafeHarborMonthRow[];
}

const IRS_DETAIL_BREAKDOWN_ICON = `<span class="sfe-tax-irs-detail-icon" aria-hidden="true" title="View breakdown">
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="11" height="11">
    <path fill="currentColor" d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5"/>
  </svg>
</span>`;

function quarterDueDate(year: TaxYear, quarterIndex: number): Date {
  if (quarterIndex === 3) return new Date(year + 1, 0, 15);
  const dueMonth = [3, 5, 8][quarterIndex];
  return new Date(year, dueMonth, 15);
}

/** Last day of the Form 2210 installment period (day before the next due date). */
function installmentPeriodEnd(year: TaxYear, quarterIndex: number): Date {
  if (quarterIndex < 3) {
    const nextDue = quarterDueDate(year, quarterIndex + 1);
    const end = new Date(nextDue);
    end.setDate(end.getDate() - 1);
    end.setHours(0, 0, 0, 0);
    return end;
  }
  const end = new Date(year + 1, 3, 14);
  end.setHours(0, 0, 0, 0);
  return end;
}

function computeQuarterInterestAccrualEnd(
  year: TaxYear,
  quarterIndex: number,
): Date {
  const lastMonthIndex = quarterIndex * 3 + 2;
  const installmentEnd = installmentPeriodEnd(year, quarterIndex);
  if (isTaxMonthForecast(year, lastMonthIndex)) {
    return installmentEnd;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (quarterIndex === 3) {
    return today.getTime() < installmentEnd.getTime() ? today : installmentEnd;
  }
  const quarterEnd = new Date(year, lastMonthIndex + 1, 0);
  quarterEnd.setHours(0, 0, 0, 0);
  const cap = quarterEnd.getTime() < installmentEnd.getTime() ? quarterEnd : installmentEnd;
  return today.getTime() < cap.getTime() ? today : cap;
}

function interestAccrualDays(fromDate: Date, toDate: Date): number {
  const from = new Date(fromDate);
  const to = new Date(toDate);
  from.setHours(0, 0, 0, 0);
  to.setHours(0, 0, 0, 0);
  return to > from ? Math.floor((to.getTime() - from.getTime()) / 86_400_000) : 0;
}

function monthCumulativeUnpaid(monthRow: TaxEstimateMonthRow): number {
  return Math.max(0, monthRow.totalTaxDue - monthRow.totalTaxPaid);
}

function monthSafeHarborShortfall(monthRow: TaxEstimateMonthRow): number {
  return Math.max(0, monthRow.safeHarborRemaining);
}

function irsBreakdownMonthRange(
  year: TaxYear,
  quarterIndex: number,
  fromYearStart: boolean,
): { startMonth: number; endMonth: number; accrueEnd: Date } {
  const accrueEnd = computeQuarterInterestAccrualEnd(year, quarterIndex);
  const quarterEndMonth = quarterIndex * 3 + 2;
  const endMonth =
    accrueEnd.getFullYear() > year ? 11 : Math.max(accrueEnd.getMonth(), quarterEndMonth);
  return {
    startMonth: fromYearStart ? 0 : quarterIndex * 3,
    endMonth,
    accrueEnd,
  };
}

/** Month-by-month IRS opportunity: balance at month start accrues this month; new unpaid accrues next month. */
function computeIrsOpportunityMonthRowsForQuarter(
  settings: TaxSettings,
  year: TaxYear,
  quarterIndex: number,
  monthRows: TaxEstimateMonthRow[],
  fromYearStart = false,
): IrsOpportunityMonthRow[] {
  const monthlyRatePct = settings.irsOpportunityMonthlyPct ?? 6;
  const { startMonth, endMonth, accrueEnd } = irsBreakdownMonthRange(
    year,
    quarterIndex,
    fromYearStart,
  );
  const opportunityEndMonth =
    quarterIndex === 3 && accrueEnd.getFullYear() > year ? 15 : endMonth;
  const attributedPayments = estimatedTaxPaymentsForYear(
    year,
    yearInputs(settings, year).estimatedTaxPayments,
  );

  const rows: IrsOpportunityMonthRow[] = [];
  for (let monthIndex = startMonth; monthIndex <= opportunityEndMonth; monthIndex += 1) {
    const isFollowingYear = monthIndex > 11;
    const priorRow = rows[rows.length - 1];
    const balanceAtStart = isFollowingYear
      ? (priorRow?.cumulativeUnpaid ?? monthCumulativeUnpaid(monthRows[11]))
      : monthIndex > 0
        ? monthCumulativeUnpaid(monthRows[monthIndex - 1])
        : 0;
    let balanceAtEnd = isFollowingYear
      ? balanceAtStart
      : monthCumulativeUnpaid(monthRows[monthIndex]);
    const newUnpaidThisMonth = Math.max(0, balanceAtEnd - balanceAtStart);
    const monthStart = new Date(year, monthIndex, 1);
    monthStart.setHours(0, 0, 0, 0);
    const accrualDays =
      balanceAtStart > 0 ? accrualDaysInMonth(monthStart, accrueEnd, year, monthIndex) : 0;
    let accruingOpportunity =
      accrualDays > 0
        ? balanceAtStart * (monthlyRatePct / 100) * (accrualDays / 30)
        : 0;
    if (isFollowingYear && balanceAtStart > 0 && accrualDays > 0) {
      const monthEnd = new Date(year, monthIndex + 1, 0);
      monthEnd.setHours(0, 0, 0, 0);
      const periodEnd =
        accrueEnd.getTime() < monthEnd.getTime() ? accrueEnd : monthEnd;
      const calendarMonth = monthIndex % 12;
      const paymentsThisMonth = attributedPayments
        .filter((payment) => {
          const parsed = parseIsoDate(payment.date);
          return (
            parsed?.year === year + 1 &&
            parsed.monthIndex === calendarMonth &&
            new Date(parsed.year, parsed.monthIndex, parsed.day).getTime() <=
              periodEnd.getTime()
          );
        })
        .sort((a, b) => a.date.localeCompare(b.date));
      let runningBalance = balanceAtStart;
      let cursor = monthStart;
      accruingOpportunity = 0;
      for (const payment of paymentsThisMonth) {
        const parsed = parseIsoDate(payment.date);
        if (!parsed) continue;
        const paymentDate = new Date(parsed.year, parsed.monthIndex, parsed.day);
        paymentDate.setHours(0, 0, 0, 0);
        const segmentDays = interestAccrualDays(cursor, paymentDate);
        accruingOpportunity +=
          runningBalance * (monthlyRatePct / 100) * (segmentDays / 30);
        runningBalance = Math.max(0, runningBalance - payment.amount);
        cursor = paymentDate;
      }
      const remainingDays = interestAccrualDays(cursor, periodEnd);
      accruingOpportunity +=
        runningBalance * (monthlyRatePct / 100) * (remainingDays / 30);
      balanceAtEnd = runningBalance;
    }

    rows.push({
      monthIndex,
      monthLabel: isFollowingYear
        ? `${MONTH_LABELS[monthIndex % 12]} ${year + 1}`
        : MONTH_LABELS[monthIndex],
      cumulativeUnpaid: balanceAtEnd,
      priorUnpaidBalance: balanceAtStart,
      newUnpaidBalance: newUnpaidThisMonth,
      priorAccrualDays: accrualDays,
      newAccrualDays: 0,
      priorOpportunity: accruingOpportunity,
      newOpportunity: 0,
      totalOpportunity: accruingOpportunity,
    });
  }
  return rows;
}

/** Month-by-month IRS interest: shortfall at month start accrues after the quarter due date. */
function computeIrsInterestMonthRowsForQuarter(
  settings: TaxSettings,
  year: TaxYear,
  quarterIndex: number,
  monthRows: TaxEstimateMonthRow[],
  fromYearStart = false,
): IrsInterestMonthRow[] {
  const y = yearInputs(settings, year);
  const annualRatePct = settings.irsInterestAprPct ?? y.irsInterestAprPct ?? 8;
  const dueDate = quarterDueDate(year, quarterIndex);
  dueDate.setHours(0, 0, 0, 0);
  const { startMonth, endMonth, accrueEnd } = irsBreakdownMonthRange(
    year,
    quarterIndex,
    fromYearStart,
  );

  const rows: IrsInterestMonthRow[] = [];
  for (let monthIndex = startMonth; monthIndex <= endMonth; monthIndex += 1) {
    const balanceAtStart =
      monthIndex > 0 ? monthSafeHarborShortfall(monthRows[monthIndex - 1]) : 0;
    const balanceAtEnd = monthSafeHarborShortfall(monthRows[monthIndex]);
    const newShortfall = Math.max(0, balanceAtEnd - balanceAtStart);
    const accrualDays =
      balanceAtStart > 0 ? accrualDaysInMonth(dueDate, accrueEnd, year, monthIndex) : 0;
    const interest =
      accrualDays > 0
        ? balanceAtStart * (annualRatePct / 100) * (accrualDays / 365)
        : 0;

    rows.push({
      monthIndex,
      monthLabel: MONTH_LABELS[monthIndex],
      cumulativeShortfall: balanceAtEnd,
      priorShortfallBalance: balanceAtStart,
      newShortfall,
      accrualDays,
      interest,
    });
  }
  return rows;
}

function computeSafeHarborMonthRows(
  year: TaxYear,
  quarterIndex: number,
  monthRows: TaxEstimateMonthRow[],
  fromYearStart: boolean,
): SafeHarborMonthRow[] {
  const { startMonth, endMonth } = irsBreakdownMonthRange(year, quarterIndex, fromYearStart);
  const rows: SafeHarborMonthRow[] = [];
  for (let monthIndex = startMonth; monthIndex <= endMonth; monthIndex += 1) {
    const monthRow = monthRows[monthIndex];
    const cumulativeTarget = monthRow.safeHarborTarget;
    const cumulativePaid = monthRow.totalTaxPaid;
    const cumulativeRemaining = monthRow.safeHarborRemaining;
    const cumulativeUnderpaid = Math.max(0, cumulativeRemaining);
    rows.push({
      monthIndex,
      monthLabel: MONTH_LABELS[monthIndex],
      cumulativeTarget,
      cumulativePaid,
      cumulativeRemaining,
      cumulativeUnderpaid,
    });
  }
  return rows;
}

function computeSafeHarborBreakdownForQuarter(
  year: TaxYear,
  quarterIndex: number,
  totals: TaxEstimateQuarterTotals,
  monthRows: TaxEstimateMonthRow[],
  fromYearStart = false,
): SafeHarborBreakdown {
  const months = computeSafeHarborMonthRows(year, quarterIndex, monthRows, fromYearStart);
  return {
    year,
    scope: "quarter",
    quarterIndex,
    title: QUARTER_LABELS[quarterIndex],
    cumulativeTarget: totals.safeHarborTarget,
    cumulativePaid: totals.totalTaxPaid,
    cumulativeRemaining: totals.safeHarborRemaining,
    cumulativeUnderpaid: Math.max(0, totals.safeHarborRemaining),
    months,
  };
}

function computeSafeHarborBreakdown(
  year: TaxYear,
  scope: "quarter" | "annual",
  quarterIndex: number,
  quarters: TaxEstimateQuarterTotals[],
  monthRows: TaxEstimateMonthRow[],
): SafeHarborBreakdown {
  if (scope === "quarter") {
    return computeSafeHarborBreakdownForQuarter(
      year,
      quarterIndex,
      quarters[quarterIndex],
      monthRows,
      true,
    );
  }
  const yearEnd = quarters[3];
  return {
    year,
    scope: "annual",
    quarterIndex: 3,
    title: "Annual totals",
    cumulativeTarget: yearEnd.safeHarborTarget,
    cumulativePaid: yearEnd.totalTaxPaid,
    cumulativeRemaining: yearEnd.safeHarborRemaining,
    cumulativeUnderpaid: Math.max(0, yearEnd.safeHarborRemaining),
    months: computeSafeHarborMonthRows(year, 3, monthRows, true),
  };
}

function computeQuarterInterest(
  settings: TaxSettings,
  year: TaxYear,
  quarterIndex: number,
  totals: TaxEstimateQuarterTotals,
  priorTotals: TaxEstimateQuarterTotals | null,
  monthRows: TaxEstimateMonthRow[],
): TaxEstimateQuarterInterest {
  const y = yearInputs(settings, year);
  const irsInterestAprPct = settings.irsInterestAprPct ?? y.irsInterestAprPct ?? 8;

  const dueDate = quarterDueDate(year, quarterIndex);
  const periodEnd = computeQuarterInterestAccrualEnd(year, quarterIndex);
  const installmentDays = interestAccrualDays(dueDate, periodEnd);

  const interestMonthRows = computeIrsInterestMonthRowsForQuarter(
    settings,
    year,
    quarterIndex,
    monthRows,
  );
  const irsInterest = interestMonthRows.reduce((sum, row) => sum + row.interest, 0);
  const accrualDays = installmentDays;
  const irsOpportunity = computeIrsOpportunityMonthRowsForQuarter(
    settings,
    year,
    quarterIndex,
    monthRows,
  ).reduce((sum, row) => sum + row.totalOpportunity, 0);

  return {
    irsInterestAprPct,
    accrualDays,
    irsInterest,
    irsOpportunity,
  };
}

function formatTaxIsoDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function accrualDaysInMonth(
  accrualStart: Date,
  accrueEnd: Date,
  year: TaxYear,
  monthIndex: number,
): number {
  const monthStart = new Date(year, monthIndex, 1);
  const monthEnd = new Date(year, monthIndex + 1, 0);
  monthStart.setHours(0, 0, 0, 0);
  monthEnd.setHours(0, 0, 0, 0);
  const fromMs = Math.max(accrualStart.getTime(), monthStart.getTime());
  const toMs = Math.min(accrueEnd.getTime(), monthEnd.getTime());
  if (toMs <= fromMs) return 0;
  return interestAccrualDays(new Date(fromMs), new Date(toMs));
}

function computeIrsOpportunityBreakdownForQuarter(
  settings: TaxSettings,
  year: TaxYear,
  quarterIndex: number,
  totals: TaxEstimateQuarterTotals,
  priorTotals: TaxEstimateQuarterTotals | null,
  monthRows: TaxEstimateMonthRow[],
  fromYearStart = false,
): IrsOpportunityBreakdown {
  const monthlyRatePct = settings.irsOpportunityMonthlyPct ?? 6;
  const dueDate = quarterDueDate(year, quarterIndex);
  const accrueEnd = computeQuarterInterestAccrualEnd(year, quarterIndex);
  const priorDueDate =
    quarterIndex > 0 ? quarterDueDate(year, quarterIndex - 1) : new Date(year, 0, 1);
  priorDueDate.setHours(0, 0, 0, 0);
  dueDate.setHours(0, 0, 0, 0);

  const cumulativeUnpaidTax = Math.max(0, totals.fifoTaxRemaining);
  const priorCumulativeUnpaidTax = priorTotals
    ? Math.max(0, priorTotals.fifoTaxRemaining)
    : 0;
  const newUnpaidTax = Math.max(0, cumulativeUnpaidTax - priorCumulativeUnpaidTax);

  const months = computeIrsOpportunityMonthRowsForQuarter(
    settings,
    year,
    quarterIndex,
    monthRows,
    fromYearStart,
  );
  const totalOpportunity = months.reduce((sum, row) => sum + row.totalOpportunity, 0);

  return {
    year,
    scope: "quarter",
    quarterIndex,
    title: QUARTER_LABELS[quarterIndex],
    monthlyRatePct,
    dueDate: formatTaxIsoDate(dueDate),
    priorDueDate: formatTaxIsoDate(priorDueDate),
    accrueEnd: formatTaxIsoDate(accrueEnd),
    priorUnpaidBalance: priorCumulativeUnpaidTax,
    newUnpaidBalance: newUnpaidTax,
    cumulativeUnpaid: cumulativeUnpaidTax,
    totalOpportunity,
    months,
    quarterSummaries: [
      { quarterIndex, label: QUARTER_LABELS[quarterIndex], totalOpportunity },
    ],
  };
}

function computeIrsOpportunityBreakdown(
  settings: TaxSettings,
  year: TaxYear,
  scope: "quarter" | "annual",
  quarterIndex: number,
  quarters: TaxEstimateQuarterTotals[],
  monthRows: TaxEstimateMonthRow[],
): IrsOpportunityBreakdown {
  if (scope === "quarter") {
    return computeIrsOpportunityBreakdownForQuarter(
      settings,
      year,
      quarterIndex,
      quarters[quarterIndex],
      quarterIndex > 0 ? quarters[quarterIndex - 1] : null,
      monthRows,
      true,
    );
  }

  const mergedMonths = new Map<number, IrsOpportunityMonthRow>();
  const quarterSummaries: IrsOpportunityQuarterSummary[] = [];
  let totalOpportunity = 0;
  for (let q = 0; q < 4; q += 1) {
    const quarterBreakdown = computeIrsOpportunityBreakdownForQuarter(
      settings,
      year,
      q,
      quarters[q],
      q > 0 ? quarters[q - 1] : null,
      monthRows,
    );
    quarterSummaries.push({
      quarterIndex: q,
      label: QUARTER_LABELS[q],
      totalOpportunity: quarterBreakdown.totalOpportunity,
    });
    totalOpportunity += quarterBreakdown.totalOpportunity;
    for (const row of quarterBreakdown.months) {
      const existing = mergedMonths.get(row.monthIndex);
      if (existing) {
        existing.priorAccrualDays += row.priorAccrualDays;
        existing.newAccrualDays += row.newAccrualDays;
        existing.priorOpportunity += row.priorOpportunity;
        existing.newOpportunity += row.newOpportunity;
        existing.totalOpportunity += row.totalOpportunity;
        existing.priorUnpaidBalance = Math.max(existing.priorUnpaidBalance, row.priorUnpaidBalance);
        existing.newUnpaidBalance = Math.max(existing.newUnpaidBalance, row.newUnpaidBalance);
      } else {
        mergedMonths.set(row.monthIndex, { ...row });
      }
    }
  }

  const lastMonthIndex = Math.max(11, ...mergedMonths.keys());
  const months: IrsOpportunityMonthRow[] = Array.from(
    { length: lastMonthIndex + 1 },
    (_, monthIndex) => {
    const monthLabel =
      monthIndex > 11
        ? `${MONTH_LABELS[monthIndex % 12]} ${year + 1}`
        : MONTH_LABELS[monthIndex];
    const merged = mergedMonths.get(monthIndex);
    const monthRow = monthRows[monthIndex];
    const cumulativeUnpaid = monthRow
      ? Math.max(0, monthRow.totalTaxDue - monthRow.totalTaxPaid)
      : (merged?.cumulativeUnpaid ?? 0);
    if (merged) {
      return { ...merged, cumulativeUnpaid };
    }
    return {
      monthIndex,
      monthLabel,
      cumulativeUnpaid,
      priorUnpaidBalance: 0,
      newUnpaidBalance: 0,
      priorAccrualDays: 0,
      newAccrualDays: 0,
      priorOpportunity: 0,
      newOpportunity: 0,
      totalOpportunity: 0,
    };
    },
  );

  const yearEnd = quarters[3];
  return {
    year,
    scope: "annual",
    quarterIndex: 3,
    title: "Annual totals",
    monthlyRatePct: settings.irsOpportunityMonthlyPct ?? 6,
    dueDate: "—",
    priorDueDate: "—",
    accrueEnd: formatTaxIsoDate(computeQuarterInterestAccrualEnd(year, 3)),
    priorUnpaidBalance: 0,
    newUnpaidBalance: 0,
    cumulativeUnpaid: Math.max(0, yearEnd.fifoTaxRemaining),
    totalOpportunity,
    months,
    quarterSummaries,
  };
}

function computeIrsInterestBreakdownForQuarter(
  settings: TaxSettings,
  year: TaxYear,
  quarterIndex: number,
  totals: TaxEstimateQuarterTotals,
  priorTotals: TaxEstimateQuarterTotals | null,
  monthRows: TaxEstimateMonthRow[],
  fromYearStart = false,
): IrsInterestBreakdown {
  const y = yearInputs(settings, year);
  const annualRatePct = settings.irsInterestAprPct ?? y.irsInterestAprPct ?? 8;
  const dueDate = quarterDueDate(year, quarterIndex);
  const accrueEnd = computeQuarterInterestAccrualEnd(year, quarterIndex);

  const cumulativeShortfall = Math.max(0, totals.safeHarborRemaining);
  const priorShortfall = priorTotals ? Math.max(0, priorTotals.safeHarborRemaining) : 0;
  const newShortfall = Math.max(0, cumulativeShortfall - priorShortfall);

  const months = computeIrsInterestMonthRowsForQuarter(
    settings,
    year,
    quarterIndex,
    monthRows,
    fromYearStart,
  );
  const totalInterest = months.reduce((sum, row) => sum + row.interest, 0);

  return {
    scope: "quarter",
    quarterIndex,
    title: QUARTER_LABELS[quarterIndex],
    annualRatePct,
    dueDate: formatTaxIsoDate(dueDate),
    accrueEnd: formatTaxIsoDate(accrueEnd),
    priorShortfall,
    newShortfall,
    cumulativeShortfall,
    totalInterest,
    months,
    quarterSummaries: [
      { quarterIndex, label: QUARTER_LABELS[quarterIndex], totalInterest },
    ],
  };
}

function computeIrsInterestBreakdown(
  settings: TaxSettings,
  year: TaxYear,
  scope: "quarter" | "annual",
  quarterIndex: number,
  quarters: TaxEstimateQuarterTotals[],
  monthRows: TaxEstimateMonthRow[],
): IrsInterestBreakdown {
  if (scope === "quarter") {
    return computeIrsInterestBreakdownForQuarter(
      settings,
      year,
      quarterIndex,
      quarters[quarterIndex],
      quarterIndex > 0 ? quarters[quarterIndex - 1] : null,
      monthRows,
      true,
    );
  }

  const mergedMonths = new Map<number, IrsInterestMonthRow>();
  const quarterSummaries: IrsInterestQuarterSummary[] = [];
  let totalInterest = 0;
  for (let q = 0; q < 4; q += 1) {
    const quarterBreakdown = computeIrsInterestBreakdownForQuarter(
      settings,
      year,
      q,
      quarters[q],
      q > 0 ? quarters[q - 1] : null,
      monthRows,
    );
    quarterSummaries.push({
      quarterIndex: q,
      label: QUARTER_LABELS[q],
      totalInterest: quarterBreakdown.totalInterest,
    });
    totalInterest += quarterBreakdown.totalInterest;
    for (const row of quarterBreakdown.months) {
      const existing = mergedMonths.get(row.monthIndex);
      if (existing) {
        existing.accrualDays += row.accrualDays;
        existing.interest += row.interest;
        existing.priorShortfallBalance = Math.max(
          existing.priorShortfallBalance,
          row.priorShortfallBalance,
        );
        existing.newShortfall = Math.max(existing.newShortfall, row.newShortfall);
      } else {
        mergedMonths.set(row.monthIndex, { ...row });
      }
    }
  }

  const months: IrsInterestMonthRow[] = MONTH_LABELS.map((monthLabel, monthIndex) => {
    const merged = mergedMonths.get(monthIndex);
    const monthRow = monthRows[monthIndex];
    const cumulativeShortfall = monthSafeHarborShortfall(monthRow);
    if (merged) {
      return { ...merged, cumulativeShortfall };
    }
    return {
      monthIndex,
      monthLabel,
      cumulativeShortfall,
      priorShortfallBalance: 0,
      newShortfall: 0,
      accrualDays: 0,
      interest: 0,
    };
  });

  const yearEnd = quarters[3];
  return {
    scope: "annual",
    quarterIndex: 3,
    title: "Annual totals",
    annualRatePct: settings.irsInterestAprPct ?? yearInputs(settings, year).irsInterestAprPct ?? 8,
    dueDate: "—",
    accrueEnd: formatTaxIsoDate(computeQuarterInterestAccrualEnd(year, 3)),
    priorShortfall: 0,
    newShortfall: 0,
    cumulativeShortfall: Math.max(0, yearEnd.safeHarborRemaining),
    totalInterest,
    months,
    quarterSummaries,
  };
}

interface TaxEstimateQuarterTotals {
  w2Income: number;
  tradingIncome: number;
  otherIncome: number;
  totalIncome: number;
  fifoTotal: number;
  cumulativeW2Income: number;
  cumulativeFifoTrading: number;
  cumulativeFifoTotal: number;
  fifoTaxDue: number;
  cumulativeW2TaxPaid: number;
  cumulativeEstTaxPaid: number;
  fifoTaxRemaining: number;
  safeHarborTarget: number;
  totalTaxDue: number;
  w2TaxPaid: number;
  otherTaxPaid: number;
  estimatedTaxPaid: number;
  totalTaxPaid: number;
  safeHarborRemaining: number;
  safeHarborMet: boolean;
  quarterSafeHarborTarget: number;
  quarterTaxDue: number;
  quarterTotalPaid: number;
  quarterSafeHarborRemaining: number;
  quarterTaxRemaining: number;
}

interface TaxEstimateAnnualInterest {
  irsInterest: number;
  irsOpportunity: number;
}

function computeTaxEstimateSchedule(
  settings: TaxSettings,
  year: TaxYear,
  y: TaxYearInputs,
  tradingIncomeMonths: number[],
  tradingFifoMonths: number[],
): { months: TaxEstimateMonthRow[]; quarters: TaxEstimateQuarterTotals[] } {
  const safeHarborAnnual = y.priorYearTotalTax ?? 0;

  let cumulativeFifoTotal = 0;
  let cumulativeW2Income = 0;
  let cumulativeFifoTrading = 0;
  let cumulativeOtherIncome = 0;
  let cumulativeW2TaxPaid = 0;
  let cumulativeOtherTaxPaid = 0;
  let cumulativeEstTaxPaid = 0;

  const months: TaxEstimateMonthRow[] = [];

  for (let monthIndex = 0; monthIndex < MONTH_LABELS.length; monthIndex += 1) {
    const w2Income = w2MonthGross(y, monthIndex);
    const tradingIncome = tradingIncomeMonths[monthIndex] ?? 0;
    const fifoIncome = tradingFifoMonths[monthIndex] ?? 0;
    const otherIncome = otherIncomeForMonth(y, year, monthIndex);
    const w2TaxPaid = w2MonthTaxPaid(y, monthIndex);
    const otherTaxPaid = otherTaxPaidForMonth(y, year, monthIndex);
    const estimatedTaxPaid = estimatedTaxPaidForMonth(y, year, monthIndex);

    cumulativeW2Income += w2Income;
    cumulativeFifoTrading += fifoIncome;
    cumulativeOtherIncome += otherIncome;
    cumulativeFifoTotal += w2Income + fifoIncome + otherIncome;
    cumulativeW2TaxPaid += w2TaxPaid;
    cumulativeOtherTaxPaid += otherTaxPaid;
    cumulativeEstTaxPaid += estimatedTaxPaid;

    const monthFraction = (monthIndex + 1) / 12;
    const safeHarborTarget = safeHarborAnnual * monthFraction;
    const w2PlusFifoIncome = cumulativeW2Income + cumulativeFifoTrading + cumulativeOtherIncome;
    const totalTaxDue = computeTaxOnW2PlusFifoIncome(
      settings,
      year,
      y,
      w2PlusFifoIncome,
      false,
    );
    const priorMonth = months[months.length - 1] ?? null;
    const monthlySafeHarborTarget = safeHarborTarget - (priorMonth?.safeHarborTarget ?? 0);
    const monthlyTaxDue = totalTaxDue - (priorMonth?.totalTaxDue ?? 0);
    const monthlyTotalTaxPaid = w2TaxPaid + otherTaxPaid + estimatedTaxPaid;
    const totalTaxPaid = cumulativeW2TaxPaid + cumulativeOtherTaxPaid + cumulativeEstTaxPaid;
    const safeHarborRemaining = safeHarborTarget - totalTaxPaid;
    const safeHarborMet = safeHarborAnnual > 0 && totalTaxPaid >= safeHarborTarget - 0.005;

    months.push({
      monthIndex,
      w2Income,
      tradingIncome,
      otherIncome,
      totalIncome: w2Income + tradingIncome + otherIncome,
      fifoTotal: w2Income + fifoIncome + otherIncome,
      cumulativeW2Income,
      cumulativeFifoTrading,
      cumulativeFifoTotal,
      monthlySafeHarborTarget,
      safeHarborTarget,
      monthlyTaxDue,
      totalTaxDue,
      w2TaxPaid,
      otherTaxPaid,
      estimatedTaxPaid,
      monthlyTotalTaxPaid,
      totalTaxPaid,
      cumulativeW2TaxPaid,
      cumulativeEstTaxPaid,
      safeHarborRemaining,
      safeHarborMet,
    });
  }

  const quarters = [0, 1, 2, 3].map((quarterIndex) => {
    const start = quarterIndex * 3;
    const quarterMonths = months.slice(start, start + 3);
    const last = quarterMonths[quarterMonths.length - 1];
    const priorLast = quarterIndex > 0 ? months[start - 1] : null;
    const w2PlusFifoYtd = last.cumulativeFifoTotal;
    const priorW2PlusFifo = priorLast != null ? priorLast.cumulativeFifoTotal : 0;
    const cumulativeTaxDue = computeTaxOnW2PlusFifoIncome(
      settings,
      year,
      y,
      w2PlusFifoYtd,
      false,
    );
    const quarterTaxDue = computeIncrementalTaxOnW2PlusFifoIncome(
      settings,
      year,
      y,
      priorW2PlusFifo,
      w2PlusFifoYtd,
      false,
    );
    const w2TaxPaid = quarterMonths.reduce((sum, row) => sum + row.w2TaxPaid, 0);
    const otherTaxPaid = quarterMonths.reduce((sum, row) => sum + row.otherTaxPaid, 0);
    const estimatedTaxPaid = quarterMonths.reduce((sum, row) => sum + row.estimatedTaxPaid, 0);
    const quarterSafeHarborTarget = last.safeHarborTarget - (priorLast?.safeHarborTarget ?? 0);
    const quarterTotalPaid = w2TaxPaid + otherTaxPaid + estimatedTaxPaid;
    const quarterSafeHarborRemaining = quarterSafeHarborTarget - quarterTotalPaid;
    const quarterTaxRemaining = quarterTaxDue - quarterTotalPaid;
    return {
      w2Income: quarterMonths.reduce((sum, row) => sum + row.w2Income, 0),
      tradingIncome: quarterMonths.reduce((sum, row) => sum + row.tradingIncome, 0),
      otherIncome: quarterMonths.reduce((sum, row) => sum + row.otherIncome, 0),
      totalIncome: quarterMonths.reduce((sum, row) => sum + row.totalIncome, 0),
      fifoTotal: quarterMonths.reduce((sum, row) => sum + row.fifoTotal, 0),
      cumulativeW2Income: last.cumulativeW2Income,
      cumulativeFifoTrading: last.cumulativeFifoTrading,
      cumulativeFifoTotal: last.cumulativeFifoTotal,
      fifoTaxDue: cumulativeTaxDue,
      cumulativeW2TaxPaid: last.cumulativeW2TaxPaid,
      cumulativeEstTaxPaid: last.cumulativeEstTaxPaid,
      fifoTaxRemaining: cumulativeTaxDue - last.totalTaxPaid,
      safeHarborTarget: last.safeHarborTarget,
      totalTaxDue: cumulativeTaxDue,
      w2TaxPaid,
      otherTaxPaid,
      estimatedTaxPaid,
      totalTaxPaid: last.totalTaxPaid,
      safeHarborRemaining: last.safeHarborRemaining,
      safeHarborMet: last.safeHarborMet,
      quarterSafeHarborTarget,
      quarterTaxDue,
      quarterTotalPaid,
      quarterSafeHarborRemaining,
      quarterTaxRemaining,
    };
  });

  return { months, quarters };
}

function computeEoyTaxDue(
  settings: TaxSettings,
  year: TaxYear,
  y: TaxYearInputs,
  quarters: TaxEstimateQuarterTotals[],
): number {
  const yearEnd = quarters[3];
  return computeTaxOnW2PlusFifoIncome(settings, year, y, yearEnd.cumulativeFifoTotal, true);
}

export interface TaxPerformanceSummary {
  year: TaxYear;
  throughMonthIndex: number;
  monthTaxOwed: number;
  monthTaxPaid: number;
  monthW2TaxPaid: number;
  monthEstimatedTaxPaid: number;
  monthRemainingTax: number;
  totalTaxOwed: number;
  totalTaxPaid: number;
  w2TaxPaid: number;
  estimatedTaxPaid: number;
  remainingTax: number;
  safeHarborBalance: number;
}

/**
 * Forecast Own Capital tax cash plan for a future tax year (2027+).
 * inYearNeed = year-end safe-harbor remaining (est. payments still owed from trading).
 * priorYearBalance = unpaid prior-year tax assumed settled Mar 30 (own-capital years only).
 */
export async function loadForecastOwnCapitalTaxPlan(
  year: number,
): Promise<ForecastOwnCapitalTaxPlan | null> {
  if (!(getTaxYears() as readonly number[]).includes(year)) return null;
  if (year < 2027 || year <= new Date().getFullYear()) {
    return { inYearNeed: 0, priorYearBalance: 0 };
  }

  const taxYear = year as TaxYear;
  const settings = await ensureTaxSettingsHydrated();
  const y = yearInputs(settings, taxYear);
  const trading = await loadTradingIncomeForTaxYear(taxYear);
  const schedule = computeTaxEstimateSchedule(
    settings,
    taxYear,
    y,
    trading.income,
    trading.fifo,
  );
  const yearEnd = schedule.quarters[3];
  const inYearNeed = Math.max(0, Number(yearEnd.safeHarborRemaining) || 0);

  const priorYear = taxYear - 1;
  const priorAffectsOwnCapital = priorYear >= OWN_CAPITAL_TAX_PAYMENTS_FROM_YEAR;
  const priorYearBalance = priorAffectsOwnCapital
    ? forecastAssumedPriorYearSettlementAmount(
        taxYear,
        y.priorYearTotalTax,
        q1PriorYearTaxPaid(settings, taxYear, y),
      )
    : 0;

  return { inYearNeed, priorYearBalance };
}

/** Compact cumulative Tax-tab totals for the Analytics performance row. */
export async function loadTaxPerformanceSummary(
  year: number,
  throughMonthIndex: number,
): Promise<TaxPerformanceSummary | null> {
  if (!(getTaxYears() as readonly number[]).includes(year)) return null;

  const taxYear = year as TaxYear;
  const settings = await ensureTaxSettingsHydrated();
  const y = yearInputs(settings, taxYear);
  const trading = await loadTradingIncomeForTaxYear(taxYear);
  const schedule = computeTaxEstimateSchedule(
    settings,
    taxYear,
    y,
    trading.income,
    trading.fifo,
  );
  const monthIndex = Math.max(0, Math.min(11, Math.trunc(throughMonthIndex)));
  const row = schedule.months[monthIndex];
  // Match Tax tab annual totals: December applies the standard deduction.
  const totalTaxOwed =
    monthIndex === 11
      ? computeEoyTaxDue(settings, taxYear, y, schedule.quarters)
      : row.totalTaxDue;

  const summary: TaxPerformanceSummary = {
    year: taxYear,
    throughMonthIndex: monthIndex,
    monthTaxOwed: row.monthlyTaxDue,
    monthTaxPaid: row.monthlyTotalTaxPaid,
    monthW2TaxPaid: row.w2TaxPaid,
    monthEstimatedTaxPaid: row.estimatedTaxPaid,
    monthRemainingTax: row.monthlyTaxDue - row.monthlyTotalTaxPaid,
    totalTaxOwed,
    totalTaxPaid: row.totalTaxPaid,
    w2TaxPaid: row.cumulativeW2TaxPaid,
    estimatedTaxPaid: row.cumulativeEstTaxPaid,
    remainingTax: totalTaxOwed - row.totalTaxPaid,
    safeHarborBalance: row.safeHarborRemaining,
  };
  publishTaxDueSnapshot(summary);
  return summary;
}

let lastPublishedTaxDue = "";

/** Hand the computed balance to GET /tax/due. External apps read it; they do not recompute. */
function publishTaxDueSnapshot(summary: TaxPerformanceSummary): void {
  const body = JSON.stringify(summary);
  if (body === lastPublishedTaxDue) return;
  lastPublishedTaxDue = body;
  void fetch("/tax/due", {
    method: "PUT",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body,
  }).catch(() => {
    lastPublishedTaxDue = "";
  });
}

function computeYearTotalTaxForSafeHarbor(
  settings: TaxSettings,
  year: TaxYear,
  tradingIncomeMonths: number[],
  tradingFifoMonths: number[],
): number {
  const y = yearInputs(settings, year);
  const schedule = computeTaxEstimateSchedule(
    settings,
    year,
    y,
    tradingIncomeMonths,
    tradingFifoMonths,
  );
  return computeEoyTaxDue(settings, year, y, schedule.quarters);
}

function zeroTradingMonths(): number[] {
  return MONTH_LABELS.map(() => 0);
}

/** Previous calendar tax year for `year`, or null when none is configured. */
function priorTaxYear(year: TaxYear): TaxYear | null {
  const yearIdx = getTaxYears().indexOf(year);
  return yearIdx > 0 ? getTaxYears()[yearIdx - 1] : null;
}

/** EOY tax for the prior year used by “Pick from last year” / safe harbour. */
function computeLastYearSafeHarborTax(
  settings: TaxSettings,
  year: TaxYear,
  priorTradingIncomeMonths: number[] = zeroTradingMonths(),
  priorTradingFifoMonths: number[] = zeroTradingMonths(),
): number | null {
  const prior = priorTaxYear(year);
  if (prior == null) return null;
  return computeYearTotalTaxForSafeHarbor(
    settings,
    prior,
    priorTradingIncomeMonths,
    priorTradingFifoMonths,
  );
}

function estimateMoneyClass(value: number, forecast = false): string {
  if (value < 0) return " sfe-neg";
  if (value > 0) return forecast ? " sfe-tax-forecast-income" : " roi-strong";
  return "";
}

function estimateSafeHarborRemainClass(value: number): string {
  if (value <= 0) return " sfe-tax-safe-harbor-overpay";
  if (value > 0) return " sfe-neg";
  return "";
}

function renderEstimateMoneyCell(
  value: number,
  forecast: boolean,
  extraClass = "",
): string {
  const displayValue = `$${fmtMoney(value)}`;
  const colorCls = estimateMoneyClass(value, forecast);
  const emptyCls = value === 0 ? " sfe-tax-paycheck-value-empty" : "";
  return `<td class="kpi-v sfe-tax-paycheck-month-value sfe-tax-paycheck-summary-cell sfe-tax-paycheck-summary-cell-money sfe-tax-estimate-money-cell${colorCls}${emptyCls}${extraClass}">${displayValue}</td>`;
}

function renderMobileEstimateTransposeCell(
  primaryValue: number,
  forecast: boolean,
  secondaryLabel = "",
  secondaryValue: number | null = null,
  extraClass = "",
): string {
  const primaryClass = estimateMoneyClass(primaryValue, forecast);
  const primaryEmpty = primaryValue === 0 ? " sfe-tax-paycheck-value-empty" : "";
  const secondary =
    secondaryValue === null
      ? ""
      : `<span class="sfe-tax-estimate-transpose-sub">
          <span class="sfe-tax-estimate-transpose-sub-label">${secondaryLabel}</span>
          <span>$${fmtWholeMoney(secondaryValue)}</span>
        </span>`;
  return `
    <td class="sfe-tax-estimate-transpose-value${forecast ? " sfe-tax-estimate-transpose-forecast" : ""}${extraClass}">
      <span class="kpi-v sfe-tax-estimate-transpose-main${primaryClass}${primaryEmpty}">$${fmtWholeMoney(primaryValue)}</span>
      ${secondary}
    </td>`;
}

function renderEstimateSafeHarborRemainCell(
  value: number,
  extraClass = "",
): string {
  const displayValue = `$${fmtMoney(value)}`;
  const colorCls = estimateSafeHarborRemainClass(value);
  const emptyCls = value === 0 ? " sfe-tax-paycheck-value-empty" : "";
  return `<td class="kpi-v sfe-tax-paycheck-month-value sfe-tax-paycheck-summary-cell sfe-tax-paycheck-summary-cell-money sfe-tax-estimate-money-cell${colorCls}${emptyCls}${extraClass}">${displayValue}</td>`;
}

function renderEstimatePaidCell(
  year: TaxYear,
  monthIndex: number,
  monthTotal: number,
  extraClass = "",
  wholeDollars = false,
): string {
  const forecast = isTaxMonthForecast(year, monthIndex);
  const displayValue =
    monthTotal !== 0
      ? `$${wholeDollars ? fmtWholeMoney(monthTotal) : fmtMoney(monthTotal)}`
      : "-";
  const colorCls = monthTotal !== 0 ? estimateMoneyClass(monthTotal, forecast) : "";
  const month = MONTH_LABELS[monthIndex];
  return `
    <td class="sfe-tax-estimate-paid-cell${extraClass}">
      <button
        type="button"
        class="sfe-tax-estimate-paid-btn"
        data-month="${monthIndex}"
        aria-label="Manage estimated tax payments for ${month}"
        title="Manage payments"
      >
        <span class="kpi-v sfe-tax-paycheck-month-value sfe-tax-paycheck-cell-value${colorCls}${monthTotal === 0 ? " sfe-tax-paycheck-value-empty" : ""}">${displayValue}</span>
        <span class="sfe-tax-estimate-paid-manage" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="9" height="9">
            <path fill="currentColor" d="M11.013 1.427a1.75 1.75 0 0 1 2.474 0l1.086 1.086a1.75 1.75 0 0 1 0 2.474l-8.986 8.986a1 1 0 0 1-.446.261l-3.25 1.083a.25.25 0 0 1-.316-.316l1.083-3.25a1 1 0 0 1 .261-.446l8.986-8.986z"/>
          </svg>
        </span>
      </button>
    </td>`;
}

function renderEstTaxPaymentsModal(
  year: TaxYear,
  payments: TaxEstimatedPayment[],
  focusMonthIndex: number | null,
  editingId: string | null,
  draftDate: string,
  draftAmount: string,
  draftKind: TaxPaymentKind,
  draftNote: string,
  draftFromOwnCapital: boolean,
): string {
  const yearPayments = estimatedTaxPaymentsForYear(year, payments);
  const editing = editingId ? yearPayments.find((payment) => payment.id === editingId) : null;
  const formDate = editing?.date ?? draftDate;
  const formAmount = editing ? fmtMoney(editing.amount) : draftAmount;
  const formKind = editing?.kind ?? draftKind;
  const formNote = editing ? editing.note : draftNote;
  const formFromOwnCapital = editing ? paymentFromOwnCapital(editing) : draftFromOwnCapital;
  const submitLabel = editing ? "Save" : "Add payment";
  const focusLabel =
    focusMonthIndex != null ? `${MONTH_LABELS[focusMonthIndex]} ${year}` : `${year}`;

  const rows =
    yearPayments.length === 0
      ? `<tr><td colspan="6" class="sfe-tax-est-payments-empty muted small">No payments yet.</td></tr>`
      : yearPayments
          .map((payment) => {
            const monthIndex = paymentMonthIndex(payment.date, year);
            const highlight =
              focusMonthIndex != null && monthIndex === focusMonthIndex
                ? " sfe-tax-est-payments-row-focus"
                : "";
            const noteText = payment.note.trim();
            const noteCell = noteText
              ? `<span class="sfe-tax-est-payments-note" title="${esc(noteText)}">${esc(noteText)}</span>`
              : `<span class="sfe-tax-est-payments-note sfe-tax-est-payments-note-empty muted">—</span>`;
            const sourceLabel = paymentFromOwnCapital(payment) ? "Trading" : "Outside";
            return `
              <tr class="sfe-tax-est-payments-row${highlight}" data-payment-id="${esc(payment.id)}">
                <td>${esc(payment.date)}</td>
                <td>${esc(taxPaymentKindLabel(payment.kind))}</td>
                <td class="sfe-tax-est-payments-amount">$${fmtMoney(payment.amount)}</td>
                <td class="sfe-tax-est-payments-source">${esc(sourceLabel)}</td>
                <td class="sfe-tax-est-payments-note-cell">${noteCell}</td>
                <td class="sfe-tax-est-payments-actions">
                  <button type="button" class="sfe-tax-est-payments-edit" data-payment-id="${esc(payment.id)}">Edit</button>
                  <button type="button" class="sfe-tax-est-payments-delete" data-payment-id="${esc(payment.id)}">Delete</button>
                </td>
              </tr>`;
          })
          .join("");

  return `
    <div class="sfe-modal-overlay sfe-modal-open sfe-tax-est-payments-overlay" id="sfe-tax-est-payments-modal" ${CUSTOM_PANEL_MARKER_ATTR}="true">
      <div class="sfe-modal sfe-tax-est-payments-modal" role="dialog" aria-modal="true" aria-labelledby="sfe-tax-est-payments-title">
        <div class="sfe-modal-header">
          <h3 id="sfe-tax-est-payments-title">Estimated tax payments — ${esc(focusLabel)}</h3>
          <button type="button" class="sfe-modal-close sfe-tax-est-payments-close" aria-label="Close">×</button>
        </div>
        <p class="muted small sfe-tax-est-payments-lead">All payments entered here apply to tax year ${year}, including payments dated through Apr 15, ${year + 1}. Cash reporting still uses the payment date. Own Capital only deducts payments from the trading account (default on for ${OWN_CAPITAL_TAX_PAYMENTS_FROM_YEAR}+; off for earlier years / W-2).</p>
        <form class="sfe-tax-est-payments-form">
          <label class="sfe-modal-field sfe-tax-est-payments-field sfe-tax-est-payments-field-date">
            Payment date
            <div class="sfe-tax-est-payments-date-field">
              <input type="hidden" class="sfe-tax-est-payments-date" value="${esc(formDate)}" />
              <button type="button" class="sfe-tax-est-payments-date-trigger" aria-haspopup="dialog" aria-expanded="false">
                <span class="sfe-tax-est-payments-date-label">${esc(formatEstPaymentDisplayDate(formDate))}</span>
                <span class="sfe-tax-est-payments-date-icon" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="14" height="14">
                    <path fill="currentColor" d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1.5A1.5 1.5 0 0 1 16 2.5v11A1.5 1.5 0 0 1 14.5 15h-13A1.5 1.5 0 0 1 0 13.5v-11A1.5 1.5 0 0 1 1.5 1H3V.5a.5.5 0 0 1 .5-.5M1 4v9.5a.5.5 0 0 0 .5.5h13a.5.5 0 0 0 .5-.5V4z"/>
                  </svg>
                </span>
              </button>
              <div class="sfe-tax-est-payments-calendar" hidden role="dialog" aria-label="Choose payment date"></div>
            </div>
          </label>
          <label class="sfe-modal-field sfe-tax-est-payments-field">
            Payment type
            <select class="sfe-tax-est-payments-input sfe-tax-est-payments-kind">
              ${(["estimated", "balance-due", "extension", "other"] as TaxPaymentKind[])
                .map(
                  (kind) =>
                    `<option value="${kind}"${kind === formKind ? " selected" : ""}>${esc(taxPaymentKindLabel(kind))}</option>`,
                )
                .join("")}
            </select>
          </label>
          <label class="sfe-modal-field sfe-tax-est-payments-field">
            Amount
            <span class="sfe-tax-est-payments-amount-wrap">
              <span class="sfe-tax-est-payments-amount-prefix">$</span>
              <input type="text" class="sfe-tax-est-payments-input sfe-tax-est-payments-amount-input" inputmode="decimal" value="${esc(formAmount)}" autocomplete="off" />
            </span>
          </label>
          <label class="sfe-modal-field sfe-tax-est-payments-field sfe-tax-est-payments-field-note">
            Note
            <input type="text" class="sfe-tax-est-payments-input sfe-tax-est-payments-note-input" value="${esc(formNote)}" placeholder="Optional memo" maxlength="200" autocomplete="off" />
          </label>
          <label class="sfe-tax-est-payments-from-own-capital">
            <input type="checkbox" class="sfe-tax-est-payments-from-own-capital-input"${formFromOwnCapital ? " checked" : ""} />
            <span>Paid from trading account (reduces Own Capital)</span>
          </label>
          <div class="sfe-tax-est-payments-form-actions">
            <button type="submit" class="sfe-tax-est-payments-submit">${esc(submitLabel)}</button>
            ${
              editing
                ? `<button type="button" class="sfe-tax-est-payments-cancel-edit">Cancel edit</button>`
                : ""
            }
          </div>
        </form>
        <div class="sfe-tax-est-payments-table-wrap">
          <table class="sfe-tax-est-payments-table">
            <thead>
              <tr>
                <th scope="col">Date</th>
                <th scope="col">Type</th>
                <th scope="col">Amount</th>
                <th scope="col">Source</th>
                <th scope="col">Note</th>
                <th scope="col" aria-hidden="true"></th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
        <div class="sfe-modal-actions">
          <button type="button" class="sfe-tax-est-payments-close">Close</button>
        </div>
      </div>
    </div>`;
}

function renderIrsOpportunityModal(breakdown: IrsOpportunityBreakdown): string {
  const title =
    breakdown.scope === "quarter"
      ? `IRS opportunity — ${breakdown.title}`
      : `IRS opportunity — ${breakdown.title}`;

  const activeMonths = breakdown.months;

  const monthRows =
    activeMonths.length === 0
      ? `<tr><td colspan="5" class="sfe-tax-irs-oppty-empty muted small">No unpaid estimated tax — no opportunity accrual.</td></tr>`
      : activeMonths
          .map(
            (row) => {
              const selectedQuarter =
                breakdown.scope === "quarter" &&
                Math.floor(row.monthIndex / 3) === breakdown.quarterIndex;
              const status = taxMonthStatus(breakdown.year, row.monthIndex);
              const rowClasses = [
                `sfe-tax-irs-oppty-row-${status}`,
                selectedQuarter ? "sfe-tax-irs-oppty-selected-quarter-row" : "",
              ]
                .filter(Boolean)
                .join(" ");
              return `
              <tr class="${rowClasses}">
                <th scope="row">${esc(row.monthLabel)}</th>
                <td class="sfe-tax-irs-oppty-num">$${fmtMoney(row.cumulativeUnpaid)}</td>
                <td class="sfe-tax-irs-oppty-num">$${fmtMoney(row.newUnpaidBalance)}</td>
                <td class="sfe-tax-irs-oppty-num">${row.priorAccrualDays}</td>
                <td class="sfe-tax-irs-oppty-num sfe-tax-irs-oppty-total">$${fmtMoney(row.totalOpportunity)}</td>
              </tr>`;
            },
          )
          .join("");

  const quarterSummary =
    breakdown.scope === "annual" && breakdown.quarterSummaries.some((q) => q.totalOpportunity > 0)
      ? `
        <div class="sfe-tax-irs-oppty-quarter-summary">
          <h4 class="sfe-tax-irs-oppty-quarter-summary-title">By quarter</h4>
          <table class="sfe-tax-irs-oppty-quarter-table">
            <thead>
              <tr>
                <th scope="col">Quarter</th>
                <th scope="col">Opportunity</th>
                <th scope="col" class="sfe-tax-irs-oppty-quarter-status-head">Status</th>
              </tr>
            </thead>
            <tbody>
              ${breakdown.quarterSummaries
                .map((row, quarterIndex) => {
                  const status = taxQuarterStatus(breakdown.year, quarterIndex);
                  const statusLabel =
                    status === "fact"
                      ? "Fact"
                      : status === "forecast"
                        ? "Forecast"
                        : "Fact + Forecast";
                  return `
                <tr class="sfe-tax-irs-oppty-quarter-row-${status}">
                  <th scope="row">${esc(row.label)}</th>
                  <td class="sfe-tax-irs-oppty-num">$${fmtMoney(row.totalOpportunity)}</td>
                  <td class="sfe-tax-irs-oppty-quarter-status">
                    <span class="sfe-tax-irs-oppty-quarter-status-badge sfe-tax-irs-oppty-quarter-status-badge-${status}">${statusLabel}</span>
                  </td>
                </tr>`;
                })
                .join("")}
            </tbody>
          </table>
        </div>`
      : "";

  const scopeLead =
    breakdown.scope === "quarter"
      ? `Months shown Jan through quarter end. Unpaid at month-end starts accruing the 1st of the following month through ${breakdown.accrueEnd}.`
      : `Annual view sums month-by-month accrual (1st of the following month after each unpaid month) through ${breakdown.accrueEnd}.`;

  return `
    <div class="sfe-modal-overlay sfe-modal-open sfe-tax-irs-oppty-overlay" id="sfe-tax-irs-opportunity-modal" ${CUSTOM_PANEL_MARKER_ATTR}="true">
      <div class="sfe-modal sfe-tax-irs-oppty-modal" role="dialog" aria-modal="true" aria-labelledby="sfe-tax-irs-oppty-title">
        <div class="sfe-modal-header">
          <h3 id="sfe-tax-irs-oppty-title">${esc(title)}</h3>
          <button type="button" class="sfe-modal-close sfe-tax-irs-oppty-close" aria-label="Close">×</button>
        </div>
        <div class="sfe-tax-irs-oppty-modal-scroll">
        <p class="muted small sfe-tax-irs-oppty-lead">
          Monthly rate ${breakdown.monthlyRatePct}% · balance × (rate ÷ 100) × (days ÷ 30).
          ${esc(scopeLead)}
        </p>
        <div class="sfe-tax-irs-oppty-summary kpi-row">
          <div class="kpi sfe-tax-irs-oppty-summary-item">
            <span class="kpi-k">Prior unpaid (cumul)</span>
            <span class="kpi-v">$${fmtMoney(breakdown.priorUnpaidBalance)}</span>
          </div>
          <div class="kpi sfe-tax-irs-oppty-summary-item">
            <span class="kpi-k">New unpaid (cumul)</span>
            <span class="kpi-v">$${fmtMoney(breakdown.newUnpaidBalance)}</span>
          </div>
          <div class="kpi sfe-tax-irs-oppty-summary-item">
            <span class="kpi-k">Total Due tax</span>
            <span class="kpi-v">$${fmtMoney(breakdown.cumulativeUnpaid)}</span>
          </div>
          <div class="kpi sfe-tax-irs-oppty-summary-item">
            <span class="kpi-k">Total Income</span>
            <span class="kpi-v sfe-tax-safe-harbor-overpay">$${fmtMoney(breakdown.totalOpportunity)}</span>
          </div>
        </div>
        <div class="sfe-tax-irs-oppty-table-wrap">
          <table class="sfe-tax-irs-oppty-table">
            <thead>
              <tr>
                <th scope="col">Month</th>
                <th scope="col">Unpaid (cumul)</th>
                <th scope="col">Balance</th>
                <th scope="col">Days</th>
                <th scope="col">Income</th>
              </tr>
            </thead>
            <tbody>${monthRows}</tbody>
            <tfoot>
              <tr>
                <th scope="row">Total</th>
                <td colspan="3"></td>
                <td class="sfe-tax-irs-oppty-num sfe-tax-irs-oppty-total">$${fmtMoney(breakdown.totalOpportunity)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
        ${quarterSummary}
        </div>
        <div class="sfe-modal-actions">
          <button type="button" class="sfe-tax-irs-oppty-close">Close</button>
        </div>
      </div>
    </div>`;
}

function renderIrsInterestModal(breakdown: IrsInterestBreakdown): string {
  const title = `Safe harbour interest — ${breakdown.title}`;

  const activeMonths = breakdown.months.filter(
    (row) =>
      row.interest > 0 ||
      row.cumulativeShortfall > 0 ||
      row.accrualDays > 0,
  );

  const monthRows =
    activeMonths.length === 0
      ? `<tr><td colspan="6" class="sfe-tax-irs-oppty-empty muted small">No safe-harbor shortfall — no interest accrual.</td></tr>`
      : activeMonths
          .map(
            (row) => `
              <tr>
                <th scope="row">${esc(row.monthLabel)}</th>
                <td class="sfe-tax-irs-oppty-num">$${fmtMoney(row.cumulativeShortfall)}</td>
                <td class="sfe-tax-irs-oppty-num">$${fmtMoney(row.priorShortfallBalance)}</td>
                <td class="sfe-tax-irs-oppty-num">$${fmtMoney(row.newShortfall)}</td>
                <td class="sfe-tax-irs-oppty-num">${row.accrualDays}</td>
                <td class="sfe-tax-irs-oppty-num sfe-tax-irs-oppty-total">$${fmtMoney(row.interest)}</td>
              </tr>`,
          )
          .join("");

  const quarterSummary =
    breakdown.scope === "annual" && breakdown.quarterSummaries.some((q) => q.totalInterest > 0)
      ? `
        <div class="sfe-tax-irs-oppty-quarter-summary">
          <h4 class="sfe-tax-irs-oppty-quarter-summary-title">By quarter</h4>
          <table class="sfe-tax-irs-oppty-quarter-table">
            <thead>
              <tr>
                <th scope="col">Quarter</th>
                <th scope="col">Interest</th>
              </tr>
            </thead>
            <tbody>
              ${breakdown.quarterSummaries
                .map(
                  (row) => `
                <tr>
                  <th scope="row">${esc(row.label)}</th>
                  <td class="sfe-tax-irs-oppty-num">$${fmtMoney(row.totalInterest)}</td>
                </tr>`,
                )
                .join("")}
            </tbody>
          </table>
        </div>`
      : "";

  const scopeLead =
    breakdown.scope === "quarter"
      ? `Months shown Jan through quarter end. Safe-harbor shortfall at month start accrues from due date ${breakdown.dueDate} through ${breakdown.accrueEnd}.`
      : `Annual view sums month-by-month interest from each quarter due date through ${breakdown.accrueEnd}.`;

  return `
    <div class="sfe-modal-overlay sfe-modal-open sfe-tax-irs-oppty-overlay" id="sfe-tax-irs-interest-modal" ${CUSTOM_PANEL_MARKER_ATTR}="true">
      <div class="sfe-modal sfe-tax-irs-oppty-modal" role="dialog" aria-modal="true" aria-labelledby="sfe-tax-irs-interest-title">
        <div class="sfe-modal-header">
          <h3 id="sfe-tax-irs-interest-title">${esc(title)}</h3>
          <button type="button" class="sfe-modal-close sfe-tax-irs-interest-close" aria-label="Close">×</button>
        </div>
        <div class="sfe-tax-irs-oppty-modal-scroll">
        <p class="muted small sfe-tax-irs-oppty-lead">
          Annual rate ${breakdown.annualRatePct}% · balance × (rate ÷ 100) × (days ÷ 365).
          ${esc(scopeLead)}
        </p>
        <div class="sfe-tax-irs-oppty-summary kpi-row">
          <div class="kpi sfe-tax-irs-oppty-summary-item">
            <span class="kpi-k">Prior shortfall (cumul)</span>
            <span class="kpi-v">$${fmtMoney(breakdown.priorShortfall)}</span>
          </div>
          <div class="kpi sfe-tax-irs-oppty-summary-item">
            <span class="kpi-k">New shortfall (cumul)</span>
            <span class="kpi-v">$${fmtMoney(breakdown.newShortfall)}</span>
          </div>
          <div class="kpi sfe-tax-irs-oppty-summary-item">
            <span class="kpi-k">Shortfall (cumul)</span>
            <span class="kpi-v">$${fmtMoney(breakdown.cumulativeShortfall)}</span>
          </div>
          <div class="kpi sfe-tax-irs-oppty-summary-item">
            <span class="kpi-k">Total interest</span>
            <span class="kpi-v sfe-neg">$${fmtMoney(breakdown.totalInterest)}</span>
          </div>
        </div>
        <div class="sfe-tax-irs-oppty-table-wrap">
          <table class="sfe-tax-irs-oppty-table">
            <thead>
              <tr>
                <th scope="col">Month</th>
                <th scope="col">Shortfall (cumul)</th>
                <th scope="col">Accruing bal.</th>
                <th scope="col">New shortfall</th>
                <th scope="col">Days</th>
                <th scope="col">Interest</th>
              </tr>
            </thead>
            <tbody>${monthRows}</tbody>
            <tfoot>
              <tr>
                <th scope="row">Total</th>
                <td colspan="4"></td>
                <td class="sfe-tax-irs-oppty-num sfe-tax-irs-oppty-total">$${fmtMoney(breakdown.totalInterest)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
        ${quarterSummary}
        </div>
        <div class="sfe-modal-actions">
          <button type="button" class="sfe-tax-irs-interest-close">Close</button>
        </div>
      </div>
    </div>`;
}

function renderSafeHarborModal(breakdown: SafeHarborBreakdown): string {
  const title = `Safe Harbour Balance — ${breakdown.title}`;

  const activeMonths = breakdown.months.filter(
    (row) =>
      row.cumulativeUnderpaid > 0 ||
      row.cumulativeRemaining !== 0,
  );

  const monthRows =
    activeMonths.length === 0
      ? `<tr><td colspan="4" class="sfe-tax-irs-oppty-empty muted small">Safe harbour target met — no underpayment.</td></tr>`
      : activeMonths
          .map(
            (row) => {
              const status = taxMonthStatus(breakdown.year, row.monthIndex);
              return `
              <tr class="sfe-tax-safe-harbor-row-${status}">
                <th scope="row">${esc(row.monthLabel)}</th>
                <td class="sfe-tax-irs-oppty-num">$${fmtMoney(row.cumulativeTarget)}</td>
                <td class="sfe-tax-irs-oppty-num">$${fmtMoney(row.cumulativePaid)}</td>
                <td class="sfe-tax-irs-oppty-num${row.cumulativeRemaining > 0 ? " sfe-neg" : " sfe-tax-safe-harbor-overpay"}">$${fmtMoney(row.cumulativeRemaining)}</td>
              </tr>`;
            },
          )
          .join("");

  return `
    <div class="sfe-modal-overlay sfe-modal-open sfe-tax-irs-oppty-overlay" id="sfe-tax-safe-harbor-modal" ${CUSTOM_PANEL_MARKER_ATTR}="true">
      <div class="sfe-modal sfe-tax-irs-oppty-modal" role="dialog" aria-modal="true" aria-labelledby="sfe-tax-safe-harbor-title">
        <div class="sfe-modal-header">
          <h3 id="sfe-tax-safe-harbor-title">${esc(title)}</h3>
          <button type="button" class="sfe-modal-close sfe-tax-safe-harbor-close" aria-label="Close">×</button>
        </div>
        <div class="sfe-tax-irs-oppty-modal-scroll">
        <div class="sfe-tax-irs-oppty-summary kpi-row">
          <div class="kpi sfe-tax-irs-oppty-summary-item">
            <span class="kpi-k">Target (cumul)</span>
            <span class="kpi-v">$${fmtMoney(breakdown.cumulativeTarget)}</span>
          </div>
          <div class="kpi sfe-tax-irs-oppty-summary-item">
            <span class="kpi-k">Paid (cumul)</span>
            <span class="kpi-v">$${fmtMoney(breakdown.cumulativePaid)}</span>
          </div>
          <div class="kpi sfe-tax-irs-oppty-summary-item">
            <span class="kpi-k">Remain (cumul)</span>
            <span class="kpi-v${breakdown.cumulativeRemaining > 0 ? " sfe-neg" : " sfe-tax-safe-harbor-overpay"}">$${fmtMoney(breakdown.cumulativeRemaining)}</span>
          </div>
          <div class="kpi sfe-tax-irs-oppty-summary-item">
            <span class="kpi-k">Underpaid (cumul)</span>
            <span class="kpi-v${breakdown.cumulativeUnderpaid > 0 ? " sfe-neg" : " sfe-tax-safe-harbor-overpay"}">$${fmtMoney(breakdown.cumulativeUnderpaid)}</span>
          </div>
        </div>
        <div class="sfe-tax-irs-oppty-table-wrap">
          <table class="sfe-tax-irs-oppty-table">
            <thead>
              <tr>
                <th scope="col">Month</th>
                <th scope="col">Target (cumul)</th>
                <th scope="col">Paid (cumul)</th>
                <th scope="col">Remain (cumul)</th>
              </tr>
            </thead>
            <tbody>${monthRows}</tbody>
          </table>
        </div>
        </div>
        <div class="sfe-modal-actions">
          <button type="button" class="sfe-tax-safe-harbor-close">Close</button>
        </div>
      </div>
    </div>`;
}

function renderSafeHarborMetCell(met: boolean): string {
  if (!met) {
    return `<td class="sfe-tax-estimate-safe-cell" aria-label="Safe harbor not met"></td>`;
  }
  return `
    <td class="sfe-tax-estimate-safe-cell sfe-tax-estimate-safe-cell-met" aria-label="Safe harbor met">
      <span class="sfe-tax-safe-harbor-met-mark" title="Safe harbor met">✓</span>
    </td>`;
}

function renderQuarterCardSubcard(
  label: string,
  displayValue: string,
  colorCls = "",
  tip?: string,
  extraClass = "",
  clickable?: {
    kind: "opportunity" | "interest" | "safe-harbor";
    scope: "quarter" | "annual";
    quarterIndex: number;
  },
): string {
  const hint = tip ? renderTaxHint(tip) : "";
  const clickClass = clickable ? " sfe-tax-irs-detail-subcard" : "";
  const attrs = clickable
    ? ` role="button" tabindex="0" data-irs-detail-kind="${clickable.kind}" data-irs-detail-scope="${clickable.scope}" data-irs-detail-quarter="${clickable.quarterIndex}" aria-label="${esc(label)} details — view breakdown"`
    : "";
  const detailIcon = clickable ? IRS_DETAIL_BREAKDOWN_ICON : "";
  return `
    <div class="kpi sfe-tax-w2-totals-subcard sfe-tax-estimate-quarter-subcard${extraClass}${clickClass}"${attrs}>
      <span class="kpi-k">${esc(label)}${hint}</span>
      <span class="kpi-v sfe-tax-kpi-gross sfe-tax-irs-detail-value${colorCls}">${detailIcon}${displayValue}</span>
    </div>`;
}

function estimateTaxRemainClass(value: number): string {
  if (value > 0) return " sfe-neg";
  if (value < 0) return " sfe-tax-safe-harbor-overpay";
  return "";
}


function renderQuarterMoneySubcard(
  label: string,
  value: number,
  forecast: boolean,
  tip?: string,
  extraClass = "",
  valueColorCls?: string,
): string {
  const colorCls = valueColorCls ?? estimateMoneyClass(value, forecast);
  return renderQuarterCardSubcard(label, `$${fmtMoney(value)}`, colorCls, tip, extraClass);
}

function renderQuarterSummaryLine(
  label: string,
  displayValue: string,
  colorCls = "",
  tip?: string,
  extraClass = "",
  clickable?: {
    kind: "opportunity" | "interest" | "safe-harbor";
    scope: "quarter" | "annual";
    quarterIndex: number;
  },
  emphasize = false,
): string {
  const hint = tip ? renderTaxHint(tip) : "";
  const clickClass = clickable ? " sfe-tax-irs-detail-subcard sfe-tax-estimate-summary-line-clickable" : "";
  const attrs = clickable
    ? ` role="button" tabindex="0" data-irs-detail-kind="${clickable.kind}" data-irs-detail-scope="${clickable.scope}" data-irs-detail-quarter="${clickable.quarterIndex}" aria-label="${esc(label)} details — view breakdown"`
    : "";
  const detailIcon = clickable ? IRS_DETAIL_BREAKDOWN_ICON : "";
  const emphasizeCls = emphasize ? " sfe-tax-estimate-summary-line-emphasis" : "";
  return `
    <div class="sfe-tax-estimate-summary-line${extraClass}${clickClass}${emphasizeCls}"${attrs}>
      <span class="sfe-tax-estimate-summary-line-label">${esc(label)}${hint}</span>
      <span class="sfe-tax-estimate-summary-line-value sfe-tax-irs-detail-value${colorCls}">${detailIcon}${displayValue}</span>
    </div>`;
}

function renderQuarterMoneyLine(
  label: string,
  value: number,
  forecast: boolean,
  tip?: string,
  extraClass = "",
  valueColorCls?: string,
  clickable?: {
    kind: "opportunity" | "interest" | "safe-harbor";
    scope: "quarter" | "annual";
    quarterIndex: number;
  },
  emphasize = false,
): string {
  const colorCls = valueColorCls ?? estimateMoneyClass(value, forecast);
  return renderQuarterSummaryLine(
    label,
    `$${fmtMoney(value)}`,
    colorCls,
    tip,
    extraClass,
    clickable,
    emphasize,
  );
}

function renderEstTaxPaidSummaryLine(
  value: number,
  forecast: boolean,
  tip: string,
  focusMonthIndex: number,
): string {
  const colorCls = estimateMoneyClass(value, forecast);
  const editButton = `
    <button
      type="button"
      class="sfe-tax-estimate-paid-btn sfe-tax-estimate-paid-card-btn"
      data-month="${focusMonthIndex}"
      aria-label="Manage estimated tax payments"
      title="Manage payments"
    >
      <span class="sfe-tax-estimate-paid-manage" aria-hidden="true">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="9" height="9">
          <path fill="currentColor" d="M11.013 1.427a1.75 1.75 0 0 1 2.474 0l1.086 1.086a1.75 1.75 0 0 1 0 2.474l-8.986 8.986a1 1 0 0 1-.446.261l-3.25 1.083a.25.25 0 0 1-.316-.316l1.083-3.25a1 1 0 0 1 .261-.446l8.986-8.986z"/>
        </svg>
      </span>
    </button>`;
  return renderQuarterSummaryLine(
    "Est tax paid",
    `${editButton}$${fmtMoney(value)}`,
    colorCls,
    tip,
    " sfe-tax-estimate-summary-line-est-paid",
  );
}

function renderQuarterLinesCard(
  title: string,
  titleExtra: string,
  lines: string,
  statusBadge: string,
  extraClass = "",
  cumulativeHeader = false,
): string {
  return renderQuarterGroupCard(
    title,
    titleExtra,
    "",
    "",
    statusBadge,
    extraClass,
    cumulativeHeader,
    "",
    `<div class="sfe-tax-estimate-quarter-group-body sfe-tax-estimate-summary-lines">${lines}</div>`,
  );
}

function renderQuarterGroupCard(
  title: string,
  titleExtra: string,
  topSubcards: string,
  bottomSubcards: string,
  statusBadge: string,
  extraClass = "",
  cumulativeHeader = false,
  middleSubcards = "",
  customBody = "",
): string {
  const titleHtml = cumulativeHeader
    ? `${esc(title)} <span class="sfe-tax-estimate-cumulative-label">cumulative</span>`
    : esc(title);
  const middleRow = middleSubcards.trim()
    ? `<div class="sfe-tax-w2-totals-split sfe-tax-estimate-quarter-group-middle">${middleSubcards}</div>`
    : "";
  const bottomRow = bottomSubcards.trim()
    ? `<div class="sfe-tax-w2-totals-split sfe-tax-estimate-quarter-group-totals">${bottomSubcards}</div>`
    : "";
  const body = customBody.trim()
    ? customBody
    : `<div class="sfe-tax-estimate-quarter-group-body">
        <div class="sfe-tax-w2-totals-split">${topSubcards}</div>
        ${middleRow}
        ${bottomRow}
      </div>`;
  return `
    <div class="kpi sfe-tax-w2-person-totals-card sfe-tax-estimate-quarter-group-card ${extraClass}">
      <span class="kpi-k sfe-tax-w2-person-totals-name sfe-tax-estimate-quarter-group-head">
        <span class="sfe-tax-estimate-quarter-group-title-row">
          <span class="sfe-tax-estimate-quarter-group-title-text">${titleHtml}${titleExtra}</span>
          ${statusBadge}
        </span>
      </span>
      ${body}
    </div>`;
}

function renderTaxEstimateSummaryCards(
  year: TaxYear,
  scope: "quarter" | "annual",
  quarterIndex: number,
  totals: TaxEstimateQuarterTotals,
  interest: TaxEstimateQuarterInterest | TaxEstimateAnnualInterest,
  forecast: boolean,
): string {
  const periodStatus =
    scope === "annual"
      ? taxYearTotalStatus(year)
      : taxQuarterTotalStatus(year, quarterIndex);
  const groupStatusBadge = renderTaxPeriodStatusBadge(periodStatus);
  const valueForecast = periodStatus === "Forecast";
  const remainingCls = estimateSafeHarborRemainClass(
    totals.safeHarborRemaining,
  );
  const safeHarborBadge = totals.safeHarborMet
    ? `<span class="sfe-tax-estimate-quarter-safe-badge" title="Safe harbor met">✓</span>`
    : "";
  const w2IncomeTip = scope === "annual" ? TIP_EST_W2_ANNUAL : TIP_EST_W2_QUARTER;
  const tradingIncomeTip =
    scope === "annual" ? TIP_EST_TRADING_ANNUAL : TIP_EST_TRADING_QUARTER;
  const fifoIncomeTip =
    scope === "annual" ? TIP_EST_FIFO_TOTAL_ANNUAL : TIP_EST_FIFO_TOTAL_QUARTER;
  const quarterHeaderMarker =
    scope === "quarter"
      ? `<span class="sfe-tax-estimate-this-quarter-mark">Quarter ${quarterIndex + 1} ${year}</span>`
      : "";

  const incomeCard = renderQuarterLinesCard(
    "Income",
    quarterHeaderMarker,
    `${renderQuarterMoneyLine("W-2", totals.w2Income, valueForecast, w2IncomeTip)}
     ${renderQuarterMoneyLine("Trading", totals.tradingIncome, valueForecast, tradingIncomeTip)}
     ${renderQuarterMoneyLine("Other", totals.otherIncome, valueForecast, TIP_EST_OTHER_INCOME)}
     ${renderQuarterMoneyLine("Total", totals.totalIncome, valueForecast, TIP_EST_TOTAL, " sfe-tax-estimate-summary-line-dim")}
     ${renderQuarterMoneyLine("FIFO total", totals.fifoTotal, valueForecast, fifoIncomeTip, " sfe-tax-estimate-summary-line-fifo")}`,
    groupStatusBadge,
    "sfe-tax-estimate-quarter-income-card",
  );

  const safeHarborTarget =
    scope === "quarter" ? totals.quarterSafeHarborTarget : totals.safeHarborTarget;
  const estTaxPaid =
    scope === "quarter" ? totals.estimatedTaxPaid : totals.cumulativeEstTaxPaid;
  const estTaxPaidFocusMonth =
    scope === "quarter" ? quarterIndex * 3 : new Date().getMonth();
  const safeHarborRemaining = totals.safeHarborRemaining;
  const safeHarborQuarterPaymentLines =
    scope === "quarter"
      ? `${renderQuarterMoneyLine(
          "W-2 tax paid",
          totals.w2TaxPaid,
          valueForecast,
          TIP_EST_W2_TAX_QUARTER,
        )}
         ${renderQuarterMoneyLine(
           "Other tax",
           totals.otherTaxPaid,
           valueForecast,
           TIP_EST_OTHER_TAX,
         )}
         ${renderEstTaxPaidSummaryLine(
           estTaxPaid,
           valueForecast,
           TIP_EST_EST_PAID_QUARTER,
           estTaxPaidFocusMonth,
         )}
         ${renderQuarterMoneyLine(
           "Total tax paid",
           totals.quarterTotalPaid,
           valueForecast,
           TIP_EST_TOTAL_PAID_QUARTER,
         )}`
      : `${renderQuarterMoneyLine(
          "Other tax",
          totals.otherTaxPaid,
          valueForecast,
          TIP_EST_OTHER_TAX,
        )}
         ${renderEstTaxPaidSummaryLine(
           estTaxPaid,
           valueForecast,
           TIP_EST_EST_PAID_YTD,
           estTaxPaidFocusMonth,
         )}`;
  const irsInterestCls = interest.irsInterest > 0 ? " sfe-neg" : "";

  const taxPaymentsCard = renderQuarterLinesCard(
    "Safe Harbour",
    `${quarterHeaderMarker}${safeHarborBadge}`,
    `${renderQuarterMoneyLine(
      scope === "quarter" ? "Safe harbour due" : "Safe harbor",
      safeHarborTarget,
      valueForecast,
      scope === "quarter" ? TIP_EST_SAFE_HARBOR_QUARTER : TIP_EST_SAFE_HARBOR,
    )}
     ${safeHarborQuarterPaymentLines}
     ${renderQuarterSummaryLine(
       scope === "quarter"
         ? "Safe harbour balance (cumulative)"
         : "Safe harbour balance",
       `$${fmtMoney(safeHarborRemaining)}`,
       remainingCls,
       TIP_EST_REMAINING,
       "",
       { kind: "safe-harbor", scope, quarterIndex },
     )}
     ${renderQuarterSummaryLine(
       scope === "quarter" ? "IRS interest incurred this quarter" : "IRS interest",
       `$${fmtMoney(interest.irsInterest)}`,
       irsInterestCls,
       TIP_EST_IRS_INTEREST,
       "",
       { kind: "interest", scope, quarterIndex },
     )}`,
    groupStatusBadge,
    "sfe-tax-estimate-quarter-tax-card",
    scope === "annual",
  );

  const totalTaxOwed = scope === "quarter" ? totals.quarterTaxDue : totals.fifoTaxDue;
  const totalTaxPaid =
    scope === "quarter"
      ? totals.quarterTotalPaid
      : totals.cumulativeW2TaxPaid + totals.cumulativeEstTaxPaid;
  const remainingTotalTax = totalTaxOwed - totalTaxPaid;
  const estUnpaidAmount = Math.max(0, remainingTotalTax);
  const estUnpaidCls = estUnpaidAmount > 0 ? " sfe-neg" : "";
  const irsOpportunityCls = interest.irsOpportunity > 0 ? " sfe-tax-safe-harbor-overpay" : "";

  const totalTaxCard = renderQuarterLinesCard(
    "Total Tax (FIFO)",
    quarterHeaderMarker,
    `${renderQuarterMoneyLine(
      "Total tax owed",
      totalTaxOwed,
      valueForecast,
      scope === "quarter" ? TIP_EST_TAX_DUE_QUARTER : TIP_EST_TOTAL_TAX_OWED_ANNUAL,
      "",
      undefined,
      undefined,
      true,
    )}
     ${renderQuarterMoneyLine(
       "Total tax paid",
       totalTaxPaid,
       valueForecast,
       scope === "quarter" ? TIP_EST_TOTAL_PAID_QUARTER : TIP_EST_TOTAL_PAID,
     )}
     ${renderQuarterMoneyLine(
       "Due total tax",
       remainingTotalTax,
       valueForecast,
       scope === "quarter"
         ? "This quarter's tax due minus W-2 withholding and estimated tax payments in this quarter."
         : TIP_EST_TAX_REMAIN_ANNUAL,
       "",
       estimateTaxRemainClass(remainingTotalTax),
     )}
     ${renderQuarterSummaryLine(
       "Est. unpaid",
       `$${fmtMoney(estUnpaidAmount)}`,
       estUnpaidCls,
       TIP_EST_IRS_EST_UNPAID,
     )}
     ${renderQuarterSummaryLine(
       "IRS opportunity",
       `$${fmtMoney(interest.irsOpportunity)}`,
       irsOpportunityCls,
       TIP_EST_IRS_OPPORTUNITY,
       "",
       { kind: "opportunity", scope, quarterIndex },
     )}`,
    groupStatusBadge,
    "sfe-tax-estimate-quarter-total-tax-card",
    scope === "annual",
  );

  return `
    <aside class="kpi-row sfe-tax-w2-totals-cards sfe-tax-estimate-quarter-summary-cards">
      ${incomeCard}
      ${taxPaymentsCard}
      ${totalTaxCard}
    </aside>`;
}

function computeAnnualInterest(
  quarterInterests: TaxEstimateQuarterInterest[],
): TaxEstimateAnnualInterest {
  return {
    irsInterest: quarterInterests.reduce((sum, row) => sum + row.irsInterest, 0),
    irsOpportunity: quarterInterests.reduce((sum, row) => sum + row.irsOpportunity, 0),
  };
}

function renderTaxEstimateQuarterTotalsCards(
  settings: TaxSettings,
  year: TaxYear,
  quarterIndex: number,
  monthRows: TaxEstimateMonthRow[],
  totals: TaxEstimateQuarterTotals,
  priorTotals: TaxEstimateQuarterTotals | null,
): string {
  const forecast = isTaxMonthForecast(year, quarterIndex * 3 + 2);
  const interest = computeQuarterInterest(
    settings,
    year,
    quarterIndex,
    totals,
    priorTotals,
    monthRows,
  );
  return renderTaxEstimateSummaryCards(
    year,
    "quarter",
    quarterIndex,
    totals,
    interest,
    forecast,
  );
}

function renderTaxEstimateQuarterTable(
  year: TaxYear,
  quarterIndex: number,
  monthRows: TaxEstimateMonthRow[],
  y: TaxYearInputs,
): string {
  const bodyRows = monthRows.map((row) => renderTaxEstimateMonthRow(year, row, y)).join("");
  return `
    <div class="sfe-tax-paycheck-table-wrap sfe-tax-estimate-table-wrap">
      <table class="sfe-tax-paycheck-table sfe-tax-estimate-table sfe-tax-estimate-table-standard">
        <colgroup>
          <col class="sfe-tax-paycheck-col-lead" />
          <col class="sfe-tax-paycheck-col-month-name" />
          <col class="sfe-tax-estimate-col-money sfe-tax-estimate-col-income" span="4" />
          <col class="sfe-tax-estimate-col-money sfe-tax-estimate-col-tax" span="2" />
          <col class="sfe-tax-estimate-col-money sfe-tax-estimate-col-payment" span="4" />
          <col class="sfe-tax-estimate-col-safe" />
        </colgroup>
        <thead>
          ${renderTaxEstimateTableHead()}
        </thead>
        <tbody>
          ${bodyRows}
        </tbody>
      </table>
      ${renderMobileTaxEstimateTransposeTable(year, monthRows)}
    </div>`;
}

function renderMobileTaxEstimateTransposeTable(
  year: TaxYear,
  monthRows: TaxEstimateMonthRow[],
): string {
  const monthHeaders = monthRows
    .map((row) => {
      const forecast = isTaxMonthForecast(year, row.monthIndex);
      const status = forecast ? "forecast" : "fact";
      const statusLabel = forecast ? "Forecast" : "Fact";
      return `
        <th scope="col" class="sfe-tax-estimate-transpose-month">
          <span>${MONTH_LABELS[row.monthIndex]}</span>
          <span class="sfe-tax-month-status sfe-tax-month-status-${status}">${statusLabel}</span>
        </th>`;
    })
    .join("");

  const renderRow = (
    label: string,
    tooltip: string,
    cells: (row: TaxEstimateMonthRow) => string,
    headerAction = "",
  ): string => `
    <tr>
      <th scope="row" class="sfe-tax-estimate-transpose-label">
        <span class="sfe-tax-estimate-transpose-label-content">${renderTaxColHead(label, tooltip)}${headerAction}</span>
      </th>
      ${monthRows.map(cells).join("")}
    </tr>`;

  const estimatedTaxPaidAction = `
    <button
      type="button"
      class="sfe-tax-estimate-paid-btn sfe-tax-estimate-paid-header-btn"
      data-month="${monthRows[0]?.monthIndex ?? 0}"
      aria-label="Manage estimated tax payments"
      title="Manage payments"
    >
      <span class="sfe-tax-estimate-paid-manage" aria-hidden="true">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="9" height="9">
          <path fill="currentColor" d="M11.013 1.427a1.75 1.75 0 0 1 2.474 0l1.086 1.086a1.75 1.75 0 0 1 0 2.474l-8.986 8.986a1 1 0 0 1-.446.261l-3.25 1.083a.25.25 0 0 1-.316-.316l1.083-3.25a1 1 0 0 1 .261-.446l8.986-8.986z"/>
        </svg>
      </span>
    </button>`;

  return `
    <table class="sfe-tax-estimate-mobile-only sfe-tax-estimate-transposed">
      <thead>
        <tr>
          <th scope="col" class="sfe-tax-estimate-transpose-corner">Metric</th>
          ${monthHeaders}
        </tr>
      </thead>
      <tbody>
        ${renderRow("Trading Income", "Trading income, with W-2 income shown below.", (row) =>
          renderMobileEstimateTransposeCell(
            row.tradingIncome,
            isTaxMonthForecast(year, row.monthIndex),
            "W-2",
            row.w2Income,
            " sfe-tax-estimate-transpose-income-total",
          ),
        )}
        ${renderRow("FIFO Income", "FIFO income, with total cash income shown below.", (row) =>
          renderMobileEstimateTransposeCell(
            row.fifoTotal,
            isTaxMonthForecast(year, row.monthIndex),
            "Total",
            row.totalIncome,
          ),
        )}
        ${renderRow("Tax due", "Tax due for this month, with this month's safe-harbour target shown below.", (row) =>
          renderMobileEstimateTransposeCell(
            row.monthlyTaxDue,
            isTaxMonthForecast(year, row.monthIndex),
            "Saf.",
            row.monthlySafeHarborTarget,
          ),
        )}
        ${renderRow("W2 Tax Paid", TIP_EST_W2_TAX, (row) =>
          renderMobileEstimateTransposeCell(
            row.w2TaxPaid,
            isTaxMonthForecast(year, row.monthIndex),
          ),
        )}
        ${renderRow("Est Tax Paid", TIP_EST_EST_PAID, (row) =>
          renderMobileEstimateTransposeCell(
            row.estimatedTaxPaid,
            isTaxMonthForecast(year, row.monthIndex),
          ),
          estimatedTaxPaidAction,
        )}
        ${renderRow("Other Income", TIP_EST_OTHER_INCOME, (row) =>
          renderMobileEstimateTransposeCell(
            row.otherIncome,
            isTaxMonthForecast(year, row.monthIndex),
          ),
        )}
        ${renderRow("Other Tax", TIP_EST_OTHER_TAX, (row) =>
          renderMobileEstimateTransposeCell(
            row.otherTaxPaid,
            isTaxMonthForecast(year, row.monthIndex),
          ),
        )}
        ${renderRow("Total Tax Paid", "W-2 withholding plus other tax plus estimated tax payments in this month.", (row) =>
          renderMobileEstimateTransposeCell(
            row.monthlyTotalTaxPaid,
            isTaxMonthForecast(year, row.monthIndex),
          ),
        )}
      </tbody>
    </table>`;
}

function renderTaxEstimateQuarterSummary(
  quarterIndex: number,
  totals: TaxEstimateQuarterTotals,
): string {
  const remainText = `$${fmtMoney(totals.quarterSafeHarborRemaining)}`;
  const remainCls = estimateSafeHarborRemainClass(totals.quarterSafeHarborRemaining);
  const safeMark = totals.safeHarborMet
    ? `<span class="sfe-tax-estimate-quarter-summary-safe" title="Safe harbor met">✓</span>`
    : "";
  return `<summary class="sfe-tax-section-h sfe-tax-estimate-quarter-summary">
      <span class="insight-h sfe-tax-section-summary-title">
        <span class="sfe-tax-section-summary-title-row">
          <span class="sfe-tax-section-summary-title-text">${esc(QUARTER_LABELS[quarterIndex])}</span>
        </span>
      </span>
      <span class="sfe-tax-section-summary-totals sfe-tax-estimate-quarter-summary-totals">
        FIFO $${fmtMoney(totals.fifoTotal)} · Tax due $${fmtMoney(totals.quarterTaxDue)} · Paid $${fmtMoney(totals.quarterTotalPaid)} · Safe harb <span class="sfe-tax-estimate-quarter-summary-remain${remainCls}">${remainText}</span>${safeMark}
      </span>
    </summary>`;
}

function renderTaxEstimateQuarterBlock(
  settings: TaxSettings,
  year: TaxYear,
  quarterIndex: number,
  monthRows: TaxEstimateMonthRow[],
  totals: TaxEstimateQuarterTotals,
  priorTotals: TaxEstimateQuarterTotals | null,
  y: TaxYearInputs,
  collapsedSections: ReadonlySet<string>,
): string {
  const sectionKey = quarterSectionKey(quarterIndex);
  const quarterMonthRows = monthRows.slice(quarterIndex * 3, quarterIndex * 3 + 3);
  return `
    <details class="collapsible sfe-tax-section sfe-tax-estimate-quarter-collapsible sfe-tax-collapsible" data-tax-section="${sectionKey}"${collapsedSections.has(sectionKey) ? "" : " open"}>
      ${renderTaxEstimateQuarterSummary(quarterIndex, totals)}
      <div class="sfe-tax-estimate-quarter-body">
        <section class="sfe-tax-paycheck-table-card sfe-tax-estimate-quarter-table-card">
          ${renderTaxEstimateQuarterTable(year, quarterIndex, quarterMonthRows, y)}
        </section>
        ${renderTaxEstimateQuarterTotalsCards(settings, year, quarterIndex, monthRows, totals, priorTotals)}
      </div>
    </details>`;
}

function q1PriorYearTaxPaid(
  settings: TaxSettings,
  year: TaxYear,
  currentInputs: TaxYearInputs,
): number {
  const yearIndex = getTaxYears().indexOf(year);
  const priorYear = year - 1;
  const sourceInputs =
    yearIndex > 0 ? yearInputs(settings, getTaxYears()[yearIndex - 1]) : currentInputs;
  const q1Start = `${year}-01-01`;
  const q1End = `${year}-04-15`;

  return sourceInputs.estimatedTaxPayments
    .filter(
      (payment) =>
        payment.date >= q1Start &&
        payment.date <= q1End &&
        (yearIndex === 0 || payment.taxYear === priorYear),
    )
    .reduce((sum, payment) => sum + payment.amount, 0);
}

function renderQ1PriorYearTaxDueLine(priorYear: number, value: number): string {
  return `
    <div class="sfe-tax-estimate-summary-line sfe-tax-q1-prior-tax-due-line">
      <span class="sfe-tax-estimate-summary-line-label">${priorYear} Tax Due:${renderTaxHint(
        `Total ${priorYear} tax due. Edit manually for Q1 cash planning.`,
      )}</span>
      <label class="sfe-tax-q1-prior-tax-input-wrap">
        <input
          type="text"
          class="sfe-tax-q1-prior-tax-input"
          data-tax-q1-prior-tax-due
          inputmode="decimal"
          value="${esc(`$${fmtMoney(value)}`)}"
          aria-label="Edit ${priorYear} Tax Due"
        />
      </label>
    </div>`;
}

function renderQ1TaxObligations(
  settings: TaxSettings,
  year: TaxYear,
  y: TaxYearInputs,
): string {
  const yearIndex = getTaxYears().indexOf(year);
  if (yearIndex < 0 || y.priorYearTotalTax == null) return "";

  const priorYear = year - 1;
  const priorYearTaxDue = y.priorYearTotalTax;
  const actualPriorYearTaxPaid = q1PriorYearTaxPaid(settings, year, y);
  const assumedApr10Payment = forecastAssumedPriorYearSettlementAmount(
    year,
    priorYearTaxDue,
    actualPriorYearTaxPaid,
  );
  const isForecastYear = year > new Date().getFullYear();
  const priorYearTaxPaid = actualPriorYearTaxPaid + assumedApr10Payment;
  const priorYearTaxBalance = priorYearTaxDue - priorYearTaxPaid;
  const paidTip = isForecastYear
    ? `Payments made from Jan 1 through Apr 15, ${year}, for ${priorYear} taxes. Forecast years assume any unpaid balance is paid on Mar 30, ${year} (Analytics Own Capital books it as a cash withdrawal).`
    : `Payments made from Jan 1 through Apr 15, ${year}, for ${priorYear} taxes.`;
  const forecastAssumptionMark = isForecastYear
    ? `<div class="sfe-tax-q1-forecast-assumption">
         <span class="sfe-tax-estimate-this-quarter-mark sfe-tax-q1-forecast-assumption-mark">Assumes unpaid ${priorYear} tax paid Mar 30, ${year}</span>
       </div>`
    : "";

  return `
    <section class="sfe-tax-q1-obligations">
      ${renderQuarterLinesCard(
        "Q1 tax obligations",
        `<span class="sfe-tax-estimate-this-quarter-mark">${year} cash plan</span>`,
        `${renderQ1PriorYearTaxDueLine(priorYear, priorYearTaxDue)}
         ${renderQuarterMoneyLine(
           `${priorYear} Tax Paid`,
           priorYearTaxPaid,
           false,
           paidTip,
         )}
         ${renderQuarterMoneyLine(
           `${priorYear} Tax Balance:`,
           priorYearTaxBalance,
           false,
           `${priorYear} Tax Due minus ${priorYear} Tax Paid.`,
           "",
           priorYearTaxBalance > 0 ? " sfe-neg" : "",
           undefined,
           true,
         )}
         ${forecastAssumptionMark}`,
        "",
        "sfe-tax-estimate-q1-obligations-card",
      )}
    </section>`;
}

function renderTaxEstimateAnnualTotals(
  settings: TaxSettings,
  year: TaxYear,
  y: TaxYearInputs,
  monthRows: TaxEstimateMonthRow[],
  quarters: TaxEstimateQuarterTotals[],
  collapsedSections: ReadonlySet<string>,
): string {
  const eoyTaxDue = computeEoyTaxDue(settings, year, y, quarters);
  const annualTotals: TaxEstimateQuarterTotals = {
    w2Income: quarters.reduce((sum, row) => sum + row.w2Income, 0),
    tradingIncome: quarters.reduce((sum, row) => sum + row.tradingIncome, 0),
    otherIncome: quarters.reduce((sum, row) => sum + row.otherIncome, 0),
    totalIncome: quarters.reduce((sum, row) => sum + row.totalIncome, 0),
    fifoTotal: quarters.reduce((sum, row) => sum + row.fifoTotal, 0),
    cumulativeW2Income: quarters[3].cumulativeW2Income,
    cumulativeFifoTrading: quarters[3].cumulativeFifoTrading,
    cumulativeFifoTotal: quarters[3].cumulativeFifoTotal,
    fifoTaxDue: eoyTaxDue,
    cumulativeW2TaxPaid: quarters[3].cumulativeW2TaxPaid,
    cumulativeEstTaxPaid: quarters[3].cumulativeEstTaxPaid,
    fifoTaxRemaining: eoyTaxDue - quarters[3].totalTaxPaid,
    safeHarborTarget: quarters[3].safeHarborTarget,
    totalTaxDue: eoyTaxDue,
    w2TaxPaid: quarters.reduce((sum, row) => sum + row.w2TaxPaid, 0),
    otherTaxPaid: quarters.reduce((sum, row) => sum + row.otherTaxPaid, 0),
    estimatedTaxPaid: quarters.reduce((sum, row) => sum + row.estimatedTaxPaid, 0),
    totalTaxPaid: quarters[3].totalTaxPaid,
    safeHarborRemaining: quarters[3].safeHarborRemaining,
    safeHarborMet: quarters[3].safeHarborMet,
    quarterSafeHarborTarget: quarters[3].safeHarborTarget,
    quarterTaxDue: eoyTaxDue,
    quarterTotalPaid: quarters[3].totalTaxPaid,
    quarterSafeHarborRemaining: quarters[3].safeHarborRemaining,
    quarterTaxRemaining: eoyTaxDue - quarters[3].totalTaxPaid,
  };
  const quarterInterests = quarters.map((totals, quarterIndex) =>
    computeQuarterInterest(
      settings,
      year,
      quarterIndex,
      totals,
      quarterIndex > 0 ? quarters[quarterIndex - 1] : null,
      monthRows,
    ),
  );
  const annualInterest = computeAnnualInterest(quarterInterests);
  const remainText = `$${fmtMoney(annualTotals.safeHarborRemaining)}`;
  const remainCls = estimateSafeHarborRemainClass(annualTotals.safeHarborRemaining);
  const safeMark = annualTotals.safeHarborMet
    ? `<span class="sfe-tax-estimate-quarter-summary-safe" title="Safe harbor met">✓</span>`
    : "";

  return `
    <details class="collapsible sfe-tax-section sfe-tax-estimate-quarter-collapsible sfe-tax-estimate-annual-collapsible sfe-tax-collapsible" data-tax-section="annual"${collapsedSections.has("annual") ? "" : " open"}>
      <summary class="sfe-tax-section-h sfe-tax-estimate-quarter-summary">
        <span class="insight-h sfe-tax-section-summary-title">
          <span class="sfe-tax-section-summary-title-row">
            <span class="sfe-tax-section-summary-title-text">Annual totals</span>
          </span>
        </span>
        <span class="sfe-tax-section-summary-totals sfe-tax-estimate-quarter-summary-totals">
          FIFO $${fmtMoney(annualTotals.fifoTotal)} · Tax due $${fmtMoney(annualTotals.totalTaxDue)} · Paid $${fmtMoney(annualTotals.totalTaxPaid)} · Safe harb <span class="sfe-tax-estimate-quarter-summary-remain${remainCls}">${remainText}</span>${safeMark}
        </span>
      </summary>
      <div class="sfe-tax-estimate-quarter-body sfe-tax-estimate-annual-body">
        ${renderQ1TaxObligations(settings, year, y)}
        ${renderTaxEstimateSummaryCards(
          year,
          "annual",
          3,
          annualTotals,
          annualInterest,
          false,
        )}
      </div>
    </details>`;
}

function renderTaxEstimateMonthRow(
  year: TaxYear,
  row: TaxEstimateMonthRow,
  y: TaxYearInputs,
): string {
  const forecast = isTaxMonthForecast(year, row.monthIndex);
  const status = forecast ? "forecast" : "fact";
  const statusLabel = forecast ? "Forecast" : "Fact";
  const month = MONTH_LABELS[row.monthIndex];
  return `
    <tr class="sfe-tax-paycheck-summary-row sfe-tax-estimate-row sfe-tax-paycheck-summary-row-${status}">
      ${renderTradingMonthCells(month, status, statusLabel)}
      ${renderEstimateMoneyCell(row.w2Income, forecast, `${EST_SECTION_INCOME}${EST_SECTION_START} sfe-tax-estimate-desktop-only`)}
      ${renderEstimateMoneyCell(row.tradingIncome, forecast, `${EST_SECTION_INCOME} sfe-tax-estimate-desktop-only`)}
      ${renderEstimateMoneyCell(row.totalIncome, forecast, `${EST_SECTION_INCOME} sfe-tax-estimate-total-col-dim sfe-tax-estimate-desktop-only`)}
      ${renderEstimateMoneyCell(row.fifoTotal, forecast, `${EST_SECTION_INCOME}${EST_SECTION_END} sfe-tax-estimate-fifo-total-col sfe-tax-estimate-desktop-only`)}
      ${renderEstimateMoneyCell(row.safeHarborTarget, forecast, `${EST_SECTION_TAX}${EST_SECTION_START} sfe-tax-estimate-cumulative-cell sfe-tax-estimate-desktop-only`)}
      ${renderEstimateMoneyCell(row.totalTaxDue, forecast, `${EST_SECTION_TAX}${EST_SECTION_END} sfe-tax-estimate-cumulative-cell sfe-tax-estimate-desktop-only`)}
      ${renderEstimateMoneyCell(row.w2TaxPaid, forecast, `${EST_SECTION_PAYMENTS}${EST_SECTION_START}`)}
      ${renderEstimatePaidCell(year, row.monthIndex, row.estimatedTaxPaid, EST_SECTION_PAYMENTS)}
      ${renderEstimateMoneyCell(row.totalTaxPaid, forecast, `${EST_SECTION_PAYMENTS} sfe-tax-estimate-cumulative-cell`)}
      ${renderEstimateSafeHarborRemainCell(row.safeHarborRemaining, `${EST_SECTION_PAYMENTS}${EST_SECTION_END} sfe-tax-estimate-cumulative-cell`)}
      ${renderSafeHarborMetCell(row.safeHarborMet)}
    </tr>`;
}

function renderTaxEstimateTableHead(): string {
  return `
    <tr class="sfe-tax-estimate-super-head sfe-tax-estimate-desktop-only">
      <th scope="col" colspan="2" class="sfe-tax-paycheck-col-head sfe-tax-estimate-super-head-corner" aria-hidden="true"></th>
      <th scope="col" colspan="4" class="sfe-tax-paycheck-col-head sfe-tax-estimate-super-head-group sfe-tax-estimate-super-head-group--income">Income</th>
      <th scope="col" colspan="2" class="sfe-tax-paycheck-col-head sfe-tax-estimate-super-head-group sfe-tax-estimate-super-head-group--tax">Tax estimate</th>
      <th scope="col" colspan="4" class="sfe-tax-paycheck-col-head sfe-tax-estimate-super-head-group sfe-tax-estimate-super-head-group--payments">Payments</th>
      <th scope="col" class="sfe-tax-paycheck-col-head sfe-tax-estimate-super-head-safe">Safe</th>
    </tr>
    <tr class="sfe-tax-estimate-sub-head sfe-tax-estimate-desktop-only">
      <th scope="col" class="sfe-tax-paycheck-col-head sfe-tax-paycheck-col-head-lead sfe-tax-estimate-sub-head-lead" aria-hidden="true"></th>
      <th scope="col" class="sfe-tax-paycheck-col-head sfe-tax-paycheck-col-head-month sfe-tax-estimate-sub-head-month">Month</th>
      <th scope="col" class="sfe-tax-paycheck-col-head sfe-tax-paycheck-col-head-money sfe-tax-paycheck-col-head-with-hint sfe-tax-estimate-section-income sfe-tax-estimate-section-start">${renderTaxColHead("W-2", TIP_EST_W2)}</th>
      <th scope="col" class="sfe-tax-paycheck-col-head sfe-tax-paycheck-col-head-money sfe-tax-paycheck-col-head-with-hint sfe-tax-estimate-section-income">${renderTaxColHead("Trading", TIP_EST_TRADING)}</th>
      <th scope="col" class="sfe-tax-paycheck-col-head sfe-tax-paycheck-col-head-money sfe-tax-paycheck-col-head-with-hint sfe-tax-estimate-section-income">${renderTaxColHead("Total", TIP_EST_TOTAL)}</th>
      <th scope="col" class="sfe-tax-paycheck-col-head sfe-tax-paycheck-col-head-money sfe-tax-paycheck-col-head-with-hint sfe-tax-estimate-section-income sfe-tax-estimate-section-end sfe-tax-estimate-fifo-total-col">${renderTaxColHead("FIFO total", TIP_EST_FIFO_TOTAL)}</th>
      <th scope="col" class="sfe-tax-paycheck-col-head sfe-tax-paycheck-col-head-money sfe-tax-paycheck-col-head-with-hint sfe-tax-estimate-section-tax sfe-tax-estimate-section-start">${renderTaxColHead("Safe harbor", TIP_EST_SAFE_HARBOR)}</th>
      <th scope="col" class="sfe-tax-paycheck-col-head sfe-tax-paycheck-col-head-money sfe-tax-paycheck-col-head-with-hint sfe-tax-estimate-section-tax sfe-tax-estimate-section-end">${renderTaxColHead("Tax due", TIP_EST_TAX_DUE)}</th>
      <th scope="col" class="sfe-tax-paycheck-col-head sfe-tax-paycheck-col-head-money sfe-tax-paycheck-col-head-with-hint sfe-tax-estimate-section-payments sfe-tax-estimate-section-start">${renderTaxColHead("W-2 paid", TIP_EST_W2_TAX)}</th>
      <th scope="col" class="sfe-tax-paycheck-col-head sfe-tax-paycheck-col-head-money sfe-tax-paycheck-col-head-with-hint sfe-tax-estimate-section-payments">${renderTaxColHead("Est. paid", TIP_EST_EST_PAID)}</th>
      <th scope="col" class="sfe-tax-paycheck-col-head sfe-tax-paycheck-col-head-money sfe-tax-paycheck-col-head-with-hint sfe-tax-estimate-section-payments">${renderTaxColHead("Total paid", TIP_EST_TOTAL_PAID)}</th>
      <th scope="col" class="sfe-tax-paycheck-col-head sfe-tax-paycheck-col-head-money sfe-tax-paycheck-col-head-with-hint sfe-tax-estimate-section-payments sfe-tax-estimate-section-end">${renderTaxColHead("Safe harbour balance", TIP_EST_REMAINING)}</th>
      <th scope="col" class="sfe-tax-paycheck-col-head sfe-tax-estimate-sub-head-safe" aria-hidden="true"></th>
    </tr>
    `;
}

function renderTaxEstimateSection(
  settings: TaxSettings,
  year: TaxYear,
  y: TaxYearInputs,
  tradingIncomeMonths: number[],
  tradingFifoMonths: number[],
  tradingIncomeLoading: boolean,
  collapsedSections: ReadonlySet<string>,
): string {
  const schedule = computeTaxEstimateSchedule(
    settings,
    year,
    y,
    tradingIncomeMonths,
    tradingFifoMonths,
  );

  const bodyRows = tradingIncomeLoading
    ? `<div class="sfe-tax-estimate-loading muted small">Loading from own ledger…</div>`
    : renderTaxEstimateAnnualTotals(
        settings,
        year,
        y,
        schedule.months,
        schedule.quarters,
        collapsedSections,
      ) +
        [3, 2, 1, 0]
          .map((quarterIndex) => {
            return renderTaxEstimateQuarterBlock(
              settings,
              year,
              quarterIndex,
              schedule.months,
              schedule.quarters[quarterIndex],
              quarterIndex > 0 ? schedule.quarters[quarterIndex - 1] : null,
              y,
              collapsedSections,
            );
          })
          .join("");

  return `
    <div class="sfe-tax-estimate-quarters">
      ${bodyRows}
    </div>`;
}

function renderPersonPaycheckTable(
  person: "anton" | "olha",
  name: string,
  year: TaxYear,
  paycheckYear: TaxPaycheckYear,
  noIncomeMonths: boolean[],
  expandedRows: ReadonlySet<string>,
  allowNameEdit: boolean,
  useMonthEditor: boolean,
): string {
  return `
      <section class="sfe-tax-paycheck-table-card">
        <header class="sfe-tax-paycheck-table-head">
          <div class="sfe-tax-paycheck-table-title">
            <div class="sfe-tax-section-header-text">
              <span class="sfe-tax-section-header-title-row">
                <span class="insight-h">${esc(name)}</span>
                ${
                  allowNameEdit
                    ? `<button type="button" class="sfe-tax-person-name-edit-btn" data-tax-person-name-edit="${person}" aria-label="Edit ${esc(name)} name" title="Edit name">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="10" height="10" aria-hidden="true">
                          <path fill="currentColor" d="M11.013 1.427a1.75 1.75 0 0 1 2.474 0l1.086 1.086a1.75 1.75 0 0 1 0 2.474l-8.986 8.986a1 1 0 0 1-.446.261l-3.25 1.083a.25.25 0 0 1-.316-.316l1.083-3.25a1 1 0 0 1 .261-.446l8.986-8.986z"/>
                        </svg>
                      </button>`
                    : ""
                }
              </span>
            </div>
          </div>
        </header>
        <div class="sfe-tax-paycheck-table-wrap">
        <table class="sfe-tax-paycheck-table">
          ${renderPaycheckSummaryColgroup()}
          <thead>
            ${renderPaycheckSummaryHead("Gross", useMonthEditor)}
          </thead>
          <tbody>
            ${MONTH_LABELS.map((month, monthIndex) => {
              const paychecks = paycheckYear[monthIndex];
              const noIncome = noIncomeMonths[monthIndex] === true;
              const totals = paycheckMonthTotals(paychecks, noIncome);
              const rowKey = `${person}-${monthIndex}`;
              const expanded = expandedRows.has(rowKey);
              const monthStatus = taxMonthStatus(year, monthIndex);
              const statusLabel = taxMonthStatusLabel(monthStatus);
              const grossDisplay = noIncome ? "—" : `$${fmtMoney(totals.grossIncome)}`;
              const taxDisplay = noIncome ? "—" : `$${fmtMoney(totals.totalTaxPaid)}`;
              return `
                <tr class="sfe-tax-paycheck-summary-row sfe-tax-paycheck-summary-row-${monthStatus}${noIncome ? " sfe-tax-paycheck-summary-row-none" : ""}${expanded ? " sfe-tax-paycheck-summary-row-open" : ""}" data-person="${person}" data-month="${monthIndex}">
                  <td class="sfe-tax-paycheck-summary-cell sfe-tax-paycheck-summary-cell-lead">
                    <button
                      type="button"
                      class="sfe-tax-paycheck-row-toggle sfe-tax-paycheck-row-toggle-lead"
                      data-person="${person}"
                      data-month="${monthIndex}"
                      aria-expanded="${expanded}"
                      aria-label="${expanded ? "Collapse" : "Expand"} ${month} paychecks"
                    >
                      <span class="sfe-tax-paycheck-row-lead">
                        <span class="sfe-tax-month-status sfe-tax-month-status-${monthStatus}">${statusLabel}</span>
                        <svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true">
                          <path fill="currentColor" d="M5.3 2.8 10.5 8l-5.2 5.2 1.4 1.4L13.3 8 6.7 1.4z"/>
                        </svg>
                      </span>
                    </button>
                  </td>
                  <td class="sfe-tax-paycheck-summary-cell sfe-tax-paycheck-summary-cell-month-name">
                    <button
                      type="button"
                      class="sfe-tax-paycheck-row-toggle sfe-tax-paycheck-row-toggle-month"
                      data-person="${person}"
                      data-month="${monthIndex}"
                      aria-expanded="${expanded}"
                      aria-label="${expanded ? "Collapse" : "Expand"} ${month} paychecks"
                    >
                      <span class="sfe-tax-paycheck-month-name">${month}</span>
                    </button>
                  </td>
                  <td class="sfe-tax-paycheck-summary-cell sfe-tax-paycheck-summary-cell-copy">
                    ${
                      useMonthEditor
                        ? renderEditPaycheckMonthButton(person, monthIndex)
                        : renderCopyPaycheckButton(
                            "sfe-tax-copy-paycheck-btn sfe-tax-copy-paycheck-month",
                            monthIndex === 0
                              ? "No previous month"
                              : `Copy paychecks from ${MONTH_LABELS[monthIndex - 1]}`,
                            monthIndex === 0,
                            { person, month: monthIndex },
                          )
                    }
                  </td>
                  <td class="kpi-v sfe-tax-paycheck-month-value sfe-tax-paycheck-summary-cell sfe-tax-paycheck-summary-cell-money${noIncome || totals.grossIncome <= 0 ? "" : " roi-strong"}">${grossDisplay}</td>
                  <td class="kpi-v sfe-tax-paycheck-month-value sfe-tax-paycheck-month-tax sfe-tax-paycheck-summary-cell sfe-tax-paycheck-summary-cell-money${noIncome || totals.totalTaxPaid <= 0 ? "" : " sfe-neg"}">${taxDisplay}</td>
                  <td class="sfe-tax-paycheck-summary-cell sfe-tax-paycheck-no-income-cell">
                    ${renderNoIncomeCheckbox(
                      noIncome,
                      false,
                      "sfe-tax-no-income-input",
                      { person, month: monthIndex },
                      "",
                      "None",
                    )}
                  </td>
                </tr>
                <tr class="sfe-tax-paycheck-editor-row sfe-tax-paycheck-editor-row-${monthStatus}" ${expanded ? "" : "hidden"}>
                  <td colspan="6">
                    ${renderPaycheckEditorPanel(person, monthIndex, paychecks, noIncome, monthStatus === "forecast" ? "forecast" : "fact")}
                  </td>
                </tr>`;
            }).join("")}
          </tbody>
        </table>
      </div>
      </section>`;
}

function renderYearForm(
  settings: TaxSettings,
  year: TaxYear,
  expandedPaycheckRows: ReadonlySet<string>,
  collapsedSections: ReadonlySet<string>,
  tradingIncomeMonths: number[],
  tradingFifoMonths: number[],
  tradingIncomeLoading: boolean,
  allowW2NameEdit: boolean,
  otherIncomeEditingId: string | null,
): string {
  const y = yearInputs(settings, year);
  const antonW2 = paycheckYearTotals(y.w2PaychecksAnton, y.w2NoIncomeAnton);
  const olhaW2 = paycheckYearTotals(y.w2PaychecksOlha, y.w2NoIncomeOlha);
  const antonW2ByStatus = paycheckYearTotalsByStatus(y.w2PaychecksAnton, y.w2NoIncomeAnton, year);
  const olhaW2ByStatus = paycheckYearTotalsByStatus(y.w2PaychecksOlha, y.w2NoIncomeOlha, year);
  const householdGross = antonW2.grossIncome + olhaW2.grossIncome;
  const householdTaxPaid = antonW2.totalTaxPaid + olhaW2.totalTaxPaid;
  const tradingTotal = tradingYearTotal(tradingIncomeMonths);
  const tradingByStatus = tradingYearTotalsByStatus(tradingIncomeMonths, year);
  const otherTotals = otherIncomeYearTotals(y.otherIncomeItems);

  return `
    <details class="collapsible sfe-tax-section sfe-tax-section-w2 sfe-tax-collapsible" data-tax-section="w2"${collapsedSections.has("w2") ? "" : " open"}>
      ${renderTaxSectionSummary(
        "W-2 Income and Tax Contributions",
        year,
        `Gross $${fmtMoney(householdGross)} · Tax paid $${fmtMoney(householdTaxPaid)}`,
      )}
      <div class="sfe-tax-section-body">
      <div class="sfe-tax-w2-layout">
        <div class="sfe-tax-w2-person-tables">
          ${renderPersonPaycheckTable("anton", settings.w2PersonNames.anton, year, y.w2PaychecksAnton, y.w2NoIncomeAnton, expandedPaycheckRows, true, allowW2NameEdit)}
          ${renderPersonPaycheckTable("olha", settings.w2PersonNames.olha, year, y.w2PaychecksOlha, y.w2NoIncomeOlha, expandedPaycheckRows, true, allowW2NameEdit)}
        </div>
        ${renderW2TotalsCards(year, settings.w2PersonNames, antonW2, olhaW2, antonW2ByStatus, olhaW2ByStatus)}
      </div>
      </div>
    </details>

    <details class="collapsible sfe-tax-section sfe-tax-section-other-income sfe-tax-collapsible" data-tax-section="other-income"${collapsedSections.has("other-income") ? "" : " open"}>
      ${renderTaxSectionSummary(
        "Other Income &amp; Tax Contributions",
        year,
        `Trading $${fmtMoney(tradingTotal)} · Other $${fmtMoney(otherTotals.amount)} · Other tax $${fmtMoney(otherTotals.taxContributed)}`,
      )}
      <div class="sfe-tax-section-body">
      <div class="sfe-tax-w2-layout sfe-tax-other-income-layout">
        ${renderTradingIncomeTable(year, tradingIncomeMonths, tradingFifoMonths, tradingIncomeLoading)}
        <div class="sfe-tax-other-income-side">
          ${renderOtherIncomeList(settings.w2PersonNames, year, y.otherIncomeItems, otherIncomeEditingId)}
          ${renderOtherIncomeSectionTotals(year, tradingTotal, tradingByStatus, tradingFifoMonths, y.otherIncomeItems)}
        </div>
      </div>
      </div>
    </details>

    <details class="collapsible sfe-tax-section sfe-tax-section-calc sfe-tax-collapsible" data-tax-section="estimate"${collapsedSections.has("estimate") ? "" : " open"}>
      ${renderTaxSectionSummary(
        "Tax estimate and payments",
        year,
        (() => {
          const schedule = computeTaxEstimateSchedule(
            settings,
            year,
            y,
            tradingIncomeMonths,
            tradingFifoMonths,
          );
          const eoyTaxDue = computeEoyTaxDue(settings, year, y, schedule.quarters);
          const yearEnd = schedule.months[11];
          return tradingIncomeLoading
            ? "Loading trading income…"
            : `Tax due $${fmtMoney(eoyTaxDue)} · Paid $${fmtMoney(yearEnd.totalTaxPaid)} · Safe harb $${fmtMoney(yearEnd.safeHarborRemaining)}`;
        })(),
      )}
      <div class="sfe-tax-section-body">
      ${renderTaxEstimateSection(
        settings,
        year,
        y,
        tradingIncomeMonths,
        tradingFifoMonths,
        tradingIncomeLoading,
        collapsedSections,
      )}
      </div>
    </details>`;
}

let taxScopeBound = false;

export class TaxController {
  private panel: HTMLElement | null = null;
  private settings: TaxSettings = loadTaxSettings();
  private selectedYear: TaxYear = currentTaxYear();
  private expandedPaycheckRows = new Set<string>();
  private collapsedTaxSections = loadTaxCollapsedSections();
  private serverHydrationStarted = false;
  private localRevision = 0;
  private tradingIncomeMonths: number[] = MONTH_LABELS.map(() => 0);
  private tradingFifoMonths: number[] = MONTH_LABELS.map(() => 0);
  private tradingIncomeLoading = false;
  private tradingIncomeLoadedYear: TaxYear | null = null;
  private tradingIncomeLoadingYear: TaxYear | null = null;
  private priorYearTradingIncome: number[] = zeroTradingMonths();
  private priorYearTradingFifo: number[] = zeroTradingMonths();
  private priorYearTradingLoadedYear: TaxYear | null = null;
  private priorYearTradingLoadingYear: TaxYear | null = null;
  private estPaymentsModalOpen = false;
  private estPaymentsModalMonth: number | null = null;
  private estPaymentsEditingId: string | null = null;
  private estPaymentsDraftDate = "";
  private estPaymentsDraftAmount = "";
  private estPaymentsDraftKind: TaxPaymentKind = "estimated";
  private estPaymentsDraftNote = "";
  private estPaymentsDraftFromOwnCapital = true;
  private estPaymentsEscapeHandler: ((event: KeyboardEvent) => void) | null = null;
  private otherIncomeModalOpen = false;
  private otherIncomeEditingId: string | null = null;
  private otherIncomeDraftDate = "";
  private otherIncomeDraftAmount = "";
  private otherIncomeDraftTax = "";
  private otherIncomeDraftPerson: TaxPersonId = "anton";
  private otherIncomeDraftNote = "";
  private otherIncomeEscapeHandler: ((event: KeyboardEvent) => void) | null = null;
  private irsOpportunityModalOpen = false;
  private irsOpportunityModalScope: "quarter" | "annual" = "quarter";
  private irsOpportunityModalQuarter = 0;
  private irsOpportunityEscapeHandler: ((event: KeyboardEvent) => void) | null = null;
  private irsInterestModalOpen = false;
  private irsInterestModalScope: "quarter" | "annual" = "quarter";
  private irsInterestModalQuarter = 0;
  private irsInterestEscapeHandler: ((event: KeyboardEvent) => void) | null = null;
  private safeHarborModalOpen = false;
  private safeHarborModalScope: "quarter" | "annual" = "quarter";
  private safeHarborModalQuarter = 0;
  private safeHarborEscapeHandler: ((event: KeyboardEvent) => void) | null = null;
  private modalHost: HTMLElement | null = null;
  private taxForecastEnabled = false;

  private ensureTaxModalHost(): HTMLElement {
    if (this.modalHost?.isConnected) return this.modalHost;
    let host = document.getElementById(TAX_MODAL_HOST_ID);
    if (!(host instanceof HTMLElement)) {
      host = document.createElement("div");
      host.id = TAX_MODAL_HOST_ID;
      host.setAttribute(CUSTOM_PANEL_MARKER_ATTR, "true");
      document.body.appendChild(host);
    }
    this.modalHost = host;
    return host;
  }

  private openBracketEditor(): void {
    const host = this.ensureTaxModalHost();
    host.querySelector(".sfe-tax-brackets-modal")?.closest(".sfe-modal-overlay")?.remove();
    const year = this.selectedYear;
    const yearKey = String(year);
    const brackets = taxBracketsForYear(this.settings, year).map((bracket) => ({ ...bracket }));
    const hasPreviousYear = getTaxYears().indexOf(year) > 0;
    const copiesPrevious =
      hasPreviousYear && Boolean(this.settings.copyBracketsFromPrevious[yearKey]);
    const isMobile = this.panel?.classList.contains("sfe-mobile-tax") === true;
    const showCopyPrevious = hasPreviousYear || isMobile;
    const rows = brackets
      .map(
        (bracket, index) => `
          <div class="sfe-tax-bracket-modal-row">
            <label class="sfe-tax-bracket-modal-field"><input name="rate-${index}" value="${esc(`${bracket.ratePct}%`)}" inputmode="decimal" aria-label="Rate for bracket ${index + 1}" required /></label>
            <label class="sfe-tax-bracket-modal-field">
              <input name="from-${index}" value="${esc(fmtWholeMoney(bracket.from))}" inputmode="decimal" aria-label="From amount for bracket ${index + 1}" required />
            </label>
            <label class="sfe-tax-bracket-modal-field">
              <input name="to-${index}" value="${bracket.to == null ? "" : esc(fmtWholeMoney(bracket.to))}" placeholder="∞" inputmode="decimal" aria-label="To amount for bracket ${index + 1}" />
            </label>
          </div>`,
      )
      .join("");
    const currentYearInputs = yearInputs(this.settings, year);
    const canPickSafeHarbor = this.canPickPriorYearSafeHarbor(year, currentYearInputs.pickPriorYearTotalTax);
    const safeHarborPickTitle = !hasPreviousYear
      ? "No previous tax year is available"
      : !canPickSafeHarbor
        ? "Last year total tax is $0"
        : "";
    const parametersSection = isMobile
      ? `
        <fieldset class="sfe-tax-bracket-modal-parameters">
          <legend>Card parameters</legend>
          <label class="sfe-tax-safe-harbor-copy"${canPickSafeHarbor ? "" : ` aria-disabled="true"${safeHarborPickTitle ? ` title="${esc(safeHarborPickTitle)}"` : ""}`}>
            <input type="checkbox" name="pick-safe-harbor"${hasPreviousYear && currentYearInputs.pickPriorYearTotalTax ? " checked" : ""}${canPickSafeHarbor ? "" : " disabled"} />
            <span>Use previous year tax for safe harbour</span>
          </label>
          <div class="sfe-tax-bracket-modal-parameters-grid">
            <label class="sfe-tax-bracket-modal-parameter">
              <span>${year - 1} total tax</span>
              <input name="prior-year-tax" value="${currentYearInputs.priorYearTotalTax == null ? "" : esc(`$${fmtMoney(currentYearInputs.priorYearTotalTax)}`)}" inputmode="decimal" />
            </label>
            <label class="sfe-tax-bracket-modal-parameter">
              <span>Standard deduction</span>
              <input name="standard-deduction" value="${esc(`$${fmtMoney(this.settings.defaultStandardDeduction)}`)}" inputmode="decimal" required />
            </label>
            <label class="sfe-tax-bracket-modal-parameter">
              <span>IRS interest (Ann)</span>
              <input name="irs-interest" value="${esc(`${this.settings.irsInterestAprPct}%`)}" inputmode="decimal" required />
            </label>
            <label class="sfe-tax-bracket-modal-parameter">
              <span>IRS OPPTY</span>
              <input name="irs-opportunity" value="${esc(`${this.settings.irsOpportunityMonthlyPct}%`)}" inputmode="decimal" required />
            </label>
          </div>
        </fieldset>`
      : "";
    host.insertAdjacentHTML(
      "beforeend",
      `<div class="sfe-modal-overlay sfe-modal-open" data-tax-brackets-overlay>
        <div class="sfe-modal sfe-tax-brackets-modal" role="dialog" aria-modal="true" aria-label="${isMobile ? "Edit tax settings" : "Edit tax brackets"}">
          <div class="sfe-modal-header"><h3>${isMobile ? "Tax settings" : "Tax rates"} ${year}</h3><button type="button" class="sfe-modal-close" data-tax-brackets-close aria-label="Close">×</button></div>
          <div class="sfe-tax-brackets-modal-body">
            <p class="sfe-tax-brackets-modal-note">${isMobile ? "Edit tax rates and card parameters, then save." : "Edit all brackets together, then save."}</p>
            <form class="sfe-tax-bracket-modal-form">
              ${showCopyPrevious ? `<label class="sfe-tax-copy-rates"${hasPreviousYear ? "" : ' aria-disabled="true" title="No previous tax year is available"'}><input type="checkbox" name="copy-previous"${copiesPrevious ? " checked" : ""}${hasPreviousYear ? "" : " disabled"} /><span>Copy from last year</span></label>` : ""}
              <div data-tax-bracket-modal-fields role="table" aria-label="Tax brackets">
                <div class="sfe-tax-bracket-modal-table-head" role="row">
                  <span role="columnheader">Rate</span>
                  <span role="columnheader">From</span>
                  <span role="columnheader">To</span>
                </div>
                ${rows}
              </div>
              ${parametersSection}
              <div class="sfe-modal-actions"><button type="button" class="sfe-btn sfe-tax-brackets-cancel" data-tax-brackets-close>Cancel</button><button type="submit" class="sfe-btn sfe-btn-primary">${isMobile ? "Save settings" : "Save rates"}</button></div>
            </form>
          </div>
        </div>
      </div>`,
    );
    const overlay = host.querySelector<HTMLElement>("[data-tax-brackets-overlay]");
    const close = () => overlay?.remove();
    overlay?.querySelectorAll<HTMLElement>("[data-tax-brackets-close]").forEach((button) => button.addEventListener("click", close));
    const form = overlay?.querySelector<HTMLFormElement>(".sfe-tax-bracket-modal-form");
    form
      ?.querySelectorAll<HTMLInputElement>('input[name^="from-"], input[name^="to-"]')
      .forEach((input) => {
        input.addEventListener("blur", () => {
          const value = parseNum(input.value);
          if (value != null) input.value = fmtWholeMoney(value);
        });
      });
    brackets.slice(0, -1).forEach((_, index) => {
      const toInput = form?.elements.namedItem(`to-${index}`) as HTMLInputElement | null;
      const nextFromInput = form?.elements.namedItem(`from-${index + 1}`) as HTMLInputElement | null;
      toInput?.addEventListener("input", () => {
        const toValue = parseNum(toInput.value);
        if (toValue == null || !nextFromInput) return;
        nextFromInput.value = fmtWholeMoney(toValue + 1);
        nextFromInput.dispatchEvent(new Event("input", { bubbles: true }));
      });
    });
    const copy = form?.elements.namedItem("copy-previous") as HTMLInputElement | null;
    const fields = overlay?.querySelector<HTMLElement>("[data-tax-bracket-modal-fields]");
    const safeHarborCopy = form?.elements.namedItem("pick-safe-harbor") as HTMLInputElement | null;
    const priorTaxInput = form?.elements.namedItem("prior-year-tax") as HTMLInputElement | null;
    const syncCopy = () => {
      if (fields) fields.toggleAttribute("inert", Boolean(copy?.checked));
      fields?.querySelectorAll<HTMLInputElement>("input").forEach((input) => (input.disabled = Boolean(copy?.checked)));
    };
    const syncSafeHarborCopy = () => {
      if (priorTaxInput) priorTaxInput.disabled = Boolean(safeHarborCopy?.checked);
    };
    copy?.addEventListener("change", syncCopy);
    safeHarborCopy?.addEventListener("change", syncSafeHarborCopy);
    syncCopy();
    syncSafeHarborCopy();
    form?.addEventListener("submit", (event) => {
      event.preventDefault();
      const ownBrackets = brackets.map((bracket, index) => {
        const rate = parseNum((form.elements.namedItem(`rate-${index}`) as HTMLInputElement).value);
        const from = parseNum((form.elements.namedItem(`from-${index}`) as HTMLInputElement).value);
        const toInput = form.elements.namedItem(`to-${index}`) as HTMLInputElement;
        const to = toInput.value.trim() ? parseNum(toInput.value) : null;
        if (rate == null || from == null || (toInput.value.trim() && to == null)) throw new Error("Enter valid bracket amounts.");
        return { ratePct: rate, from, to };
      });
      const deductionInput = form.elements.namedItem("standard-deduction") as HTMLInputElement | null;
      const interestInput = form.elements.namedItem("irs-interest") as HTMLInputElement | null;
      const opportunityInput = form.elements.namedItem("irs-opportunity") as HTMLInputElement | null;
      const priorYearTax = priorTaxInput?.value.trim() ? parseNum(priorTaxInput.value) : null;
      const defaultStandardDeduction = deductionInput ? parseNum(deductionInput.value) : this.settings.defaultStandardDeduction;
      const irsInterestAprPct = interestInput ? parseNum(interestInput.value) : this.settings.irsInterestAprPct;
      const irsOpportunityMonthlyPct = opportunityInput
        ? parseNum(opportunityInput.value)
        : this.settings.irsOpportunityMonthlyPct;
      const picksSafeHarbor = Boolean(safeHarborCopy?.checked);
      if (
        (!picksSafeHarbor && priorTaxInput?.value.trim() && priorYearTax == null) ||
        defaultStandardDeduction == null ||
        irsInterestAprPct == null ||
        irsOpportunityMonthlyPct == null
      ) {
        throw new Error("Enter valid card parameters.");
      }
      this.settings = {
        ...this.settings,
        defaultStandardDeduction,
        irsInterestAprPct,
        irsOpportunityMonthlyPct,
        years: isMobile
          ? {
              ...this.settings.years,
              [yearKey]: {
                ...currentYearInputs,
                priorYearTotalTax: picksSafeHarbor
                  ? currentYearInputs.priorYearTotalTax
                  : priorYearTax,
                pickPriorYearTotalTax: false,
              },
            }
          : this.settings.years,
        bracketsByYear: { ...this.settings.bracketsByYear, [yearKey]: ownBrackets },
        copyBracketsFromPrevious: { ...this.settings.copyBracketsFromPrevious, [yearKey]: Boolean(copy?.checked) },
      };
      this.persist();
      close();
      this.render();
      if (picksSafeHarbor && hasPreviousYear) {
        void this.pickPriorYearTotalTaxFromPreviousYear();
      }
    });
  }

  private openPaycheckMonthEditor(person: "anton" | "olha", monthIndex: number): void {
    if (monthIndex < 0 || monthIndex >= MONTH_LABELS.length) return;
    const host = this.ensureTaxModalHost();
    host.querySelector("[data-tax-paycheck-month-overlay]")?.remove();
    const year = this.selectedYear;
    const current = yearInputs(this.settings, year);
    const paycheckYear = normalizePaycheckYear(
      person === "anton" ? current.w2PaychecksAnton : current.w2PaychecksOlha,
    );
    const noIncomeMonths = [
      ...(person === "anton" ? current.w2NoIncomeAnton : current.w2NoIncomeOlha),
    ];
    const personName = this.settings.w2PersonNames[person];
    const paychecks: TaxPaycheckMonth = [
      { ...paycheckYear[monthIndex][0] },
      { ...paycheckYear[monthIndex][1] },
    ];
    const renderMoneyField = (
      paycheckIndex: number,
      name: "gross" | "tax",
      label: string,
      value: number | null,
    ) => `
      <label class="sfe-tax-paycheck-month-modal-field">
        <span>${label}</span>
        <input
          type="text"
          name="${name}-${paycheckIndex}"
          value="${value == null ? "" : esc(`$${fmtMoney(value)}`)}"
          inputmode="decimal"
          autocomplete="off"
        />
      </label>`;
    const paycheckCards = paychecks
      .map(
        (paycheck, paycheckIndex) => `
          <fieldset class="sfe-tax-paycheck-month-modal-card">
            <legend>Paycheck ${paycheckIndex + 1}</legend>
            <label class="sfe-tax-paycheck-month-modal-none">
              <input type="checkbox" name="none-${paycheckIndex}"${paycheck.noIncome ? " checked" : ""} />
              <span>No income</span>
            </label>
            <div class="sfe-tax-paycheck-month-modal-grid">
              ${renderMoneyField(paycheckIndex, "gross", "Gross income", paycheck.grossIncome)}
              ${renderMoneyField(paycheckIndex, "tax", "Tax withheld", paycheck.totalTaxPaid)}
            </div>
          </fieldset>`,
      )
      .join("");
    host.insertAdjacentHTML(
      "beforeend",
      `<div class="sfe-modal-overlay sfe-modal-open" data-tax-paycheck-month-overlay>
        <div class="sfe-modal sfe-tax-paycheck-month-modal" role="dialog" aria-modal="true" aria-label="Edit ${esc(MONTH_LABELS[monthIndex])} W-2 income">
          <div class="sfe-modal-header">
            <h3>${esc(personName)} · ${MONTH_LABELS[monthIndex]} ${year}</h3>
            <div class="sfe-tax-paycheck-month-modal-head-actions">
              ${renderCopyPaycheckButton(
                "sfe-tax-copy-paycheck-btn sfe-tax-paycheck-month-modal-copy",
                monthIndex === 0
                  ? "No previous month"
                  : `Copy all values from ${MONTH_LABELS[monthIndex - 1]}`,
                monthIndex === 0,
                { month: monthIndex },
              )}
              <button type="button" class="sfe-modal-close" data-tax-paycheck-month-close aria-label="Close">×</button>
            </div>
          </div>
          <div class="sfe-tax-paycheck-month-modal-body">
            <p class="sfe-tax-paycheck-month-modal-note">Edit both paychecks for this month, then save.</p>
            <form class="sfe-tax-paycheck-month-modal-form">
              <label class="sfe-tax-paycheck-month-modal-month-none">
                <input type="checkbox" name="month-none"${noIncomeMonths[monthIndex] ? " checked" : ""} />
                <span>No W-2 income this month</span>
              </label>
              ${paycheckCards}
              <div class="sfe-modal-actions">
                <button type="button" class="sfe-btn sfe-tax-paycheck-month-modal-cancel" data-tax-paycheck-month-close>Cancel</button>
                <button type="submit" class="sfe-btn sfe-btn-primary">Save month</button>
              </div>
            </form>
          </div>
        </div>
      </div>`,
    );
    const overlay = host.querySelector<HTMLElement>("[data-tax-paycheck-month-overlay]");
    const form = overlay?.querySelector<HTMLFormElement>(".sfe-tax-paycheck-month-modal-form");
    const close = () => overlay?.remove();
    overlay
      ?.querySelectorAll<HTMLElement>("[data-tax-paycheck-month-close]")
      .forEach((button) => button.addEventListener("click", close));
    const monthNone = form?.elements.namedItem("month-none") as HTMLInputElement | null;
    const syncDisabled = () => {
      const wholeMonthDisabled = Boolean(monthNone?.checked);
      for (let paycheckIndex = 0; paycheckIndex < 2; paycheckIndex += 1) {
        const paycheckNone = form?.elements.namedItem(
          `none-${paycheckIndex}`,
        ) as HTMLInputElement | null;
        if (paycheckNone) paycheckNone.disabled = wholeMonthDisabled;
        const disabled = wholeMonthDisabled || Boolean(paycheckNone?.checked);
        (["gross", "tax"] as const).forEach((name) => {
          const input = form?.elements.namedItem(
            `${name}-${paycheckIndex}`,
          ) as HTMLInputElement | null;
          if (input) input.disabled = disabled;
        });
      }
    };
    monthNone?.addEventListener("change", syncDisabled);
    for (let paycheckIndex = 0; paycheckIndex < 2; paycheckIndex += 1) {
      (
        form?.elements.namedItem(`none-${paycheckIndex}`) as HTMLInputElement | null
      )?.addEventListener("change", syncDisabled);
    }
    overlay
      ?.querySelector<HTMLButtonElement>(".sfe-tax-paycheck-month-modal-copy")
      ?.addEventListener("click", () => {
        if (!form || monthIndex === 0) return;
        const sourcePaychecks = paycheckYear[monthIndex - 1];
        const sourceMonthNone = noIncomeMonths[monthIndex - 1] === true;
        if (monthNone) monthNone.checked = sourceMonthNone;
        sourcePaychecks.forEach((paycheck, paycheckIndex) => {
          const paycheckNone = form.elements.namedItem(
            `none-${paycheckIndex}`,
          ) as HTMLInputElement;
          const gross = form.elements.namedItem(`gross-${paycheckIndex}`) as HTMLInputElement;
          const tax = form.elements.namedItem(`tax-${paycheckIndex}`) as HTMLInputElement;
          paycheckNone.checked = paycheck.noIncome === true;
          gross.value = paycheck.grossIncome == null ? "" : `$${fmtMoney(paycheck.grossIncome)}`;
          tax.value =
            paycheck.totalTaxPaid == null ? "" : `$${fmtMoney(paycheck.totalTaxPaid)}`;
        });
        syncDisabled();
      });
    syncDisabled();
    form?.addEventListener("submit", (event) => {
      event.preventDefault();
      const updatedPaychecks: TaxPaycheckMonth = [0, 1].map((paycheckIndex) => {
        const noIncome = (
          form.elements.namedItem(`none-${paycheckIndex}`) as HTMLInputElement
        ).checked;
        return {
          grossIncome: parseNum(
            (form.elements.namedItem(`gross-${paycheckIndex}`) as HTMLInputElement).value,
          ),
          totalTaxPaid: parseNum(
            (form.elements.namedItem(`tax-${paycheckIndex}`) as HTMLInputElement).value,
          ),
          noIncome,
        };
      }) as TaxPaycheckMonth;
      paycheckYear[monthIndex] = updatedPaychecks;
      noIncomeMonths[monthIndex] = Boolean(monthNone?.checked);
      this.settings = setYear(
        this.settings,
        year,
        person === "anton"
          ? {
              w2PaychecksAnton: paycheckYear,
              w2NoIncomeAnton: noIncomeMonths,
            }
          : {
              w2PaychecksOlha: paycheckYear,
              w2NoIncomeOlha: noIncomeMonths,
            },
      );
      this.expandedPaycheckRows.delete(this.paycheckRowKey(person, monthIndex));
      this.persist();
      close();
      this.render();
    });
  }

  private openW2PersonNameEditor(person: "anton" | "olha"): void {
    const host = this.ensureTaxModalHost();
    host.querySelector("[data-tax-person-name-overlay]")?.remove();
    const currentName = this.settings.w2PersonNames[person];
    host.insertAdjacentHTML(
      "beforeend",
      `<div class="sfe-modal-overlay sfe-modal-open" data-tax-person-name-overlay>
        <div class="sfe-modal sfe-tax-person-name-modal" role="dialog" aria-modal="true" aria-label="Edit W-2 card name">
          <div class="sfe-modal-header"><h3>Edit W-2 name</h3><button type="button" class="sfe-modal-close" data-tax-person-name-close aria-label="Close">×</button></div>
          <form class="sfe-tax-person-name-form">
            <label class="sfe-tax-person-name-field">
              <span>Card name</span>
              <input type="text" name="person-name" value="${esc(currentName)}" maxlength="30" required autocomplete="off" />
            </label>
            <div class="sfe-modal-actions">
              <button type="button" class="sfe-btn" data-tax-person-name-close>Cancel</button>
              <button type="submit" class="sfe-btn sfe-btn-primary">Save name</button>
            </div>
          </form>
        </div>
      </div>`,
    );
    const overlay = host.querySelector<HTMLElement>("[data-tax-person-name-overlay]");
    const close = () => overlay?.remove();
    overlay
      ?.querySelectorAll<HTMLElement>("[data-tax-person-name-close]")
      .forEach((button) => button.addEventListener("click", close));
    const input = overlay?.querySelector<HTMLInputElement>('input[name="person-name"]');
    input?.focus();
    input?.select();
    overlay
      ?.querySelector<HTMLFormElement>(".sfe-tax-person-name-form")
      ?.addEventListener("submit", (event) => {
        event.preventDefault();
        const name = input?.value.trim();
        if (!name) {
          input?.focus();
          return;
        }
        this.settings = {
          ...this.settings,
          w2PersonNames: { ...this.settings.w2PersonNames, [person]: name },
        };
        this.persist();
        close();
        this.render();
      });
  }

  mount(panel: HTMLElement): void {
    this.panel = panel;
    this.render();
    if (!taxScopeBound) {
      taxScopeBound = true;
      subscribeAccountScope(() => {
        if (this.panel?.isConnected) this.refresh();
      });
    }
    if (
      this.tradingIncomeLoadedYear !== this.selectedYear &&
      this.tradingIncomeLoadingYear !== this.selectedYear
    ) {
      void this.loadTradingIncomeFromAnalytics();
    }
    this.ensurePriorYearTradingForPick();
    this.hydrateFromServer();
  }

  refresh(): void {
    void this.loadTradingIncomeFromAnalytics();
    this.ensurePriorYearTradingForPick();
  }

  private lastYearSafeHarborTax(year: TaxYear = this.selectedYear): number | null {
    const prior = priorTaxYear(year);
    if (prior == null) return null;
    const income =
      this.priorYearTradingLoadedYear === prior
        ? this.priorYearTradingIncome
        : zeroTradingMonths();
    const fifo =
      this.priorYearTradingLoadedYear === prior
        ? this.priorYearTradingFifo
        : zeroTradingMonths();
    return computeLastYearSafeHarborTax(this.settings, year, income, fifo);
  }

  /** Allow checking when last-year tax > 0; always allow unchecking when already picked. */
  private canPickPriorYearSafeHarbor(year: TaxYear, currentlyPicked: boolean): boolean {
    if (priorTaxYear(year) == null) return false;
    if (currentlyPicked) return true;
    const tax = this.lastYearSafeHarborTax(year);
    return tax != null && tax > 0;
  }

  private ensurePriorYearTradingForPick(): void {
    if (!this.panel) return;
    const prior = priorTaxYear(this.selectedYear);
    if (prior == null) return;
    if (
      this.priorYearTradingLoadedYear === prior ||
      this.priorYearTradingLoadingYear === prior
    ) {
      return;
    }
    this.priorYearTradingLoadingYear = prior;
    const scopeGen = accountScopeGeneration();
    void loadTradingIncomeForTaxYear(prior)
      .then((data) => {
        if (scopeGen !== accountScopeGeneration()) return;
        if (priorTaxYear(this.selectedYear) !== prior || !this.panel) return;
        this.priorYearTradingIncome = data.income;
        this.priorYearTradingFifo = data.fifo;
        this.priorYearTradingLoadedYear = prior;
      })
      .catch(() => {
        if (scopeGen !== accountScopeGeneration()) return;
        if (priorTaxYear(this.selectedYear) !== prior || !this.panel) return;
        this.priorYearTradingIncome = zeroTradingMonths();
        this.priorYearTradingFifo = zeroTradingMonths();
        this.priorYearTradingLoadedYear = prior;
      })
      .finally(() => {
        if (scopeGen !== accountScopeGeneration()) {
          if (this.priorYearTradingLoadingYear === prior) this.priorYearTradingLoadingYear = null;
          return;
        }
        if (this.priorYearTradingLoadingYear === prior) {
          this.priorYearTradingLoadingYear = null;
        }
        if (priorTaxYear(this.selectedYear) !== prior || !this.panel) return;
        this.render();
      });
  }

  private async loadTradingIncomeFromAnalytics(): Promise<void> {
    if (!this.panel) return;
    const year = this.selectedYear;
    const scopeGen = accountScopeGeneration();
    this.tradingIncomeLoadingYear = year;
    if (this.tradingIncomeLoadedYear !== year && !this.tradingIncomeLoading) {
      this.tradingIncomeLoading = true;
      this.render();
    }
    try {
      const data = await withTaxAnalyticsTimeout(loadTradingIncomeForTaxYear(year));
      if (this.selectedYear !== year || !this.panel || scopeGen !== accountScopeGeneration()) return;
      this.tradingIncomeMonths = data.income;
      this.tradingFifoMonths = data.fifo;
    } catch {
      if (this.selectedYear !== year || !this.panel) return;
      this.tradingIncomeMonths = MONTH_LABELS.map(() => 0);
      this.tradingFifoMonths = MONTH_LABELS.map(() => 0);
    } finally {
      if (scopeGen !== accountScopeGeneration()) {
        if (this.tradingIncomeLoadingYear === year) this.tradingIncomeLoadingYear = null;
        return;
      }
      if (this.tradingIncomeLoadingYear === year) {
        this.tradingIncomeLoadingYear = null;
      }
      if (this.selectedYear !== year || !this.panel) return;
      this.tradingIncomeLoading = false;
      this.tradingIncomeLoadedYear = year;
      this.render();
    }
  }

  private paycheckRowKey(person: "anton" | "olha", monthIndex: number): string {
    return `${person}-${monthIndex}`;
  }

  private setPaycheckRowExpanded(
    person: "anton" | "olha",
    monthIndex: number,
    expanded: boolean,
  ): void {
    const rowKey = this.paycheckRowKey(person, monthIndex);
    if (expanded) this.expandedPaycheckRows.add(rowKey);
    else this.expandedPaycheckRows.delete(rowKey);
    this.syncPaycheckRowExpanded(person, monthIndex, expanded);
  }

  private syncPaycheckRowExpanded(
    person: "anton" | "olha",
    monthIndex: number,
    expanded: boolean,
  ): void {
    if (!this.panel) return;
    const summaryRow = this.panel.querySelector<HTMLTableRowElement>(
      `.sfe-tax-paycheck-summary-row[data-person="${person}"][data-month="${monthIndex}"]`,
    );
    const editorRow = summaryRow?.nextElementSibling as HTMLTableRowElement | null;
    const toggles = summaryRow?.querySelectorAll<HTMLButtonElement>(".sfe-tax-paycheck-row-toggle");
    summaryRow?.classList.toggle("sfe-tax-paycheck-summary-row-open", expanded);
    toggles?.forEach((toggle) => {
      toggle.setAttribute("aria-expanded", String(expanded));
      toggle.setAttribute(
        "aria-label",
        `${expanded ? "Collapse" : "Expand"} ${MONTH_LABELS[monthIndex]} paychecks`,
      );
    });
    if (editorRow?.classList.contains("sfe-tax-paycheck-editor-row")) {
      editorRow.hidden = !expanded;
    }
  }

  private togglePaycheckRow(person: "anton" | "olha", monthIndex: number): void {
    const rowKey = this.paycheckRowKey(person, monthIndex);
    this.setPaycheckRowExpanded(person, monthIndex, !this.expandedPaycheckRows.has(rowKey));
  }

  private openPaycheckFieldEdit(field: HTMLElement): void {
    if (!this.panel || field.classList.contains("sfe-tax-paycheck-field-disabled")) return;
    this.panel.querySelectorAll<HTMLElement>(".sfe-tax-paycheck-field").forEach((node) => {
      const view = node.querySelector<HTMLElement>(".sfe-tax-paycheck-field-view");
      const edit = node.querySelector<HTMLElement>(".sfe-tax-paycheck-field-edit");
      if (view) view.hidden = false;
      if (edit) edit.hidden = true;
    });
    const view = field.querySelector<HTMLElement>(".sfe-tax-paycheck-field-view");
    const edit = field.querySelector<HTMLElement>(".sfe-tax-paycheck-field-edit");
    if (!view || !edit) return;
    view.hidden = true;
    edit.hidden = false;
    const input = edit.querySelector<HTMLInputElement>("input");
    input?.focus();
    input?.select();
  }

  private async pickPriorYearTotalTaxFromPreviousYear(): Promise<void> {
    const year = this.selectedYear;
    const yearIdx = getTaxYears().indexOf(year);
    if (yearIdx <= 0 || !this.panel) return;
    const sourceYear = getTaxYears()[yearIdx - 1];
    const checkboxes = this.panel.querySelectorAll<HTMLInputElement>(
      "#sfe-tax-pick-prior-year-tax",
    );
    checkboxes.forEach((checkbox) => (checkbox.disabled = true));
    try {
      const data = await loadTradingIncomeForTaxYear(sourceYear);
      if (this.selectedYear !== year || !this.panel) return;
      this.priorYearTradingIncome = data.income;
      this.priorYearTradingFifo = data.fifo;
      this.priorYearTradingLoadedYear = sourceYear;
      const totalTax = computeYearTotalTaxForSafeHarbor(
        this.settings,
        sourceYear,
        data.income,
        data.fifo,
      );
      if (totalTax <= 0) {
        this.settings = setYear(this.settings, year, {
          pickPriorYearTotalTax: false,
        });
        this.persist();
        this.render();
        return;
      }
      this.settings = setYear(this.settings, year, {
        priorYearTotalTax: totalTax,
        pickPriorYearTotalTax: true,
      });
      this.persist();
      this.render();
    } catch {
      if (this.selectedYear !== year || !this.panel) return;
      checkboxes.forEach((checkbox) => {
        checkbox.disabled = false;
        checkbox.checked = false;
      });
    }
  }

  private render(): void {
    if (!this.panel) return;
    this.selectedYear = normalizeTaxYear(this.selectedYear);
    this.selectedYear = normalizeTaxYearChipSelection(this.selectedYear, this.taxForecastEnabled);
    const mobileScrollContainer = this.panel.closest<HTMLElement>(".sfe-mobile-main");
    const mobileScrollTop = mobileScrollContainer?.scrollTop ?? null;
    const year = this.selectedYear;
    const yearIdx = getTaxYears().indexOf(year);
    const canCopyPrevious = yearIdx > 0;
    const copiesPrevious =
      canCopyPrevious && this.settings.copyBracketsFromPrevious[String(year)] === true;
    const priorTaxSourceYear = canCopyPrevious ? getTaxYears()[yearIdx - 1] : null;
    const previousBrackets = priorTaxSourceYear
      ? taxBracketsForYear(this.settings, priorTaxSourceYear)
      : null;
    const pickedPriorTax = yearInputs(this.settings, year).pickPriorYearTotalTax;
    const canPickPriorTax = this.canPickPriorYearSafeHarbor(year, pickedPriorTax);
    const pickPriorTaxTitle = !canPickPriorTax
      ? "Last year total tax is $0"
      : `Fill from ${priorTaxSourceYear} total tax (FIFO, with standard deduction)`;
    const pickPriorTaxAction = priorTaxSourceYear
      ? `<label class="sfe-tax-pick-prior-year-tax"${canPickPriorTax ? "" : ' aria-disabled="true"'} title="${esc(pickPriorTaxTitle)}">
           <input type="checkbox" id="sfe-tax-pick-prior-year-tax"${pickedPriorTax ? " checked" : ""}${canPickPriorTax ? "" : " disabled"} />
           <span>Pick from last year</span>
         </label>`
      : "";
    this.ensurePriorYearTradingForPick();
    const brackets = taxBracketsForYear(this.settings, year);
    const selectedInputs = yearInputs(this.settings, year);
    const selectedDeduction =
      selectedInputs.standardDeduction ?? this.settings.defaultStandardDeduction;

    this.panel.innerHTML = `
        <div class="sfe-analytics-plus-header">
          <div class="sfe-analytics-plus-header-text">
            <h2 class="insight-h">Tax planning</h2>
          </div>
          <div class="sfe-analytics-plus-header-actions"></div>
        </div>

        <div class="sfe-income-year-row">
          <span class="muted small sfe-income-year-label">Year</span>
          <div class="year-chips sfe-income-year-chips" role="tablist" aria-label="Tax year">
            ${[...getTaxYearChipOptions(this.taxForecastEnabled)].reverse().map((y) => {
              const tip = taxYearChipTip(y);
              return `<button type="button" class="${taxYearChipClassName(y, y === year)}" data-year="${y}" role="tab" aria-selected="${y === year}"${tip ? ` data-tip="${tip}"` : ""}>${y}</button>`;
            }).join("")}
          </div>
          <div class="sfe-income-year-trailing">
            <label class="sfe-toggle sfe-income-forecast-toggle">
              <input type="checkbox" class="sfe-income-forecast-input"${this.taxForecastEnabled ? " checked" : ""} />
              Forecast
            </label>
          </div>
        </div>

        <details class="collapsible sfe-tax-section sfe-tax-global-details sfe-tax-collapsible" data-tax-section="settings"${this.collapsedTaxSections.has("settings") ? "" : " open"}>
          ${renderTaxSectionSummary(
            "Settings",
            year,
            `Safe harbour $${fmtMoney(selectedInputs.priorYearTotalTax)} · Deduction $${fmtMoney(selectedDeduction)}`,
            `<span class="sfe-tax-filing-status-banner">Married, filing jointly</span>`,
          )}
          <div class="sfe-tax-global-body">
            <div class="sfe-tax-settings-row">
              <div class="sfe-tax-rate-heading">
                <header class="sfe-tax-card-head sfe-tax-settings-card-head">
                  ${renderTaxSectionHeaderText(`Tax rates ${year}`, year)}
                </header>
              </div>
              <div class="sfe-tax-settings-content">
                <div class="sfe-tax-bracket-panel-card">
                  <div class="sfe-tax-bracket-mobile-toolbar">
                    <div class="sfe-tax-bracket-mobile-toolbar-actions">
                      <button type="button" class="sfe-tax-bracket-mobile-edit-btn" data-tax-brackets-edit aria-label="Edit tax rates" title="Edit tax rates"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true"><path fill="currentColor" d="M11.013 1.427a1.75 1.75 0 0 1 2.474 0l1.086 1.086a1.75 1.75 0 0 1 0 2.474l-8.986 8.986a1 1 0 0 1-.446.261l-3.25 1.083a.25.25 0 0 1-.316-.316l1.083-3.25a1 1 0 0 1 .261-.446l8.986-8.986z"/></svg></button>
                    </div>
                  </div>
                  ${renderBracketTable(brackets, copiesPrevious, previousBrackets)}
                </div>
                <div class="sfe-tax-settings-fields-col">
                  ${taxSettingsMoneyField(
                    `y${year}-prior-year-tax`,
                    "Last year total tax (safe harbour)",
                    yearInputs(this.settings, year).priorYearTotalTax,
                    "Prior-year total tax used as the safe harbor reference.",
                    pickPriorTaxAction,
                    pickedPriorTax,
                  )}
                  ${taxSettingsMoneyField(
                    "sfe-tax-default-deduction",
                    "Default standard deduction",
                    this.settings.defaultStandardDeduction,
                  )}
                  ${taxSettingsPctField(
                    "sfe-tax-irs-interest",
                    "IRS Interest (annual)",
                    this.settings.irsInterestAprPct,
                    TIP_IRS_INTEREST_ANNUAL,
                  )}
                  ${taxSettingsPctField(
                    "sfe-tax-irs-opportunity",
                    "IRS opportunity (monthly)",
                    this.settings.irsOpportunityMonthlyPct,
                    TIP_IRS_OPPORTUNITY_MONTHLY,
                  )}
                </div>
              </div>
            </div>
          </div>
        </details>

        <div class="sfe-tax-year-form" data-year="${year}">
          ${renderYearForm(
            this.settings,
            year,
            this.expandedPaycheckRows,
            this.collapsedTaxSections,
            this.tradingIncomeMonths,
            this.tradingFifoMonths,
            this.tradingIncomeLoading,
            this.panel.classList.contains("sfe-mobile-tax"),
            this.otherIncomeModalOpen ? this.otherIncomeEditingId : null,
          )}
        </div>`;

    this.bindEvents();
    if (this.estPaymentsModalOpen) {
      // Leave the modal mounted while the panel re-renders underneath it.
    } else {
      document.getElementById("sfe-tax-est-payments-modal")?.remove();
      this.detachEstPaymentsEscapeHandler();
    }
    if (this.irsOpportunityModalOpen) {
      // Leave the modal mounted while the panel re-renders underneath it.
    } else {
      document.getElementById("sfe-tax-irs-opportunity-modal")?.remove();
      this.detachIrsOpportunityEscapeHandler();
    }
    if (this.irsInterestModalOpen) {
      // Leave the modal mounted while the panel re-renders underneath it.
    } else {
      document.getElementById("sfe-tax-irs-interest-modal")?.remove();
      this.detachIrsInterestEscapeHandler();
    }
    if (this.safeHarborModalOpen) {
      // Leave the modal mounted while the panel re-renders underneath it.
    } else {
      document.getElementById("sfe-tax-safe-harbor-modal")?.remove();
      this.detachSafeHarborEscapeHandler();
    }
    if (this.otherIncomeModalOpen) {
      // Leave the modal mounted while the panel re-renders underneath it.
    } else {
      document.getElementById("sfe-tax-other-income-modal")?.remove();
      this.detachOtherIncomeEscapeHandler();
    }
    this.panel.dispatchEvent(new CustomEvent("sfe-tax-rendered", { bubbles: false }));
    if (mobileScrollContainer && mobileScrollTop != null) {
      mobileScrollContainer.scrollTop = mobileScrollTop;
    }
  }

  private syncIrsOpportunityModal(): void {
    document.getElementById("sfe-tax-irs-opportunity-modal")?.remove();
    if (!this.irsOpportunityModalOpen) {
      this.detachIrsOpportunityEscapeHandler();
      return;
    }
    const year = this.selectedYear;
    const y = yearInputs(this.settings, year);
    const schedule = computeTaxEstimateSchedule(
      this.settings,
      year,
      y,
      this.tradingIncomeMonths,
      this.tradingFifoMonths,
    );
    const breakdown = computeIrsOpportunityBreakdown(
      this.settings,
      year,
      this.irsOpportunityModalScope,
      this.irsOpportunityModalQuarter,
      schedule.quarters,
      schedule.months,
    );
    this.ensureTaxModalHost().insertAdjacentHTML("beforeend", renderIrsOpportunityModal(breakdown));
    this.bindIrsOpportunityModalEvents();
    this.attachIrsOpportunityEscapeHandler();
  }

  private scheduleOpenIrsOpportunityModal(scope: "quarter" | "annual", quarterIndex: number): void {
    window.requestAnimationFrame(() => {
      if (!this.panel) return;
      this.openIrsOpportunityModal(scope, quarterIndex);
    });
  }

  private attachIrsOpportunityEscapeHandler(): void {
    this.detachIrsOpportunityEscapeHandler();
    this.irsOpportunityEscapeHandler = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        this.closeIrsOpportunityModal();
      }
    };
    document.addEventListener("keydown", this.irsOpportunityEscapeHandler);
  }

  private detachIrsOpportunityEscapeHandler(): void {
    if (this.irsOpportunityEscapeHandler) {
      document.removeEventListener("keydown", this.irsOpportunityEscapeHandler);
      this.irsOpportunityEscapeHandler = null;
    }
  }

  private openIrsOpportunityModal(scope: "quarter" | "annual", quarterIndex: number): void {
    this.closeEstPaymentsModal();
    this.closeIrsInterestModal();
    this.closeSafeHarborModal();
    this.closeOtherIncomeModal();
    this.irsOpportunityModalOpen = true;
    this.irsOpportunityModalScope = scope;
    this.irsOpportunityModalQuarter = quarterIndex;
    this.syncIrsOpportunityModal();
  }

  private closeIrsOpportunityModal(): void {
    this.irsOpportunityModalOpen = false;
    this.syncIrsOpportunityModal();
  }

  private syncIrsInterestModal(): void {
    document.getElementById("sfe-tax-irs-interest-modal")?.remove();
    if (!this.irsInterestModalOpen) {
      this.detachIrsInterestEscapeHandler();
      return;
    }
    const year = this.selectedYear;
    const y = yearInputs(this.settings, year);
    const schedule = computeTaxEstimateSchedule(
      this.settings,
      year,
      y,
      this.tradingIncomeMonths,
      this.tradingFifoMonths,
    );
    const breakdown = computeIrsInterestBreakdown(
      this.settings,
      year,
      this.irsInterestModalScope,
      this.irsInterestModalQuarter,
      schedule.quarters,
      schedule.months,
    );
    this.ensureTaxModalHost().insertAdjacentHTML("beforeend", renderIrsInterestModal(breakdown));
    this.bindIrsInterestModalEvents();
    this.attachIrsInterestEscapeHandler();
  }

  private scheduleOpenIrsInterestModal(scope: "quarter" | "annual", quarterIndex: number): void {
    window.requestAnimationFrame(() => {
      if (!this.panel) return;
      this.openIrsInterestModal(scope, quarterIndex);
    });
  }

  private attachIrsInterestEscapeHandler(): void {
    this.detachIrsInterestEscapeHandler();
    this.irsInterestEscapeHandler = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        this.closeIrsInterestModal();
      }
    };
    document.addEventListener("keydown", this.irsInterestEscapeHandler);
  }

  private detachIrsInterestEscapeHandler(): void {
    if (this.irsInterestEscapeHandler) {
      document.removeEventListener("keydown", this.irsInterestEscapeHandler);
      this.irsInterestEscapeHandler = null;
    }
  }

  private openIrsInterestModal(scope: "quarter" | "annual", quarterIndex: number): void {
    this.closeEstPaymentsModal();
    this.closeIrsOpportunityModal();
    this.closeSafeHarborModal();
    this.closeOtherIncomeModal();
    this.irsInterestModalOpen = true;
    this.irsInterestModalScope = scope;
    this.irsInterestModalQuarter = quarterIndex;
    this.syncIrsInterestModal();
  }

  private closeIrsInterestModal(): void {
    this.irsInterestModalOpen = false;
    this.syncIrsInterestModal();
  }

  private bindIrsInterestModalEvents(): void {
    const overlay = document.getElementById("sfe-tax-irs-interest-modal");
    if (!overlay) return;
    const close = () => this.closeIrsInterestModal();
    overlay.querySelectorAll(".sfe-tax-irs-interest-close").forEach((el) => {
      el.addEventListener("click", close);
    });
    overlay.addEventListener("click", (event) => {
      if (event.target === overlay) close();
    });
  }

  private syncSafeHarborModal(): void {
    document.getElementById("sfe-tax-safe-harbor-modal")?.remove();
    if (!this.safeHarborModalOpen) {
      this.detachSafeHarborEscapeHandler();
      return;
    }
    const year = this.selectedYear;
    const y = yearInputs(this.settings, year);
    const schedule = computeTaxEstimateSchedule(
      this.settings,
      year,
      y,
      this.tradingIncomeMonths,
      this.tradingFifoMonths,
    );
    const breakdown = computeSafeHarborBreakdown(
      year,
      this.safeHarborModalScope,
      this.safeHarborModalQuarter,
      schedule.quarters,
      schedule.months,
    );
    this.ensureTaxModalHost().insertAdjacentHTML("beforeend", renderSafeHarborModal(breakdown));
    this.bindSafeHarborModalEvents();
    this.attachSafeHarborEscapeHandler();
  }

  private scheduleOpenSafeHarborModal(scope: "quarter" | "annual", quarterIndex: number): void {
    window.requestAnimationFrame(() => {
      if (!this.panel) return;
      this.openSafeHarborModal(scope, quarterIndex);
    });
  }

  private attachSafeHarborEscapeHandler(): void {
    this.detachSafeHarborEscapeHandler();
    this.safeHarborEscapeHandler = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        this.closeSafeHarborModal();
      }
    };
    document.addEventListener("keydown", this.safeHarborEscapeHandler);
  }

  private detachSafeHarborEscapeHandler(): void {
    if (this.safeHarborEscapeHandler) {
      document.removeEventListener("keydown", this.safeHarborEscapeHandler);
      this.safeHarborEscapeHandler = null;
    }
  }

  private openSafeHarborModal(scope: "quarter" | "annual", quarterIndex: number): void {
    this.closeEstPaymentsModal();
    this.closeIrsOpportunityModal();
    this.closeIrsInterestModal();
    this.closeOtherIncomeModal();
    this.safeHarborModalOpen = true;
    this.safeHarborModalScope = scope;
    this.safeHarborModalQuarter = quarterIndex;
    this.syncSafeHarborModal();
  }

  private closeSafeHarborModal(): void {
    this.safeHarborModalOpen = false;
    this.syncSafeHarborModal();
  }

  private bindSafeHarborModalEvents(): void {
    const overlay = document.getElementById("sfe-tax-safe-harbor-modal");
    if (!overlay) return;
    const close = () => this.closeSafeHarborModal();
    overlay.querySelectorAll(".sfe-tax-safe-harbor-close").forEach((el) => {
      el.addEventListener("click", close);
    });
    overlay.addEventListener("click", (event) => {
      if (event.target === overlay) close();
    });
  }

  private bindIrsOpportunityModalEvents(): void {
    const overlay = document.getElementById("sfe-tax-irs-opportunity-modal");
    if (!overlay) return;
    const close = () => this.closeIrsOpportunityModal();
    overlay.querySelectorAll(".sfe-tax-irs-oppty-close").forEach((el) => {
      el.addEventListener("click", close);
    });
    overlay.addEventListener("click", (event) => {
      if (event.target === overlay) close();
    });
  }

  private syncEstPaymentsModal(): void {
    document.getElementById("sfe-tax-est-payments-modal")?.remove();
    if (!this.estPaymentsModalOpen) {
      this.detachEstPaymentsEscapeHandler();
      return;
    }
    const year = this.selectedYear;
    const y = yearInputs(this.settings, year);
    const draftDate =
      this.estPaymentsDraftDate ||
      (this.estPaymentsModalMonth != null
        ? defaultEstPaymentDate(year, this.estPaymentsModalMonth)
        : defaultEstPaymentDate(year, 0));
    this.ensureTaxModalHost().insertAdjacentHTML(
      "beforeend",
      renderEstTaxPaymentsModal(
        year,
        y.estimatedTaxPayments,
        this.estPaymentsModalMonth,
        this.estPaymentsEditingId,
        draftDate,
        this.estPaymentsDraftAmount,
        this.estPaymentsDraftKind,
        this.estPaymentsDraftNote,
        this.estPaymentsDraftFromOwnCapital,
      ),
    );
    this.bindEstPaymentsModalEvents();
    this.attachEstPaymentsEscapeHandler();
  }

  private attachEstPaymentsEscapeHandler(): void {
    this.detachEstPaymentsEscapeHandler();
    this.estPaymentsEscapeHandler = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        this.closeEstPaymentsModal();
      }
    };
    document.addEventListener("keydown", this.estPaymentsEscapeHandler);
  }

  private detachEstPaymentsEscapeHandler(): void {
    if (this.estPaymentsEscapeHandler) {
      document.removeEventListener("keydown", this.estPaymentsEscapeHandler);
      this.estPaymentsEscapeHandler = null;
    }
  }

  private openEstPaymentsModal(monthIndex: number): void {
    const year = this.selectedYear;
    this.closeIrsOpportunityModal();
    this.closeIrsInterestModal();
    this.closeSafeHarborModal();
    this.closeOtherIncomeModal();
    this.estPaymentsModalOpen = true;
    this.estPaymentsModalMonth = monthIndex;
    this.estPaymentsEditingId = null;
    this.estPaymentsDraftDate = defaultEstPaymentDate(year, monthIndex);
    this.estPaymentsDraftAmount = "";
    this.estPaymentsDraftKind = "estimated";
    this.estPaymentsDraftNote = "";
    this.estPaymentsDraftFromOwnCapital = defaultFromOwnCapitalForTaxYear(year);
    this.syncEstPaymentsModal();
  }

  /** Public entry for Tax tab + Analytics manage-payments (same modal). */
  openEstimatedPaymentsModal(year: TaxYear, monthIndex: number): void {
    if (year > currentTaxYear()) {
      this.taxForecastEnabled = true;
    }
    if (this.selectedYear !== year) {
      this.selectedYear = year;
      this.tradingIncomeLoadedYear = null;
      if (this.panel) {
        void this.loadTradingIncomeFromAnalytics();
        this.render();
      }
    }
    this.openEstPaymentsModal(Math.max(0, Math.min(11, Math.trunc(monthIndex))));
  }

  private scheduleOpenEstPaymentsModal(monthIndex: number): void {
    window.requestAnimationFrame(() => {
      if (!this.panel) return;
      this.openEstPaymentsModal(monthIndex);
    });
  }

  private closeEstPaymentsModal(): void {
    this.estPaymentsModalOpen = false;
    this.estPaymentsModalMonth = null;
    this.estPaymentsEditingId = null;
    this.estPaymentsDraftDate = "";
    this.estPaymentsDraftAmount = "";
    this.estPaymentsDraftKind = "estimated";
    this.estPaymentsDraftNote = "";
    this.estPaymentsDraftFromOwnCapital = defaultFromOwnCapitalForTaxYear(this.selectedYear);
    this.syncEstPaymentsModal();
  }

  private maybeToastPaymentMonthMismatch(year: TaxYear, date: string): void {
    const focusMonth = this.estPaymentsModalMonth;
    if (focusMonth == null) return;
    const cashMonth = paymentMonthIndex(date, year);
    const focusKey = `${year}-${String(focusMonth + 1).padStart(2, "0")}`;
    const cashKey = date.slice(0, 7);
    if (!cashKey || cashKey === focusKey) return;
    const jumpLabel =
      cashMonth != null
        ? `${MONTH_LABELS[cashMonth]} ${year}`
        : cashKey;
    showToast(`Payment saved to ${jumpLabel}`, "info", {
      actionLabel: "Jump",
      durationMs: 8000,
      onAction: () => {
        if (cashMonth != null) {
          this.openEstPaymentsModal(cashMonth);
        }
        const [yRaw, mRaw] = cashKey.split("-").map(Number);
        if (Number.isFinite(yRaw) && Number.isFinite(mRaw) && mRaw >= 1 && mRaw <= 12) {
          notifyTaxPaymentMonthFocus({
            monthKey: cashKey,
            year: yRaw,
            monthIndex: mRaw - 1,
          });
        }
      },
    });
  }

  private setEstimatedTaxPayments(year: TaxYear, payments: TaxEstimatedPayment[]): void {
    const sorted = [...payments].sort(
      (a, b) => a.date.localeCompare(b.date) || a.id.localeCompare(b.id),
    );
    this.settings = setYear(this.settings, year, {
      estimatedTaxPayments: sorted,
      estimatedTaxPaidMonths: syncEstimatedTaxPaidMonthsFromPayments(year, sorted),
    });
    this.persist();
  }

  private bindEstPaymentsModalEvents(): void {
    const overlay = document.getElementById("sfe-tax-est-payments-modal");
    if (!overlay) return;
    const year = this.selectedYear;

    const close = () => this.closeEstPaymentsModal();
    overlay.querySelectorAll(".sfe-tax-est-payments-close").forEach((el) => {
      el.addEventListener("click", close);
    });
    overlay.addEventListener("click", (event) => {
      if (event.target === overlay) close();
    });

    overlay.querySelector(".sfe-tax-est-payments-form")?.addEventListener("submit", (event) => {
      event.preventDefault();
      const form = event.currentTarget as HTMLFormElement;
      const dateInput = form.querySelector<HTMLInputElement>(".sfe-tax-est-payments-date");
      const amountInput = form.querySelector<HTMLInputElement>(".sfe-tax-est-payments-amount-input");
      const kindInput = form.querySelector<HTMLSelectElement>(".sfe-tax-est-payments-kind");
      const noteInput = form.querySelector<HTMLInputElement>(".sfe-tax-est-payments-note-input");
      const fromOwnCapitalInput = form.querySelector<HTMLInputElement>(
        ".sfe-tax-est-payments-from-own-capital-input",
      );
      if (!dateInput || !amountInput || !kindInput || !noteInput) return;
      const date = dateInput.value.trim();
      const amount = parseNum(amountInput.value);
      const kind = kindInput.value as TaxPaymentKind;
      const note = noteInput.value.trim();
      const fromOwnCapital = fromOwnCapitalInput?.checked === true;
      if (!date || amount == null || amount <= 0) return;
      if (!paymentDateAppliesToTaxYear(date, year)) return;
      if (!["estimated", "balance-due", "extension", "other"].includes(kind)) return;

      const current = yearInputs(this.settings, year);
      let next = [...current.estimatedTaxPayments];
      if (this.estPaymentsEditingId) {
        next = next.map((payment) =>
          payment.id === this.estPaymentsEditingId
            ? { ...payment, taxYear: year, kind, date, amount, note, fromOwnCapital }
            : payment,
        );
        this.estPaymentsEditingId = null;
      } else {
        next.push({
          id: newEstimatedTaxPaymentId(),
          taxYear: year,
          kind,
          date,
          amount,
          note,
          fromOwnCapital,
        });
      }
      this.setEstimatedTaxPayments(year, next);
      this.estPaymentsDraftDate = date;
      this.estPaymentsDraftAmount = "";
      this.estPaymentsDraftKind = "estimated";
      this.estPaymentsDraftNote = "";
      this.estPaymentsDraftFromOwnCapital = defaultFromOwnCapitalForTaxYear(year);
      this.render();
      this.syncEstPaymentsModal();
      this.maybeToastPaymentMonthMismatch(year, date);
    });

    overlay.querySelector(".sfe-tax-est-payments-cancel-edit")?.addEventListener("click", () => {
      this.estPaymentsEditingId = null;
      this.estPaymentsDraftAmount = "";
      this.estPaymentsDraftKind = "estimated";
      this.estPaymentsDraftNote = "";
      this.estPaymentsDraftFromOwnCapital = defaultFromOwnCapitalForTaxYear(year);
      this.syncEstPaymentsModal();
    });

    overlay.querySelectorAll(".sfe-tax-est-payments-edit").forEach((el) => {
      el.addEventListener("click", () => {
        const id = (el as HTMLElement).dataset.paymentId;
        if (!id) return;
        this.estPaymentsEditingId = id;
        this.syncEstPaymentsModal();
        overlay.querySelector<HTMLInputElement>(".sfe-tax-est-payments-amount-input")?.select();
      });
    });

    overlay.querySelectorAll(".sfe-tax-est-payments-delete").forEach((el) => {
      el.addEventListener("click", () => {
        const id = (el as HTMLElement).dataset.paymentId;
        if (!id) return;
        const current = yearInputs(this.settings, year);
        const next = current.estimatedTaxPayments.filter((payment) => payment.id !== id);
        if (this.estPaymentsEditingId === id) {
          this.estPaymentsEditingId = null;
          this.estPaymentsDraftAmount = "";
          this.estPaymentsDraftKind = "estimated";
          this.estPaymentsDraftNote = "";
          this.estPaymentsDraftFromOwnCapital = defaultFromOwnCapitalForTaxYear(year);
        }
        this.setEstimatedTaxPayments(year, next);
        this.render();
        this.syncEstPaymentsModal();
      });
    });

    bindEstPaymentDateField(overlay, year, (iso) => {
      this.estPaymentsDraftDate = iso;
    });

    const amountInput = overlay.querySelector<HTMLInputElement>(".sfe-tax-est-payments-amount-input");
    const kindInput = overlay.querySelector<HTMLSelectElement>(".sfe-tax-est-payments-kind");
    const noteInput = overlay.querySelector<HTMLInputElement>(".sfe-tax-est-payments-note-input");
    const fromOwnCapitalInput = overlay.querySelector<HTMLInputElement>(
      ".sfe-tax-est-payments-from-own-capital-input",
    );
    amountInput?.addEventListener("input", () => {
      if (!this.estPaymentsEditingId) {
        this.estPaymentsDraftAmount = amountInput.value;
      }
    });
    kindInput?.addEventListener("change", () => {
      if (!this.estPaymentsEditingId) {
        this.estPaymentsDraftKind = kindInput.value as TaxPaymentKind;
      }
    });
    noteInput?.addEventListener("input", () => {
      if (!this.estPaymentsEditingId) {
        this.estPaymentsDraftNote = noteInput.value;
      }
    });
    fromOwnCapitalInput?.addEventListener("change", () => {
      if (!this.estPaymentsEditingId) {
        this.estPaymentsDraftFromOwnCapital = fromOwnCapitalInput.checked;
      }
    });
    if (this.estPaymentsEditingId) {
      amountInput?.focus();
      amountInput?.select();
    }
  }

  private persist(): void {
    this.localRevision += 1;
    commitTaxSettingsToStore(this.settings, { queueServer: true });
  }

  private setOtherIncomeItems(year: TaxYear, items: TaxOtherIncomeItem[]): void {
    const sorted = normalizeOtherIncomeItems(items);
    this.settings = setYear(this.settings, year, { otherIncomeItems: sorted });
    this.persist();
  }

  private clearOtherIncomeDraft(): void {
    this.otherIncomeDraftDate = "";
    this.otherIncomeDraftAmount = "";
    this.otherIncomeDraftTax = "";
    this.otherIncomeDraftPerson = "anton";
    this.otherIncomeDraftNote = "";
  }

  private syncOtherIncomeModal(): void {
    document.getElementById("sfe-tax-other-income-modal")?.remove();
    if (!this.otherIncomeModalOpen) {
      this.detachOtherIncomeEscapeHandler();
      return;
    }
    const year = this.selectedYear;
    const y = yearInputs(this.settings, year);
    this.ensureTaxModalHost().insertAdjacentHTML(
      "beforeend",
      renderOtherIncomeModal(
        this.settings.w2PersonNames,
        year,
        y.otherIncomeItems,
        this.otherIncomeEditingId,
        {
          date: this.otherIncomeDraftDate,
          amount: this.otherIncomeDraftAmount,
          tax: this.otherIncomeDraftTax,
          person: this.otherIncomeDraftPerson,
          note: this.otherIncomeDraftNote,
        },
      ),
    );
    this.bindOtherIncomeModalEvents();
    this.attachOtherIncomeEscapeHandler();
    document
      .querySelector<HTMLInputElement>("#sfe-tax-other-income-modal .sfe-tax-other-income-amount")
      ?.focus();
  }

  private attachOtherIncomeEscapeHandler(): void {
    this.detachOtherIncomeEscapeHandler();
    this.otherIncomeEscapeHandler = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        this.closeOtherIncomeModal();
        this.render();
      }
    };
    document.addEventListener("keydown", this.otherIncomeEscapeHandler);
  }

  private detachOtherIncomeEscapeHandler(): void {
    if (this.otherIncomeEscapeHandler) {
      document.removeEventListener("keydown", this.otherIncomeEscapeHandler);
      this.otherIncomeEscapeHandler = null;
    }
  }

  private openOtherIncomeModal(editId: string | null = null): void {
    const year = this.selectedYear;
    this.closeEstPaymentsModal();
    this.closeIrsOpportunityModal();
    this.closeIrsInterestModal();
    this.closeSafeHarborModal();
    this.otherIncomeModalOpen = true;
    this.otherIncomeEditingId = editId;
    if (editId) {
      const item = yearInputs(this.settings, year).otherIncomeItems.find((row) => row.id === editId);
      this.otherIncomeDraftDate = item?.date ?? defaultOtherIncomeDate(year);
      this.otherIncomeDraftAmount = item ? fmtMoney(item.amount) : "";
      this.otherIncomeDraftTax = item ? fmtMoney(item.taxContributed) : "";
      this.otherIncomeDraftPerson = item?.person ?? "anton";
      this.otherIncomeDraftNote = item?.note ?? "";
    } else {
      this.clearOtherIncomeDraft();
      this.otherIncomeDraftDate = defaultOtherIncomeDate(year);
    }
    this.render();
    this.syncOtherIncomeModal();
  }

  private closeOtherIncomeModal(): void {
    this.otherIncomeModalOpen = false;
    this.otherIncomeEditingId = null;
    this.clearOtherIncomeDraft();
    this.syncOtherIncomeModal();
  }

  private bindOtherIncomeModalEvents(): void {
    const overlay = document.getElementById("sfe-tax-other-income-modal");
    if (!overlay) return;
    const year = this.selectedYear;
    const close = () => {
      this.closeOtherIncomeModal();
      this.render();
    };
    overlay.querySelectorAll(".sfe-tax-other-income-close").forEach((el) => {
      el.addEventListener("click", close);
    });
    overlay.addEventListener("click", (event) => {
      if (event.target === overlay) close();
    });
    overlay.querySelector(".sfe-tax-other-income-form")?.addEventListener("submit", (event) => {
      event.preventDefault();
      const form = event.currentTarget as HTMLFormElement;
      const dateInput = form.querySelector<HTMLInputElement>(".sfe-tax-other-income-date");
      const amountInput = form.querySelector<HTMLInputElement>(".sfe-tax-other-income-amount");
      const taxInput = form.querySelector<HTMLInputElement>(".sfe-tax-other-income-tax");
      const personInput = form.querySelector<HTMLSelectElement>(".sfe-tax-other-income-person");
      const noteInput = form.querySelector<HTMLInputElement>(".sfe-tax-other-income-note");
      if (!dateInput || !amountInput || !personInput || !noteInput) return;
      const date = dateInput.value.trim();
      const amount = parseNum(amountInput.value);
      const taxContributed = taxInput?.value.trim() ? parseNum(taxInput.value) : 0;
      const person = personInput.value === "olha" ? "olha" : "anton";
      const note = noteInput.value.trim();
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || amount == null || taxContributed == null) return;
      const current = yearInputs(this.settings, year);
      let next = [...current.otherIncomeItems];
      if (this.otherIncomeEditingId) {
        next = next.map((item) =>
          item.id === this.otherIncomeEditingId
            ? { ...item, date, amount, taxContributed: Math.max(0, taxContributed), person, note }
            : item,
        );
      } else {
        next.push({
          id: newOtherIncomeItemId(),
          date,
          amount,
          taxContributed: Math.max(0, taxContributed),
          person,
          note,
        });
      }
      this.setOtherIncomeItems(year, next);
      this.closeOtherIncomeModal();
      this.render();
    });
  }

  private bindOtherIncomeEvents(): void {
    if (!this.panel) return;
    const year = this.selectedYear;

    this.panel.querySelector("[data-other-income-add]")?.addEventListener("click", () => {
      this.openOtherIncomeModal(null);
    });

    this.panel.querySelectorAll("[data-other-income-edit]").forEach((el) => {
      el.addEventListener("click", () => {
        const id = (el as HTMLElement).dataset.otherIncomeEdit;
        if (!id) return;
        this.openOtherIncomeModal(id);
      });
    });

    this.panel.querySelectorAll("[data-other-income-delete]").forEach((el) => {
      el.addEventListener("click", () => {
        const id = (el as HTMLElement).dataset.otherIncomeDelete;
        if (!id) return;
        const current = yearInputs(this.settings, year);
        const next = current.otherIncomeItems.filter((item) => item.id !== id);
        if (this.otherIncomeEditingId === id) this.closeOtherIncomeModal();
        this.setOtherIncomeItems(year, next);
        this.render();
      });
    });
  }

  private hydrateFromServer(): void {
    if (this.serverHydrationStarted) return;
    this.serverHydrationStarted = true;
    const revisionAtStart = this.localRevision;
    void ensureTaxStoreReady<TaxSettings>()
      .then((merged) => {
        if (this.localRevision !== revisionAtStart) {
          // Local edits landed while hydrate ran — store already kept them.
          this.settings = getTaxSettingsFromStore<TaxSettings>();
          this.render();
          return;
        }
        this.settings = merged;
        // Match prior behavior: notify Analytics after Tax-tab hydrate merges.
        notifyTaxSettingsChanged();
        this.render();
      })
      .catch(() => {
        // Local storage remains available while the server is unreachable.
      });
  }

  private bindEvents(): void {
    if (!this.panel) return;

    this.bindOtherIncomeEvents();

    this.panel
      .querySelectorAll<HTMLButtonElement>("[data-tax-person-name-edit]")
      .forEach((button) => {
        button.addEventListener("click", () => {
          const person = button.dataset.taxPersonNameEdit;
          if (person === "anton" || person === "olha") {
            this.openW2PersonNameEditor(person);
          }
        });
      });

    this.panel.querySelectorAll<HTMLDetailsElement>(".sfe-tax-collapsible").forEach((details) => {
      details.addEventListener("toggle", () => {
        const section = details.dataset.taxSection;
        if (!section) return;
        if (details.open) this.collapsedTaxSections.delete(section);
        else this.collapsedTaxSections.add(section);
        setCollapsibleOpen(TAX_COLLAPSIBLE_SCOPE, section, details.open);
      });
    });

    this.panel.querySelectorAll(".sfe-income-year-chip").forEach((btn) => {
      btn.addEventListener("click", () => {
        const y = Number((btn as HTMLElement).dataset.year);
        if (!getTaxYearChipOptions(this.taxForecastEnabled).includes(y as TaxYear)) return;
        this.closeEstPaymentsModal();
        this.closeIrsOpportunityModal();
        this.closeIrsInterestModal();
        this.closeSafeHarborModal();
        this.closeOtherIncomeModal();
        this.selectedYear = y as TaxYear;
        this.tradingIncomeLoading = this.tradingIncomeLoadedYear !== this.selectedYear;
        this.render();
        void this.loadTradingIncomeFromAnalytics();
      });
    });

    this.panel.querySelector<HTMLInputElement>(".sfe-income-forecast-input")?.addEventListener("change", (e) => {
      const input = e.target as HTMLInputElement;
      this.taxForecastEnabled = input.checked;
      if (!this.taxForecastEnabled && this.selectedYear > currentTaxYear()) {
        this.selectedYear = currentTaxYear();
      }
      this.selectedYear = normalizeTaxYearChipSelection(this.selectedYear, this.taxForecastEnabled);
      this.render();
    });

    this.panel.querySelector("#sfe-tax-default-deduction")?.addEventListener("change", (e) => {
      const input = e.target as HTMLInputElement;
      const n = parseNum(input.value);
      if (n != null) {
        this.settings = { ...this.settings, defaultStandardDeduction: n };
        input.value = fmtMoney(n);
        this.persist();
      }
    });

    this.panel.querySelector("#sfe-tax-irs-interest")?.addEventListener("change", (e) => {
      const input = e.target as HTMLInputElement;
      const n = parseNum(input.value);
      if (n != null) {
        this.settings = { ...this.settings, irsInterestAprPct: n };
        input.value = String(n);
        this.persist();
      }
    });

    this.panel.querySelector("#sfe-tax-irs-opportunity")?.addEventListener("change", (e) => {
      const input = e.target as HTMLInputElement;
      const n = parseNum(input.value);
      if (n != null) {
        this.settings = { ...this.settings, irsOpportunityMonthlyPct: n };
        input.value = String(n);
        this.persist();
      }
    });

    this.panel.querySelectorAll(".sfe-tax-settings-edit-btn").forEach((el) => {
      el.addEventListener("click", () => {
        if ((el as HTMLButtonElement).disabled) return;
        this.panel
          ?.querySelectorAll<HTMLElement>(".sfe-tax-settings-kpi")
          .forEach((field) => {
            field.querySelector<HTMLElement>(".sfe-tax-settings-money-view")!.hidden = false;
            field.querySelector<HTMLElement>(".sfe-tax-settings-money-edit")!.hidden = true;
          });
        const field = el.closest<HTMLElement>(".sfe-tax-settings-kpi");
        if (!field) return;
        field.querySelector<HTMLElement>(".sfe-tax-settings-money-view")!.hidden = true;
        field.querySelector<HTMLElement>(".sfe-tax-settings-money-edit")!.hidden = false;
        const input = field.querySelector<HTMLInputElement>("input");
        input?.focus();
        input?.select();
      });
    });

    this.panel.querySelectorAll(".sfe-tax-settings-money-edit input").forEach((el) => {
      el.addEventListener("keydown", (e) => {
        if (!(e instanceof KeyboardEvent)) return;
        if (e.key === "Enter") {
          e.preventDefault();
          (el as HTMLInputElement).blur();
        } else if (e.key === "Escape") {
          e.preventDefault();
          this.render();
        }
      });
      el.addEventListener("blur", () => {
        requestAnimationFrame(() => {
          if (this.panel?.contains(el)) this.render();
        });
      });
    });

    this.panel
      .querySelectorAll<HTMLInputElement>("#sfe-tax-pick-prior-year-tax")
      .forEach((input) => {
        input.addEventListener("change", () => {
          if (input.disabled) return;
          if (input.checked) {
            void this.pickPriorYearTotalTaxFromPreviousYear();
            return;
          }
          this.settings = setYear(this.settings, this.selectedYear, {
            pickPriorYearTotalTax: false,
          });
          this.persist();
          this.render();
        });
      });

    this.panel.querySelector<HTMLElement>("[data-tax-brackets-edit]")?.addEventListener("click", () => {
      this.openBracketEditor();
    });

    const year = this.selectedYear;
    const p = `y${year}`;
    const q1PriorTaxDueInput =
      this.panel.querySelector<HTMLInputElement>("[data-tax-q1-prior-tax-due]");
    q1PriorTaxDueInput?.addEventListener("keydown", (event) => {
      if (event.key !== "Enter") return;
      event.preventDefault();
      q1PriorTaxDueInput.blur();
    });
    q1PriorTaxDueInput?.addEventListener("change", () => {
      const amount = parseNum(q1PriorTaxDueInput.value);
      if (amount == null || amount < 0) {
        q1PriorTaxDueInput.value = `$${fmtMoney(
          yearInputs(this.settings, year).priorYearTotalTax,
        )}`;
        return;
      }
      this.settings = setYear(this.settings, year, {
        priorYearTotalTax: amount,
        pickPriorYearTotalTax: false,
      });
      this.persist();
      this.render();
    });
    const bindMoney = (id: string, field: keyof TaxYearInputs) => {
      this.panel!.querySelector(`#${id}`)?.addEventListener("change", (e) => {
        const input = e.target as HTMLInputElement;
        const n = parseNum(input.value);
        input.value = fmtMoney(n);
        this.settings = setYear(this.settings, year, { [field]: n });
        this.persist();
      });
    };

    bindMoney(`${p}-w2-anton`, "w2IncomeAnton");
    bindMoney(`${p}-w2-olha`, "w2IncomeOlha");
    this.panel.querySelector(`#${p}-prior-year-tax`)?.addEventListener("change", (e) => {
      const input = e.target as HTMLInputElement;
      const n = parseNum(input.value);
      input.value = fmtMoney(n);
      this.settings = setYear(this.settings, year, {
        priorYearTotalTax: n,
        pickPriorYearTotalTax: false,
      });
      this.persist();
    });
    bindMoney(`${p}-w2-wh-anton`, "w2WithholdingMonthlyAnton");
    bindMoney(`${p}-w2-wh-olha`, "w2WithholdingMonthlyOlha");
    bindMoney(`${p}-fact-total-paid`, "factTotalEstimatedTaxPaid");

    this.panel.querySelectorAll(".sfe-tax-paycheck-row-toggle").forEach((el) => {
      el.addEventListener("click", (event) => {
        event.stopPropagation();
        const button = el as HTMLButtonElement;
        const person = button.dataset.person;
        const monthIndex = Number(button.dataset.month);
        if (
          (person !== "anton" && person !== "olha") ||
          !Number.isInteger(monthIndex) ||
          monthIndex < 0 ||
          monthIndex >= MONTH_LABELS.length
        ) {
          return;
        }
        this.togglePaycheckRow(person, monthIndex);
      });
    });

    this.panel.querySelectorAll(".sfe-tax-paycheck-summary-cell-money").forEach((el) => {
      el.addEventListener("click", () => {
        const row = (el as HTMLElement).closest<HTMLTableRowElement>(".sfe-tax-paycheck-summary-row");
        const person = row?.dataset.person;
        const monthIndex = Number(row?.dataset.month);
        if (
          (person !== "anton" && person !== "olha") ||
          !Number.isInteger(monthIndex) ||
          monthIndex < 0 ||
          monthIndex >= MONTH_LABELS.length
        ) {
          return;
        }
        if (!this.expandedPaycheckRows.has(this.paycheckRowKey(person, monthIndex))) {
          this.setPaycheckRowExpanded(person, monthIndex, true);
        }
      });
    });

    this.panel.querySelectorAll(".sfe-tax-copy-paycheck-stub").forEach((el) => {
      el.addEventListener("click", (event) => {
        event.stopPropagation();
        const button = el as HTMLButtonElement;
        if (button.disabled) return;
        const person = button.dataset.person;
        const monthIndex = Number(button.dataset.month);
        const paycheckIndex = Number(button.dataset.paycheck);
        const source = paycheckCopySource(monthIndex, paycheckIndex);
        if (
          (person !== "anton" && person !== "olha") ||
          !Number.isInteger(monthIndex) ||
          monthIndex < 0 ||
          monthIndex >= MONTH_LABELS.length ||
          !Number.isInteger(paycheckIndex) ||
          paycheckIndex < 0 ||
          paycheckIndex > 1 ||
          !source
        ) {
          return;
        }
        const current = yearInputs(this.settings, year);
        const paychecks = normalizePaycheckYear(
          person === "anton" ? current.w2PaychecksAnton : current.w2PaychecksOlha,
        );
        const noIncomeMonths = [
          ...(person === "anton" ? current.w2NoIncomeAnton : current.w2NoIncomeOlha),
        ];
        paychecks[monthIndex][paycheckIndex] = {
          ...paychecks[source.monthIndex][source.paycheckIndex],
        };
        this.settings = setYear(
          this.settings,
          year,
          person === "anton"
            ? {
                w2PaychecksAnton: paychecks,
                w2NoIncomeAnton: noIncomeMonths,
              }
            : {
                w2PaychecksOlha: paychecks,
                w2NoIncomeOlha: noIncomeMonths,
              },
        );
        this.persist();
        this.render();
      });
    });

    this.panel.querySelectorAll(".sfe-tax-edit-paycheck-month").forEach((el) => {
      el.addEventListener("click", (event) => {
        event.stopPropagation();
        const button = el as HTMLButtonElement;
        const person = button.dataset.person;
        const monthIndex = Number(button.dataset.month);
        if (
          (person !== "anton" && person !== "olha") ||
          !Number.isInteger(monthIndex) ||
          monthIndex < 0 ||
          monthIndex >= MONTH_LABELS.length
        ) {
          return;
        }
        this.openPaycheckMonthEditor(person, monthIndex);
      });
    });

    this.panel.querySelectorAll(".sfe-tax-copy-paycheck-month").forEach((el) => {
      el.addEventListener("click", (event) => {
        event.stopPropagation();
        const button = el as HTMLButtonElement;
        const person = button.dataset.person;
        const monthIndex = Number(button.dataset.month);
        if (
          (person !== "anton" && person !== "olha") ||
          !Number.isInteger(monthIndex) ||
          monthIndex <= 0 ||
          monthIndex >= MONTH_LABELS.length
        ) {
          return;
        }
        const current = yearInputs(this.settings, year);
        const paychecks = normalizePaycheckYear(
          person === "anton" ? current.w2PaychecksAnton : current.w2PaychecksOlha,
        );
        const noIncomeMonths = [
          ...(person === "anton" ? current.w2NoIncomeAnton : current.w2NoIncomeOlha),
        ];
        paychecks[monthIndex] = [
          { ...paychecks[monthIndex - 1][0] },
          { ...paychecks[monthIndex - 1][1] },
        ];
        this.settings = setYear(
          this.settings,
          year,
          person === "anton"
            ? {
                w2PaychecksAnton: paychecks,
                w2NoIncomeAnton: noIncomeMonths,
              }
            : {
                w2PaychecksOlha: paychecks,
                w2NoIncomeOlha: noIncomeMonths,
              },
        );
        this.persist();
        this.render();
      });
    });

    this.panel.querySelectorAll(".sfe-tax-no-income-check").forEach((el) => {
      el.addEventListener("click", (event) => event.stopPropagation());
    });

    this.panel.querySelectorAll(".sfe-tax-paycheck-no-income-input").forEach((el) => {
      el.addEventListener("change", (event) => {
        event.stopPropagation();
        const input = el as HTMLInputElement;
        const person = input.dataset.person;
        const monthIndex = Number(input.dataset.month);
        const paycheckIndex = Number(input.dataset.paycheck);
        if (
          (person !== "anton" && person !== "olha") ||
          !Number.isInteger(monthIndex) ||
          monthIndex < 0 ||
          monthIndex >= MONTH_LABELS.length ||
          (paycheckIndex !== 0 && paycheckIndex !== 1)
        ) {
          return;
        }
        const current = yearInputs(this.settings, year);
        const paychecks = normalizePaycheckYear(
          person === "anton" ? current.w2PaychecksAnton : current.w2PaychecksOlha,
        );
        paychecks[monthIndex][paycheckIndex] = {
          ...paychecks[monthIndex][paycheckIndex],
          noIncome: input.checked,
        };
        this.settings = setYear(
          this.settings,
          year,
          person === "anton"
            ? { w2PaychecksAnton: paychecks }
            : { w2PaychecksOlha: paychecks },
        );
        this.persist();
        if (input.checked) {
          this.expandedPaycheckRows.delete(this.paycheckRowKey(person, monthIndex));
        }
        this.render();
      });
    });

    this.panel.querySelectorAll(".sfe-tax-no-income-input").forEach((el) => {
      el.addEventListener("change", (event) => {
        event.stopPropagation();
        const input = el as HTMLInputElement;
        const person = input.dataset.person;
        const monthIndex = Number(input.dataset.month);
        if (
          (person !== "anton" && person !== "olha") ||
          !Number.isInteger(monthIndex) ||
          monthIndex < 0 ||
          monthIndex >= MONTH_LABELS.length
        ) {
          return;
        }
        const current = yearInputs(this.settings, year);
        const paychecks = normalizePaycheckYear(
          person === "anton" ? current.w2PaychecksAnton : current.w2PaychecksOlha,
        );
        const noIncomeMonths = [
          ...(person === "anton" ? current.w2NoIncomeAnton : current.w2NoIncomeOlha),
        ];
        noIncomeMonths[monthIndex] = input.checked;
        this.settings = setYear(
          this.settings,
          year,
          person === "anton"
            ? {
                w2PaychecksAnton: paychecks,
                w2NoIncomeAnton: noIncomeMonths,
              }
            : {
                w2PaychecksOlha: paychecks,
                w2NoIncomeOlha: noIncomeMonths,
              },
        );
        this.persist();
        if (input.checked) {
          this.expandedPaycheckRows.delete(this.paycheckRowKey(person, monthIndex));
        }
        this.render();
      });
    });

    this.panel.querySelectorAll(".sfe-tax-paycheck-edit-btn").forEach((el) => {
      el.addEventListener("click", (event) => {
        event.stopPropagation();
        const button = el as HTMLButtonElement;
        if (button.disabled) return;
        const field = button.closest<HTMLElement>(".sfe-tax-paycheck-field");
        if (field) this.openPaycheckFieldEdit(field);
      });
    });

    this.panel.querySelectorAll(".sfe-tax-paycheck-field-clickable").forEach((el) => {
      const open = () => {
        const field = (el as HTMLElement).closest<HTMLElement>(".sfe-tax-paycheck-field");
        if (field) this.openPaycheckFieldEdit(field);
      };
      el.addEventListener("click", (event) => {
        event.stopPropagation();
        open();
      });
      el.addEventListener("keydown", (event) => {
        if (!(event instanceof KeyboardEvent)) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          event.stopPropagation();
          open();
        }
      });
    });

    this.panel.querySelectorAll(".sfe-tax-paycheck-input").forEach((el) => {
      el.addEventListener("keydown", (e) => {
        if (!(e instanceof KeyboardEvent)) return;
        if (e.key === "Enter") {
          e.preventDefault();
          (el as HTMLInputElement).blur();
        } else if (e.key === "Escape") {
          e.preventDefault();
          this.render();
        }
      });
      el.addEventListener("change", () => {
        const input = el as HTMLInputElement;
        const person = input.dataset.person;
        const monthIndex = Number(input.dataset.month);
        const paycheckIndex = Number(input.dataset.paycheck);
        const field = input.dataset.field as keyof TaxPaycheck;
        if (
          (person !== "anton" && person !== "olha") ||
          !Number.isInteger(monthIndex) ||
          monthIndex < 0 ||
          monthIndex >= MONTH_LABELS.length ||
          (paycheckIndex !== 0 && paycheckIndex !== 1) ||
          (field !== "grossIncome" && field !== "totalTaxPaid")
        ) {
          return;
        }
        const value = parseNum(input.value);
        const current = yearInputs(this.settings, year);
        const paychecks = normalizePaycheckYear(
          person === "anton" ? current.w2PaychecksAnton : current.w2PaychecksOlha,
        );
        paychecks[monthIndex][paycheckIndex] = {
          ...paychecks[monthIndex][paycheckIndex],
          [field]: value,
        };
        this.settings = setYear(
          this.settings,
          year,
          person === "anton"
            ? { w2PaychecksAnton: paychecks }
            : { w2PaychecksOlha: paychecks },
        );
        input.value = fmtMoney(value);
        this.persist();
        this.render();
      });
      el.addEventListener("blur", () => {
        requestAnimationFrame(() => {
          if (this.panel?.contains(el)) this.render();
        });
      });
    });

    this.panel.querySelectorAll(".sfe-tax-estimate-paid-btn").forEach((el) => {
      el.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        const monthIndex = Number((el as HTMLElement).dataset.month);
        if (!Number.isInteger(monthIndex) || monthIndex < 0 || monthIndex >= MONTH_LABELS.length) {
          return;
        }
        this.closeIrsOpportunityModal();
        this.closeIrsInterestModal();
        this.closeSafeHarborModal();
        this.scheduleOpenEstPaymentsModal(monthIndex);
      });
    });

    this.panel.querySelectorAll(".sfe-tax-irs-detail-subcard").forEach((el) => {
      const open = (event?: Event) => {
        event?.preventDefault();
        event?.stopPropagation();
        const kind = (el as HTMLElement).dataset.irsDetailKind;
        const scope = (el as HTMLElement).dataset.irsDetailScope;
        const quarterIndex = Number((el as HTMLElement).dataset.irsDetailQuarter);
        if (scope !== "quarter" && scope !== "annual") return;
        if (!Number.isInteger(quarterIndex) || quarterIndex < 0 || quarterIndex > 3) return;
        if (kind === "interest") {
          this.scheduleOpenIrsInterestModal(scope, quarterIndex);
        } else if (kind === "opportunity") {
          this.scheduleOpenIrsOpportunityModal(scope, quarterIndex);
        } else if (kind === "safe-harbor") {
          this.scheduleOpenSafeHarborModal(scope, quarterIndex);
        }
      };
      el.addEventListener("click", open);
      el.addEventListener("keydown", (event) => {
        if (event.key === "Enter" || event.key === " ") {
          open(event);
        }
      });
    });

    for (let qi = 0; qi < 4; qi += 1) {
      const id = `${p}-q${qi + 1}-paid`;
      this.panel.querySelector(`#${id}`)?.addEventListener("change", (e) => {
        const input = e.target as HTMLInputElement;
        const n = parseNum(input.value);
        input.value = fmtMoney(n);
        const cur = yearInputs(this.settings, year);
        const next = [...cur.quarterlyPaidAfterW2] as TaxYearInputs["quarterlyPaidAfterW2"];
        next[qi] = n;
        this.settings = setYear(this.settings, year, { quarterlyPaidAfterW2: next });
        this.persist();
      });
    }

    this.panel.querySelector(`#${p}-profit-pct`)?.addEventListener("change", (e) => {
      const n = parseNum((e.target as HTMLInputElement).value);
      this.settings = setYear(this.settings, year, { profitPerMonthPct: n });
      this.persist();
    });

  }
}

let registeredEstPaymentsController: TaxController | null = null;
let fallbackEstPaymentsController: TaxController | null = null;

export function registerEstPaymentsModalController(controller: TaxController | null): void {
  registeredEstPaymentsController = controller;
}

/** Open the shared Tax estimated-payments modal (Tax tab or Analytics entry). */
export function openEstimatedTaxPaymentsModal(year: number, monthIndex: number): void {
  if (!(getTaxYears() as readonly number[]).includes(year)) return;
  const taxYear = year as TaxYear;
  const month = Math.max(0, Math.min(11, Math.trunc(monthIndex)));
  const controller =
    registeredEstPaymentsController ??
    (fallbackEstPaymentsController ??= new TaxController());
  controller.openEstimatedPaymentsModal(taxYear, month);
}
